<template>
  <div
    class="game-page"
    :class="{
      'is-mobile': isMobile,
      'is-landscape': isMobile && !isPortrait,
      'is-portrait': isMobile && isPortrait
    }"
  >
    <!-- 游戏桌背景 -->
    <div class="table-background"></div>
    
    <!-- 顶部信息栏 -->
    <div class="top-bar">
      <div class="top-left">
        <!-- ⬅ -->
        <button class="back-btn-room" @click="goHome">⬅</button>
        <span class="round">{{ roundDisplayText }}</span>
        <span v-if="roomDisplayId" class="room-code">房间号: {{ roomDisplayId }}</span>
      </div>
      <!-- <div class="game-info">
        <span class="countdown" v-if="countdown > 0">⏱️ {{ countdown }}s</span>
      </div> -->
      <div class="score-info">
        <button class="room-config-btn" type="button" @click="showRoomConfigDialog = true">房间设置</button>
      </div>
    </div>
    
    <!-- AI玩家区域 - 玩家信息在椭圆上 -->
    <div
      v-for="(player, index) in positionedPlayers"
      :key="'player-'+player.id"
      class="player-position"
      :class="getPortraitPlayerSideClass(index)"
      :style="getPlayerPosition(index, positionedPlayers.length)"
    >
      <PlayerInfo
        :player="player"
        :is-active="false"
        :editable="isCurrentPlayer(player)"
        :show-confirmed-badge="gameStatus === 'arranging'"
        :result-summary="getPlayerResultSummary(player.id)"
        :chat-bubble="chatBubbles[player.id]"
        @show-score-detail="openScoreDetail"
        @edit-nickname="openNicknameModal"
      />
    </div>

    <!-- AI玩家的牌 - 独立显示在更小的椭圆上 -->
    <div
      v-for="(player, index) in positionedPlayers"
      :key="'cards-'+player.id"
      class="cards-position"
      :class="getPortraitPlayerSideClass(index)"
      :style="getCardsPosition(index, positionedPlayers.length)"
    >
      <!-- 比牌阶段显示牌 -->
      <div
        v-if="shouldShowCards(player) && hasDisplayLayout(player)"
        class="ai-cards-fan"
        :class="{
          'is-special-hand': player.specialHand && isSpecialHandShown(player),
          'is-special-burst': isSpecialHandBurst(player)
        }"
      >
        <div
          v-if="player.specialHand && isSpecialHandShown(player)"
          class="special-hand-display-badge"
          :class="'type-' + (player.specialHand.type || '')"
        >{{ player.specialHand.name }}</div>
        <!-- 头道 (3张) -->
        <div class="fan-row-wrapper">
          <div class="fan-row front-row" :class="{ 'revealed': isRowRevealed(player.id, 'front') }">
            <div
              v-for="(card, i) in getPlayerDisplayLayout(player).front"
              :key="'front-'+i"
              class="fan-card"
              :class="{ 'special-flip': isSpecialHandBurst(player) }"
              :style="fanCardStyle(i, 3, 'front', player)"
            >
              <PlayingCard
                :card="card"
                :face-down="!isRowRevealed(player.id, 'front')"
                size="small"
              />
            </div>
            <div v-if="isRowRevealed(player.id, 'front') && isSpecialHand('front', player)" class="hand-type-badge">
              {{ getRowHandType(player, 'front') }}
            </div>
          </div>
        </div>
        <!-- 中道 (5张) -->
        <div class="fan-row-wrapper">
          <div class="fan-row middle-row" :class="{ 'revealed': isRowRevealed(player.id, 'middle') }">
            <div
              v-for="(card, i) in getPlayerDisplayLayout(player).middle"
              :key="'middle-'+i"
              class="fan-card"
              :class="{ 'special-flip': isSpecialHandBurst(player) }"
              :style="fanCardStyle(i, 5, 'middle', player, 3)"
            >
              <PlayingCard
                :card="card"
                :face-down="!isRowRevealed(player.id, 'middle')"
                size="small"
              />
            </div>
            <div v-if="isRowRevealed(player.id, 'middle') && isSpecialHand('middle', player)" class="hand-type-badge">
              {{ getRowHandType(player, 'middle') }}
            </div>
          </div>
        </div>
        <!-- 尾道 (5张) -->
        <div class="fan-row-wrapper">
          <div class="fan-row back-row" :class="{ 'revealed': isRowRevealed(player.id, 'back') }">
            <div
              v-for="(card, i) in getPlayerDisplayLayout(player).back"
              :key="'back-'+i"
              class="fan-card"
              :class="{ 'special-flip': isSpecialHandBurst(player) }"
              :style="fanCardStyle(i, 5, 'back', player, 8)"
            >
              <PlayingCard
                :card="card"
                :face-down="!isRowRevealed(player.id, 'back')"
                size="small"
              />
            </div>
            <div v-if="isRowRevealed(player.id, 'back') && isSpecialHand('back', player)" class="hand-type-badge">
              {{ getRowHandType(player, 'back') }}
            </div>
          </div>
        </div>
      </div>
      <!-- 特殊牌型：摆牌阶段本人看明牌 -->
      <div
        v-else-if="gameStatus === 'arranging' && player.isLayoutConfirmed && player.specialHand && isCurrentPlayer(player)"
        class="dealing-hands is-arranging special-hand-self-hands"
      >
        <div class="special-hand-self-badge">{{ player.specialHand.name }}</div>
        <div class="dealing-card-row row-1">
          <div
            v-for="(card, idx) in getDealingDisplayHands(player).slice(0, 7)"
            :key="'special-dealt-' + idx"
            class="dealt-card"
          >
            <PlayingCard :card="card" size="tiny" class="dealing-card-override" />
          </div>
        </div>
        <div class="dealing-card-row row-2" v-if="getDealingDisplayHands(player).length > 7">
          <div
            v-for="(card, idx) in getDealingDisplayHands(player).slice(7)"
            :key="'special-dealt2-' + idx"
            class="dealt-card"
          >
            <PlayingCard :card="card" size="tiny" class="dealing-card-override" />
          </div>
        </div>
      </div>
      <!-- 摆牌阶段显示背面牌 -->
      <div v-else-if="gameStatus === 'arranging' && player.isLayoutConfirmed" class="ai-cards-back">
        <div class="three-stacks-fan">
          <div class="stack-row-fan front-stack">
            <div
              v-for="n in 3"
              :key="'front-'+n"
              class="stack-card-fan"
              :style="getStackFanStyle(n-1, 3, 'small')"
            >
              <div class="card-back-small"></div>
            </div>
          </div>
          <div class="stack-row-fan middle-stack">
            <div
              v-for="n in 5"
              :key="'middle-'+n"
              class="stack-card-fan"
              :style="getStackFanStyle(n-1, 5, 'small')"
            >
              <div class="card-back-small"></div>
            </div>
          </div>
          <!-- 尾道 (5张) - 最上面，遮挡中道一半 -->
          <div class="stack-row-fan back-stack">
            <div
              v-for="n in 5"
              :key="'back-'+n"
              class="stack-card-fan"
              :style="getStackFanStyle(n-1, 5, 'small')"
            >
              <div class="card-back-small"></div>
            </div>
          </div>
        </div>
      </div>
      <!-- 发牌中 / 摆牌中（未确认）：显示手牌两排 -->
      <div v-else-if="gameStatus === 'dealing' || (gameStatus === 'arranging' && !player.isLayoutConfirmed)" class="dealing-hands" :class="{ 'is-arranging': gameStatus === 'arranging' }" :style="getDealFlyVars(player.id)">
        <div class="dealing-card-row row-1">
          <div
            v-for="(card, idx) in getDealingDisplayHands(player).slice(0, 7)"
            :key="'dealt-' + idx"
            class="dealt-card"
            :class="{ 'dealt-fly': gameStatus === 'dealing', 'last-dealt': idx === getDealingDisplayHands(player).length - 1 && gameStatus === 'dealing' }"
            :style="gameStatus === 'dealing' ? { animationDelay: (idx * 0.02) + 's' } : {}"
          >
            <template v-if="isCurrentPlayer(player)">
              <PlayingCard :card="card" size="tiny" class="dealing-card-override" />
            </template>
            <template v-else>
              <div class="card-back-small-dealing"></div>
            </template>
          </div>
        </div>
        <div class="dealing-card-row row-2" v-if="getDealingDisplayHands(player).length > 7">
          <div
            v-for="(card, idx) in getDealingDisplayHands(player).slice(7)"
            :key="'dealt2-' + idx"
            class="dealt-card"
            :class="{ 'dealt-fly': gameStatus === 'dealing', 'last-dealt': idx + 7 === getDealingDisplayHands(player).length - 1 && gameStatus === 'dealing' }"
            :style="gameStatus === 'dealing' ? { animationDelay: ((idx + 7) * 0.02) + 's' } : {}"
          >
            <template v-if="isCurrentPlayer(player)">
              <PlayingCard :card="card" size="tiny" class="dealing-card-override" />
            </template>
            <template v-else>
              <div class="card-back-small-dealing"></div>
            </template>
          </div>
        </div>
        <div v-if="gameStatus === 'dealing' && getDealingDisplayHands(player).length < 13" class="dealing-label">发牌中...</div>
        <div v-else-if="gameStatus === 'arranging'" class="dealing-label">
          <span v-if="player.isAI">思考中...</span>
          <span v-else>摆牌中...</span>
        </div>
      </div>
    </div>

    <!-- 比牌浮动分数 -->
    <div
      v-for="fs in floatingScores"
      :key="fs.id"
      class="floating-score"
      :class="{ positive: fs.isPositive, negative: !fs.isPositive }"
      :style="{ left: fs.left + 'px', top: (fs.top - 40) + 'px' }"
    >
      {{ fs.text }}
    </div>

    <!-- 本局比牌结束：赢钱 / 输钱 -->
    <div
      v-for="pop in moneyPops"
      :key="pop.id"
      class="money-pop"
      :class="{ positive: pop.isPositive, negative: !pop.isPositive }"
      :style="{ left: pop.left + 'px', top: pop.top + 'px' }"
    >
      <span
        v-for="coin in pop.coins"
        :key="coin.id"
        class="money-coin"
        :style="{ '--dx': coin.dx + 'px', animationDelay: coin.delay + 's' }"
      ></span>
      <div class="money-amount">{{ pop.text }}</div>
    </div>

    <!-- 中央游戏区 -->
    <div class="game-center">
      <!-- 游戏状态提示 -->
      <div class="status-message" v-if="statusMessage">
        {{ statusMessage }}
      </div>
      
      <!-- 比牌阶段提示 -->
      <div v-if="gameStatus === 'comparing'" class="compare-stage">
        <div class="stage-indicator" :class="`stage-${revealStage}`">
          <span v-if="compareCountdown > 0" class="stage-text countdown">{{ compareCountdown }}</span>
          <span v-else-if="specialHandAnimation && specialHandAnimation.show" class="stage-text special-stage">{{ specialHandAnimation.name }}</span>
          <span v-else-if="wulongAnimation && wulongAnimation.show" class="stage-text wulong-stage">乌龙</span>
          <span v-else-if="revealStage === 0" class="stage-text">准备比牌</span>
          <span v-else-if="revealStage === 1" class="stage-text">头道</span>
          <span v-else-if="revealStage === 2" class="stage-text">中道</span>
          <span v-else-if="revealStage === 3" class="stage-text">尾道</span>
          <span v-else-if="revealStage === 4" class="stage-text">比牌完成</span>
        </div>
      </div>

      <div v-if="gameStatus === 'settling' && !roundResultDialog && moneyPops.length === 0" class="table-round-actions">
        <div v-if="nextRoundCountdown > 0" class="next-round-countdown">
          <span class="next-round-count">{{ nextRoundCountdown }}</span>
          <span class="next-round-label">即将开始下一局</span>
        </div>
        <button
          v-else-if="showReturnToLobby"
          class="next-btn primary is-lobby"
          @click="returnToLobby"
        >返回大厅</button>
        <button v-else class="next-btn primary" @click="nextRound">下一局</button>
      </div>

      <!-- 乌龙动画：比牌时展示倒水玩家 -->
      <template v-if="wulongAnimation && wulongAnimation.playerIds && wulongAnimation.playerIds.length">
        <div
          v-for="(player, index) in positionedPlayers"
          v-if="wulongAnimation.playerIds.includes(player.id) && (wulongAnimation.show || gameStatus === 'comparing' || gameStatus === 'settling')"
          :key="'wulong-' + player.id"
          class="wulong-overlay"
          :class="getPortraitPlayerSideClass(index)"
          :style="getCardsPosition(index, positionedPlayers.length)"
        >
          <div class="wulong-badge" :class="{ 'is-active': wulongAnimation.show }">
            <span class="wulong-label">乌龙</span>
            <span v-if="wulongAnimation.show" class="wulong-player-name">{{ player.name }}</span>
          </div>
        </div>
      </template>

      <!-- 打枪动画显示在每个玩家位置上方 -->
      <template v-if="sweepAnimation && sweepAnimation.show">
        <div
          v-for="(player, index) in positionedPlayers"
          :key="'sweep-'+player.id"
          class="sweep-overlay"
          :class="getPortraitPlayerSideClass(index)"
          :style="getCardsPosition(index, positionedPlayers.length)"
        >
          <div v-if="getSweepRole(player.id) === 'shooter'" class="sweep-badge shooter">
            <div 
              class="gun-wrapper" 
              :style="{ transform: `scaleX(${sweepAnimation.gunFlip ? -1 : 1}) rotate(${sweepAnimation.gunRotation}deg)` }"
            >
              <img class="gun-image" src="~@/assets/images/gun.png" alt="gun" />
              <div class="muzzle-flash"></div>
            </div>
            <span class="sweep-label">打枪</span>
          </div>
          <div v-else-if="getSweepRole(player.id) === 'target'" class="sweep-badge target">
            <div class="bullet-holes-wrapper">
              <img class="bullet-hole hole-1" src="~@/assets/images/bullet_1.png" alt="bullet hole" />
              <img class="bullet-hole hole-2" src="~@/assets/images/bullet_2.png" alt="bullet hole" />
              <img class="bullet-hole hole-3" src="~@/assets/images/bullet_3.png" alt="bullet hole" />
              <img class="bullet-hole hole-4" src="~@/assets/images/bullet_4.png" alt="bullet hole" />
              <img class="bullet-hole hole-5" src="~@/assets/images/bullet_5.png" alt="bullet hole" />
            </div>
            <span class="target-label">被打</span>
          </div>
        </div>
      </template>

      <!-- 报道牌（特殊牌型）动画 -->
      <div
        v-if="specialHandAnimation && specialHandAnimation.show"
        class="special-announce-overlay"
        :class="'type-' + specialHandAnimation.type"
      >
        <div class="special-announce-burst">
          <img class="special-announce-flash flash-f2" src="~@/assets/images/flash1/f2.png" alt="" />
          <img class="special-announce-flash flash-f1" src="~@/assets/images/flash1/f1.png" alt="" />
          <div class="special-announce-mark">{{ specialHandAnimation.mark }}</div>
          <div class="special-announce-title">{{ specialHandAnimation.name }}</div>
          <div class="special-announce-score">赢每家 {{ specialHandAnimation.score }} 分</div>
          <div class="special-announce-player">{{ specialHandAnimation.playerName }}</div>
        </div>
      </div>

      <!-- 全垒打动画 -->
      <div v-if="homeRunAnimation && homeRunAnimation.show" class="home-run-overlay">
        <div class="home-run-burst">
          <div class="home-run-rays"></div>
          <div class="home-run-title">全垒打</div>
          <div class="home-run-players">
            {{ homeRunAnimation.playerIds.map(id => getPlayerById(id)?.name || '').filter(Boolean).join('、') }}
          </div>
        </div>
      </div>

    </div>

    <!-- 房间设置参数弹窗 -->
    <div v-if="showRoomConfigDialog" class="info-modal" @click.self="showRoomConfigDialog = false">
      <div class="info-modal-content room-config-modal">
        <div class="info-modal-header">
          <h3>房间设置</h3>
          <button class="info-close-btn" type="button" @click="showRoomConfigDialog = false">×</button>
        </div>
        <div class="config-grid">
          <div v-for="item in roomConfigDetails" :key="item.label" class="config-item">
            <span class="config-label">{{ item.label }}</span>
            <span class="config-value">{{ item.value }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 本局结果 -->
    <div v-if="roundResultDialog" class="settle-modal" @click.self="closeRoundResult">
      <div class="settle-content">
        <div class="settle-header">
          <div class="settle-title"></div>
        </div>
        <div class="result-section">
          <div class="settle-players">
            <div
              v-for="(row, index) in roundResultRows"
              :key="row.id"
              class="settle-player-card"
              :class="{ 'highlight-human': row.isHuman }"
            >
              <div class="player-header">
                <div class="player-avatar" :class="row.avatar">
                  <span v-if="row.isAI">🤖</span>
                  <span v-else>👤</span>
                </div>
                <div class="player-meta">
                  <div class="player-name">{{ index + 1 }}. {{ row.name }}</div>
                  <div class="player-total" :class="scoreTone(row.score)">本局 {{ formatSigned(row.score) }}</div>
                  <div class="player-tags" v-if="row.tags.length">
                    <span v-for="tag in row.tags" :key="tag.text" class="tag" :class="tag.type">{{ tag.text }}</span>
                  </div>
                </div>
                <div class="player-row-scores" v-if="row.summary">
                  <span class="row-score" :class="row.summary.rowClasses.front">头{{ row.summary.rows.front }}</span>
                  <span class="row-score" :class="row.summary.rowClasses.middle">中{{ row.summary.rows.middle }}</span>
                  <span class="row-score" :class="row.summary.rowClasses.back">尾{{ row.summary.rows.back }}</span>
                </div>
                <div class="player-total" :class="scoreTone(row.totalScore)">总分 {{ row.totalScore }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="settle-buttons">
          <button
            v-if="showReturnToLobby"
            class="next-btn primary is-lobby"
            @click="returnToLobby"
          >返回大厅</button>
          <button v-else class="next-btn primary" @click="nextRound">下一局</button>
        </div>
      </div>
    </div>

    <!-- 玩家本局得分计算弹窗 -->
    <div v-if="scoreDetailDialog.show" class="info-modal" @click.self="closeScoreDetail">
      <div class="info-modal-content score-detail-modal">
        <div class="info-modal-header">
          <h3>{{ scoreDetailDialog.playerName }} 本局得分</h3>
          <button class="info-close-btn" type="button" @click="closeScoreDetail">×</button>
        </div>
        <div class="score-total-line">
          <span>本局显示分</span>
          <strong :class="{ positive: scoreDetailDialog.total > 0, negative: scoreDetailDialog.total < 0 }">
            {{ formatSigned(scoreDetailDialog.total) }}
          </strong>
        </div>
        <div class="score-rule-note">
          计算顺序：特殊牌型/奇牌 -> 三道基础分 -> 打枪 -> 乌龙 -> 全垒打 -> 油菜 -> 开房积分。马牌会作用在特殊牌型、奇牌、基础分、打枪、乌龙和全垒打上；油菜与开房积分不再套马牌。
        </div>
        <div class="score-section" v-for="section in scoreDetailDialog.sections" :key="section.key">
          <div class="score-section-header">
            <h4>{{ section.title }}</h4>
            <strong
              v-if="section.lines.length > 0"
              :class="{ positive: section.subtotal > 0, negative: section.subtotal < 0, zero: section.subtotal === 0 }"
            >
              {{ formatSigned(section.subtotal) }}
            </strong>
          </div>
          <div v-if="section.lines.length === 0" class="score-empty">无</div>
          <div v-for="(line, idx) in section.lines" :key="idx" class="score-line">
            <div class="score-line-main">
              <span>{{ line.text }}</span>
              <strong :class="{ positive: line.score > 0, negative: line.score < 0, zero: line.score === 0 }">
                {{ formatSigned(line.score) }}
              </strong>
            </div>
            <div v-if="line.detail" class="score-line-detail">{{ line.detail }}</div>
          </div>
        </div>
        <div class="score-total-line score-detail-sum">
          <span>明细合计（不含开房积分）</span>
          <strong :class="{ positive: scoreDetailDialog.detailSum > 0, negative: scoreDetailDialog.detailSum < 0 }">
            {{ formatSigned(scoreDetailDialog.detailSum) }}
          </strong>
        </div>
        <div
          v-if="scoreDetailDialog.detailSum !== scoreDetailDialog.total"
          class="score-detail-mismatch"
        >
          明细合计与显示分不一致，请联系开发者排查。
        </div>
        <div v-if="scoreDetailDialog.roomRate !== 0" class="room-rate-summary">
          开房积分合计：{{ formatSigned(scoreDetailDialog.roomRate) }}，不计入本局得分。
        </div>
      </div>
    </div>
    
    <!-- 摆牌面板遮罩 -->
    <div v-if="showArrangePanel && gameStatus === 'arranging' && shouldShowArrangeUi" class="arrange-overlay"></div>
    <!-- 手动摆牌面板 -->
    <div v-if="false" class="arrange-panel">
      <div class="arrange-panel-inner">
        <div class="arrange-main">

          <!-- 三墩摆放区 -->
          <div class="layout-rows">
            <!-- 头道 -->
            <div class="layout-row row-front" :class="{ 'row-active': selectedCards.length > 0 }"
                @click="onSlotClick('front')"
                @dragover.prevent
                @drop="onDropToSlot($event, 'front')">
              <div class="row-label-text">头道</div>
              <div class="row-slot" :class="{ 'slot-valid': playerLayout.front.length === 3, 'slot-invalid': isLayoutComplete && !layoutValidation.valid }">
                <div v-if="playerLayout.front.length === 0" class="slot-placeholder">3张</div>
                <div
                  v-for="(card, idx) in playerLayout.front"
                  :key="'front-' + card.id"
                  class="slot-card-item"
                  @dblclick="removeOneCard(card, 'front')"
                >
                  <PlayingCard
                    :card="card"
                    size="small"
                    :class="{ 'manual-slot-selected': selectedManualSlotCard && selectedManualSlotCard.row === 'front' && selectedManualSlotCard.index === idx }"
                    :selected="selectedCards.some(sc => sc.id === card.id)"
                    @click="onSlotCardClick(card, 'front', idx)"
                    @remove="removeOneCard(card, 'front')"
                  />
                </div>
              </div>
              <button class="row-clear-btn" @click.stop="clearRow('front')" v-if="playerLayout.front.length > 0">×</button>
              <div v-else class="row-clear-placeholder"></div>
            </div>

            <!-- 中道 -->
            <div class="layout-row row-middle" :class="{ 'row-active': selectedCards.length > 0 }"
                @click="onSlotClick('middle')"
                @dragover.prevent
                @drop="onDropToSlot($event, 'middle')">
              <div class="row-label-text">中道</div>
              <div class="row-slot" :class="{ 'slot-valid': playerLayout.middle.length === 5, 'slot-invalid': isLayoutComplete && !layoutValidation.valid }">
                <div v-if="playerLayout.middle.length === 0" class="slot-placeholder">5张</div>
                <div
                  v-for="(card, idx) in playerLayout.middle"
                  :key="'middle-' + card.id"
                  class="slot-card-item"
                  @dblclick="removeOneCard(card, 'middle')"
                >
                  <PlayingCard
                    :card="card"
                    size="small"
                    :class="{ 'manual-slot-selected': selectedManualSlotCard && selectedManualSlotCard.row === 'middle' && selectedManualSlotCard.index === idx }"
                    :selected="selectedCards.some(sc => sc.id === card.id)"
                    @click="onSlotCardClick(card, 'middle', idx)"
                    @remove="removeOneCard(card, 'middle')"
                  />
                </div>
              </div>
              <button class="row-clear-btn" @click.stop="clearRow('middle')" v-if="playerLayout.middle.length > 0">×</button>
              <div v-else class="row-clear-placeholder"></div>
            </div>

            <!-- 尾道 -->
            <div class="layout-row row-back" :class="{ 'row-active': selectedCards.length > 0 }"
                @click="onSlotClick('back')"
                @dragover.prevent
                @drop="onDropToSlot($event, 'back')">
              <div class="row-label-text">尾道</div>
              <div class="row-slot" :class="{ 'slot-valid': playerLayout.back.length === 5, 'slot-invalid': isLayoutComplete && !layoutValidation.valid }">
                <div v-if="playerLayout.back.length === 0" class="slot-placeholder">5张</div>
                <div
                  v-for="(card, idx) in playerLayout.back"
                  :key="'back-' + card.id"
                  class="slot-card-item"
                  @dblclick="removeOneCard(card, 'back')"
                >
                  <PlayingCard
                    :card="card"
                    size="small"
                    :class="{ 'manual-slot-selected': selectedManualSlotCard && selectedManualSlotCard.row === 'back' && selectedManualSlotCard.index === idx }"
                    :selected="selectedCards.some(sc => sc.id === card.id)"
                    @click="onSlotCardClick(card, 'back', idx)"
                    @remove="removeOneCard(card, 'back')"
                  />
                </div>
              </div>
              <button class="row-clear-btn" @click.stop="clearRow('back')" v-if="playerLayout.back.length > 0">×</button>
              <div v-else class="row-clear-placeholder"></div>
            </div>
          </div>

          <!-- 牌型提示栏 -->
          <div class="hand-type-hints">
            <div
              v-for="hint in handTypeHints"
              :key="hint.key"
              class="hint-item"
              :class="{ 'hint-active': availableHandTypes.includes(hint.key) }"
              @click="selectCardsByHandType(hint.key)"
            >
              {{ hint.label }}
            </div>
          </div>

          <!-- 底部操作区 -->
          <div class="arrange-bottom">
            <!-- 手牌区 -->
            <div class="hands-area-new">
              <div class="hands-cards-new" @click="onHandAreaClick">
                <PlayingCard
                  v-for="card in playerHands"
                  :key="'hand-' + card.id"
                  :card="card"
                  :selected="selectedCards.some(sc => sc.id === card.id)"
                  size="small"
                  @click="onCardClick(card)"
                  :draggable="!isMobile"
                  @dragstart="onDragStart($event, card)"
                />
              </div>
            </div>

          </div>
        </div>
        <div class="arrange-side">
          <!-- 顶部栏：倒计时 + 关闭 -->
          <div class="arrange-top-bar">
            <!-- 关闭按钮 -->
            <!-- <button class="arrange-close-btn" @click="showArrangePanel = false">×</button> -->
            <div class="arrange-countdown">
              <!-- ⏱️ -->
              <span class="clock-icon"></span>
              <span class="countdown-num">{{ countdown }}秒</span>
            </div>
          </div>

          <div class="arrange-smart-btn">
            <button class="action-btn-smart" @click="switchToSmart">
                智能
              </button>
          </div>

          <!-- 右侧按钮 -->
            <div class="arrange-actions">
              <button class="action-btn-onekey" @click="applyBestSuggestion">
                <span class="onekey-icon">🤖</span>
                <span>一键摆牌</span>
              </button>
              <button class="action-btn-confirm" @click="confirmLayout" :disabled="!canConfirmLayout">
                出牌
              </button>
              <button class="action-btn-reset" @click="clearLayout" :disabled="!hasLayout">
                重置
              </button>
              
            </div>
        </div>
      </div>
    </div>

    <!-- 智能摆牌面板 -->
    <div
      v-if="showArrangePanel && gameStatus === 'arranging' && arrangeMode === 'smart' && shouldShowArrangeUi"
      class="smart-arrange-panel"
      :class="{ 'is-layout-locked': layoutLocked }"
    >
      <div class="smart-arrange-inner">
        <!-- 顶部栏：倒计时 -->
        <div class="smart-arrange-top">
          <div class="smart-countdown">
            <span class="clock-icon"></span>
            <span class="countdown-num">{{ countdown }}</span>
          </div>
        </div>

        <!-- 主体内容 -->
        <div class="smart-arrange-body">
          <!-- 左侧三道牌区 -->
          <div class="smart-layout-rows">
            <!-- 头道 -->
            <div class="smart-row row-front">
              <div class="smart-row-info">
                <div class="smart-row-label">头道</div>
                
              </div>
              <div class="smart-row-stat">
                  <div v-if="playerLayout.front.length === 3" class="smart-row-check">✓</div>
                  <div v-if="frontHandType" class="smart-row-type">{{ frontHandType }}</div>
                </div>
              <div class="smart-row-cards">
                <PlayingCard
                  v-for="(card, idx) in playerLayout.front"
                  :key="'smart-front-' + idx"
                  :card="card"
                  size="small"
                  :class="{ 'smart-card-selected': selectedSmartCard && selectedSmartCard.row === 'front' && selectedSmartCard.index === idx }"
                  @click="selectSmartCard('front', idx)"
                />
                <div v-if="playerLayout.front.length === 0" class="smart-placeholder">3张</div>
              </div>
            </div>

            <!-- 中道 -->
            <div class="smart-row row-middle">
              <div class="smart-row-info">
                <div class="smart-row-label">中道</div>
                
              </div>
              <div class="smart-row-stat">
                  <div v-if="playerLayout.middle.length === 5" class="smart-row-check">✓</div>
                  <div v-if="middleHandType" class="smart-row-type">{{ middleHandType }}</div>
                </div>
              <div class="smart-row-cards">
                <PlayingCard
                  v-for="(card, idx) in playerLayout.middle"
                  :key="'smart-middle-' + idx"
                  :card="card"
                  size="small"
                  :class="{ 'smart-card-selected': selectedSmartCard && selectedSmartCard.row === 'middle' && selectedSmartCard.index === idx }"
                  @click="selectSmartCard('middle', idx)"
                />
                <div v-if="playerLayout.middle.length === 0" class="smart-placeholder">5张</div>
              </div>
            </div>

            <!-- 尾道 -->
            <div class="smart-row row-back">
              <div class="smart-row-info">
                <div class="smart-row-label">尾道</div>
              </div>
              <div class="smart-row-stat">
                  <div v-if="playerLayout.back.length === 5" class="smart-row-check">✓</div>
                  <div v-if="backHandType" class="smart-row-type">{{ backHandType }}</div>
                </div>
              <div class="smart-row-cards">
                <PlayingCard
                  v-for="(card, idx) in playerLayout.back"
                  :key="'smart-back-' + idx"
                  :card="card"
                  size="small"
                  :class="{ 'smart-card-selected': selectedSmartCard && selectedSmartCard.row === 'back' && selectedSmartCard.index === idx }"
                  @click="selectSmartCard('back', idx)"
                />
                <div v-if="playerLayout.back.length === 0" class="smart-placeholder">5张</div>
              </div>
            </div>
          </div>

          <!-- 右侧方案区和操作 -->
          <div class="smart-right-side">
            <div class="smart-suggestions">
              <div
                v-for="(suggestion, index) in aiSuggestions"
                :key="'sugg-' + index"
                class="smart-suggestion-item"
                :class="{
                  'suggestion-active': selectedSuggestion === index,
                  'suggestion-disabled': layoutLocked,
                  'suggestion-special': !!suggestion.specialHand
                }"
                @click="applySuggestion(index)"
              >
                <template v-if="suggestion.specialHand">
                  <div class="suggestion-row suggestion-special-row">
                    <span class="suggestion-type">{{ suggestion.specialHand.name }}</span>
                  </div>
                </template>
                <template v-else>
                  <div class="suggestion-row">
                    <span class="suggestion-label">头</span>
                    <span class="suggestion-type">{{ getSuggestionHandType(suggestion.layout.front, 'front') }}</span>
                  </div>
                  <div class="suggestion-row">
                    <span class="suggestion-label">中</span>
                    <span class="suggestion-type">{{ getSuggestionHandType(suggestion.layout.middle, 'middle') }}</span>
                  </div>
                  <div class="suggestion-row">
                    <span class="suggestion-label">尾</span>
                    <span class="suggestion-type">{{ getSuggestionHandType(suggestion.layout.back, 'back') }}</span>
                  </div>
                </template>
              </div>

              <!-- 特殊牌型 + 报道：固定在推荐列表最后（通栏一行） -->
              <div
                class="smart-report-row"
                :class="{
                  'is-disabled': !availableSpecialHand,
                  'suggestion-active': selectedSpecial,
                  'suggestion-disabled': layoutLocked
                }"
              >
                <div
                  class="smart-report-name"
                  :class="{ 'is-clickable': !!availableSpecialHand && !layoutLocked }"
                  @click="applySpecialSuggestion"
                >
                  {{ availableSpecialHand ? availableSpecialHand.name : '特殊牌型' }}
                </div>
                <div>
                  <button
                    class="smart-report-btn"
                    :class="{ 'is-reported': reported }"
                    :disabled="!availableSpecialHand || layoutLocked"
                    @click.stop="toggleReport"
                  >
                    {{ reported ? '已报道' : '报道' }}
                  </button>
                </div>
              </div>
            </div>
            <button class="smart-confirm-btn" @click="confirmLayout" :disabled="!canConfirmLayout || layoutLocked">
              出牌
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 显示摆牌面板按钮（当面板关闭且未确认摆牌时）- 放在左下角避免重叠 -->
    <button
      v-if="!showArrangePanel && gameStatus === 'arranging' && shouldShowArrangeUi"
      class="show-arrange-btn"
      @click="showArrangePanel = true"
    >
      摆牌
    </button>

    <!-- 底部右侧：开房积分 -->
    <div v-if="config.allowRoomRate" class="bottom-room-rate">
      开房积分: {{ currentRoomRateScore }}
    </div>
    <NicknameModal
      :visible="showNicknameModal"
      v-model="nicknameInput"
      :submitting="nicknameSubmitting"
      @close="closeNicknameModal"
      @confirm="submitNicknameChange"
    />
    <ChatPanel
      v-model="chatInput"
      :open="chatOpen"
      :messages="chatMessages"
      :self-id="chatSelfId"
      @toggle="chatOpen = !chatOpen"
      @send="sendChat"
    />
  </div>
</template>

<script>
/**
 * 游戏主界面组件。
 *
 * 这个文件承担 UI 编排职责：
 * - 根据 Vuex/在线房间状态展示发牌、摆牌、比牌、赛后牌桌；
 * - 管理本地摆牌面板、推荐方案、手动/智能摆牌交互；
 * - 播放比牌倒计时、逐道翻牌、牌型语音和打枪动画；
 * - 根据横竖屏动态调整玩家、牌堆、摆牌面板的位置。
 *
 * 计分和牌型比较的“数据真相”不在这里：
 * - 单机计分在 store/index.js；
 * - 在线计分在后端 GameService.java；
 * - 牌型比较在 utils/handEvaluator.js。
 */
import { mapState, mapGetters } from 'vuex'
import PlayingCard from '@/components/PlayingCard.vue'
import CardSlot from '@/components/CardSlot.vue'
import PlayerInfo from '@/components/PlayerInfo.vue'
import NicknameModal from '@/components/NicknameModal.vue'
import ChatPanel from '@/components/ChatPanel.vue'
import nicknameMixin from '@/mixins/nickname'
import chatMixin from '@/mixins/chat'
import { aiArrange, getAIThinkTime } from '@/utils/ai'
import { compareFiveCards, compareThreeCards, evaluateFiveCards, evaluateThreeCards, isValidLayout } from '@/utils/handEvaluator'
import { sortByRank } from '@/utils/poker'
import { detectSpecialHand, resolveSpecialHandLayout, checkThreeStraight, checkThreeFlush } from '@/utils/specialHands'
import { buildDealingQueue } from '@/utils/dealQueue'
import { playDealSound, playRevealSound, playGunSound, playHitSound, playCompareCountdownSound, playCompareStartSound, playHandTypeVoice, playHomeRunSound, playSpecialHandSound } from '@/utils/sound'

export default {
  name: 'GamePage',
  components: { PlayingCard, CardSlot, PlayerInfo, NicknameModal, ChatPanel },
  mixins: [nicknameMixin, chatMixin],
  data() {
    return {
      selectedCards: [],
      playerLayout: {
        front: [],
        middle: [],
        back: []
      },
      // 比牌翻牌阶段：0 未开始，1 头道，2 中道，3 尾道，4 完成。
      revealStage: 0,
      revealedRows: {
        front: [],
        middle: [],
        back: []
      },
      compareCountdown: 0, // 比牌倒计时
      nextRoundCountdown: 0, // 点击下一局后的开局倒计时
      nextRoundTimer: null,
      showArrangePanel: true, // 是否显示摆牌面板
      arrangeMode: 'smart', // 摆牌模式: 'smart' 智能摆牌, 'manual' 手动摆牌
      aiSuggestions: [], // 智能摆牌方案列表
      selectedSuggestion: -1, // 当前选中的方案索引
      selectedSpecial: false, // 是否选中末尾特殊牌型选项
      selectedSmartCard: null, // 智能摆牌中选中的牌 { row, index }
      selectedManualSlotCard: null, // 手动摆牌中选中的三道牌 { row, index }
      layoutLocked: false, // 已点击出牌后锁定摆牌交互，防止重复提交和继续调整
      reported: false, // 已点「报道」：出牌时按特殊牌型计分
      sweepAnimation: null, // 打枪动画状态
      homeRunAnimation: null, // 全垒打动画状态
      wulongAnimation: null, // 乌龙（倒水）动画状态
      specialHandAnimation: null, // 报道牌（特殊牌型）动画状态
      onlineDealInProgress: false, // 在线模式是否正在播放本局发牌动画
      onlineDealtCount: 0, // 在线发牌动画已显示的牌数，用于同步其他玩家背面牌进度
      onlineDealtCounts: {},
      arrangeEndTime: 0, // 在线模式服务端统一下发的摆牌截止时间
      // 每次开始/取消比牌动画都会递增，用于让旧异步流程自动失效，避免多局串动画。
      compareAnimationRunId: 0,
      onlineScores: {}, // 在线模式结算分数
      pendingOnlineScores: null, // 在线模式收到 SETTLE 后先缓存，待比牌动画结束再落分
      wsUnsubscribers: [],
      pollInterval: null,
      countdownTimer: null,
      roomClockTimer: null,
      roomClockNow: Date.now(),
      roomSessionFinished: false, // 创建房间的局数或时长已用完，不能再开下一局
      isMobile: false,
      isPortrait: false,
      showRoomConfigDialog: false,
      scoreDetailDialog: {
        show: false,
        playerId: '',
        playerName: '',
        total: 0,
        detailSum: 0,
        roomRate: 0,
        sections: []
      },
      // 比牌分数浮动动画
      floatingScores: [],
      // 本局结算赢钱 / 输钱动画
      moneyPops: [],
      roundResultDialog: false,
      // 牌型提示
      handTypeHints: [
        { key: 'pair', label: '对子' },
        { key: 'two_pair', label: '两对' },
        { key: 'three_kind', label: '三条' },
        { key: 'straight', label: '顺子' },
        { key: 'flush', label: '同花' },
        { key: 'full_house', label: '葫芦' },
        { key: 'four_kind', label: '铁支' },
        { key: 'straight_flush', label: '同花顺' },
        { key: 'five_kind', label: '五同' }
      ]
    }
  },
  computed: {
    ...mapState(['gameStatus', 'players', 'countdown', 'round', 'config', 'compareResults']),
    ...mapState('multiplayer', ['room', 'gameMode']),
    ...mapGetters(['humanPlayer', 'aiPlayers']),

    roundResultRows() {
      return this.players
        .map(player => {
          const summary = this.getPlayerResultSummary(player.id)
          const tags = []
          if (summary) {
            if (summary.specialHand) tags.push({ type: 'sweep', text: summary.specialHand.name })
            if (summary.isHomeRun) tags.push({ type: 'sweep', text: '全垒打' })
            if (summary.sweepsMade > 0) tags.push({ type: 'sweep', text: `打枪x${summary.sweepsMade}` })
            if (summary.isFolded) tags.push({ type: 'beaten', text: '乌龙' })
            if (summary.timesBeaten > 0) tags.push({ type: 'beaten', text: `被打x${summary.timesBeaten}` })
          }
          return {
            id: player.id,
            name: player.name,
            isHuman: this.isCurrentPlayer(player),
            isAI: player.isAI,
            avatar: player.avatar,
            score: Number(player.score) || 0,
            totalScore: Number(player.totalScore) || 0,
            summary,
            tags
          }
        })
        .sort((a, b) => b.score - a.score || b.totalScore - a.totalScore)
    },

    currentArrangePlayer() {
      if (this.isOnlineMode) {
        return this.players.find(p => p.id === this.$store.state.playerId) || null
      }
      return this.humanPlayer
    },

    currentArrangeHandsKey() {
      const hands = this.currentArrangePlayer && this.currentArrangePlayer.hands
      if (!hands || !hands.length) return ''
      return hands.map(card => card && card.id).filter(Boolean).slice().sort().join(',')
    },

    isOnlineMode() {
      return this.gameMode === 'online'
    },
    
    // 玩家位置分布（包含所有玩家，人类玩家在底部）
    positionedPlayers() {
      return this.players
    },
    
    playerHands() {
      const playerId = this.isOnlineMode ? this.$store.state.playerId : this.humanPlayer?.id
      const player = this.players.find(p => p.id === playerId)
      if (!player) return []

      // 返回未摆入手牌区的牌，按从小到大排列
      const usedIds = new Set([
        ...this.playerLayout.front.map(c => c.id),
        ...this.playerLayout.middle.map(c => c.id),
        ...this.playerLayout.back.map(c => c.id)
      ])

      const remaining = player.hands.filter(c => !usedIds.has(c.id))
      return sortByRank(remaining).reverse() // 从小到大排列（点数小的在前）
    },
    
    hasLayout() {
      return this.playerLayout.front.length > 0 || 
             this.playerLayout.middle.length > 0 || 
             this.playerLayout.back.length > 0
    },
    
    isLayoutComplete() {
      return this.playerLayout.front.length === 3 && 
             this.playerLayout.middle.length === 5 && 
             this.playerLayout.back.length === 5
    },
    
    isLayoutValid() {
      if (!this.isLayoutComplete) return false
      const result = isValidLayout(
        this.playerLayout.front,
        this.playerLayout.middle,
        this.playerLayout.back
      )
      return result.valid
    },

    canConfirmLayout() {
      // 已报道特殊牌型时可直接出牌（按特殊计分），无需摆满三道
      if (this.availableSpecialHand && this.reported) return true
      if (!this.isLayoutComplete) return false
      return this.config.allowFouled || this.isLayoutValid
    },

    layoutValidation() {
      if (!this.isLayoutComplete) return { valid: true, reason: '' }
      return isValidLayout(
        this.playerLayout.front,
        this.playerLayout.middle,
        this.playerLayout.back
      )
    },
    
    canArrange() {
      return this.gameStatus === 'arranging' && this.playerHands.length === 13 && this.shouldShowArrangeUi
    },

    shouldShowArrangeUi() {
      const player = this.currentArrangePlayer
      if (!player || this.gameStatus !== 'arranging') return false
      return !player.isLayoutConfirmed
    },

    // 当前可报到的特殊牌型（发牌已识别，或当前摆牌可组成三同花/三顺子）
    availableSpecialHand() {
      if (!this.config.enableSpecialHands) return null
      const player = this.currentArrangePlayer
      if (!player || !Array.isArray(player.hands) || player.hands.length !== 13) return null
      if (player.specialHand) return player.specialHand
      const layout = this.isLayoutComplete ? this.playerLayout : null
      return detectSpecialHand(player.hands, layout)
    },
    
    frontHandType() {
      if (this.playerLayout.front.length !== 3) return ''
      try {
        const eval_result = evaluateThreeCards(this.playerLayout.front)
        return eval_result.rank.name
      } catch (e) {
        return ''
      }
    },
    
    middleHandType() {
      if (this.playerLayout.middle.length !== 5) return ''
      try {
        const eval_result = evaluateFiveCards(this.playerLayout.middle)
        return eval_result.rank.name
      } catch (e) {
        return ''
      }
    },
    
    backHandType() {
      if (this.playerLayout.back.length !== 5) return ''
      try {
        const eval_result = evaluateFiveCards(this.playerLayout.back)
        return eval_result.rank.name
      } catch (e) {
        return ''
      }
    },

    // 检测手牌中可组成的牌型（用于高亮提示按钮）
    availableHandTypes() {
      const hints = new Set()
      const { evaluateFiveCards, evaluateThreeCards } = require('@/utils/handEvaluator')
      const cards = this.playerHands
      if (cards.length === 0) return []

      const rankToKey = {
        10: 'straight_flush', // 同花顺
        9: 'four_kind',       // 铁支
        8: 'full_house',      // 葫芦
        7: 'flush',           // 同花
        6: 'straight',        // 顺子
        5: 'three_kind',      // 三条
        4: 'two_pair',        // 两对
        3: 'pair'             // 对子
      }

      // 枚举3张组合检测3张牌型（对子、三条）
      for (let i = 0; i < cards.length - 2; i++) {
        for (let j = i + 1; j < cards.length - 1; j++) {
          for (let k = j + 1; k < cards.length; k++) {
            try {
              const eval3 = evaluateThreeCards([cards[i], cards[j], cards[k]])
              const key = rankToKey[eval3.rank.value]
              if (key) hints.add(key)
            } catch (e) {}
          }
        }
      }

      // 枚举5张组合检测5张牌型
      if (cards.length >= 5) {
        const combos5 = this.generateCombinations(cards.length, 5)
        for (const combo of combos5) {
          try {
            const eval5 = evaluateFiveCards(combo.map(idx => cards[idx]))
            const key = rankToKey[eval5.rank.value]
            if (key) hints.add(key)
          } catch (e) {}
        }
      }

      return Array.from(hints)
    },

    statusMessage() {
      switch (this.gameStatus) {
        case 'dealing':
          return '正在发牌...'
        case 'arranging':
          return this.countdown > 0 ? `请摆牌 (${this.countdown}秒)` : '时间到！'
        case 'comparing':
          return '比牌中...'
        case 'settling':
          if (this.showReturnToLobby) {
            return this.isTimeLimitedRoom ? '时间已结束' : '局数已结束'
          }
          return '比牌完成'
        default:
          return ''
      }
    },

    totalRoundCount() {
      const roomRoundCount = this.room && Number(this.room.roundCount)
      const configRoundCount = Number(this.config.roundCount)
      return roomRoundCount || configRoundCount || 20
    },

    isTimeLimitedRoom() {
      const roomDuration = this.room && Number(this.room.durationMinutes)
      const configDuration = Number(this.config.durationMinutes)
      return this.isOnlineMode && (
        (this.room && this.room.limitType === 'time') ||
        this.config.limitType === 'time' ||
        roomDuration > 0 ||
        configDuration > 0
      )
    },

    roomExpireTime() {
      const roomExpireTime = this.room && Number(this.room.expireTime)
      const configExpireTime = Number(this.config.expireTime)
      return roomExpireTime || configExpireTime || 0
    },

    roomDurationMinutes() {
      const roomDuration = this.room && Number(this.room.durationMinutes)
      const configDuration = Number(this.config.durationMinutes)
      return roomDuration || configDuration || 0
    },

    showReturnToLobby() {
      if (!this.isOnlineMode) return false
      if (this.roomSessionFinished) return true
      if (this.isTimeLimitedRoom) {
        return this.roomExpireTime > 0 && this.roomClockNow >= this.roomExpireTime
      }
      return this.totalRoundCount > 0 && Number(this.round) >= this.totalRoundCount
    },

    roundDisplayText() {
      if (this.isTimeLimitedRoom) {
        const fallbackMs = Math.max(0, this.roomDurationMinutes) * 60 * 1000
        const remainingMs = this.roomExpireTime > 0
          ? Math.max(0, this.roomExpireTime - this.roomClockNow)
          : fallbackMs
        return `剩余 ${this.formatRoomDuration(remainingMs)}`
      }
      return `第 ${this.round}/${this.totalRoundCount} 局`
    },

    roomDisplayId() {
      return this.isOnlineMode && this.room ? this.room.id : ''
    },

    currentRoomRateScore() {
      const playerId = this.isOnlineMode ? this.$store.state.playerId : this.humanPlayer?.id
      const player = this.players.find(p => p.id === playerId)
      return player ? (player.totalRoomRateScore || 0) : 0
    },

    roomConfigDetails() {
      const c = this.config || {}
      const limitText = c.limitType === 'time'
        ? `${c.durationMinutes || 0}分钟`
        : `${c.roundCount || this.totalRoundCount}局`
      const sweepText = c.sweepScoreType === 'multiplier'
        ? `倍数 x${Number(c.sweepScoreValue) || 2}`
        : `分数 ${Number(c.sweepScoreValue) || 2}分`
      const dealModeLabels = { single: '单张', '223': '223', '334': '334', '553': '553', thirteen: '13张' }
      const horseText = c.enableHorse
        ? `启用，${this.formatRankName(c.horseRank)}${this.formatSuitName(c.horseSuit)}`
        : '关闭'
      const extraSuitText = this.formatExtraSuits(c.extraSuits)
      const rowScores = c.rowSpecialScores || {}

      const details = [
        { label: '局数/时长', value: limitText },
        { label: '人数', value: `${c.playerCount || this.players.length}人` },
        { label: '随机入座', value: c.enableRandomSeat === false ? '否' : '是' },
        { label: 'IP限制', value: c.enableIpLimit === false ? '否' : '是' },
        { label: '摆牌时间', value: `${c.arrangeTime || this.countdown || 60}秒` },
        { label: '发牌方式', value: dealModeLabels[c.dealMode] || '单张' },
        { label: '打枪计分', value: sweepText },
        { label: '全垒打', value: c.enableHomeRun === false ? '关闭' : '启用' },
        { label: '奇牌', value: c.allowOddCards === false ? '不叠加' : '叠加' },
        { label: '乌龙', value: c.allowFouled ? '允许' : '不允许' },
        { label: '马牌', value: horseText },
        { label: '油菜', value: c.enableYucai ? `启用，${Number(c.yucaiScore) || 0}分` : '关闭' },
        { label: '开房积分', value: c.allowRoomRate ? `启用，${Number(c.roomRatePercent) || 2}%` : '关闭' },
        { label: '一条龙', value: `${Number(c.dragonScore) || 15}分` },
        { label: '全大', value: `${Number(c.allBigScore) || 10}分` },
        { label: '全小', value: `${Number(c.allSmallScore) || 10}分` },
        { label: '六对半', value: `${Number(c.sixPairsHalfScore) || 7}分` },
        { label: '三同花', value: `${Number(c.threeFlushScore) || 7}分` },
        { label: '三顺子', value: `${Number(c.threeStraightScore) || 7}分` }
      ]

      if (extraSuitText) {
        details.splice(2, 0, { label: '花牌', value: extraSuitText })
      }

      if (c.allowOddCards !== false) {
        details.push({
          label: '奇牌分',
          value: `头三条${rowScores.frontThreeKind || 3} / 中葫芦${rowScores.middleFullHouse || 2} / 中炸弹${rowScores.middleFourKind || 8} / 中同花顺${rowScores.middleStraightFlush || 10} / 中五同${rowScores.middleFiveKind || 20} / 尾炸弹${rowScores.backFourKind || 4} / 尾同花顺${rowScores.backStraightFlush || 5} / 尾五同${rowScores.backFiveKind || 10}`
        })
      }

      return details
    }
  },
  watch: {
    gameStatus(newStatus, oldStatus) {
      if (newStatus !== 'settling') {
        this.roundResultDialog = false
      }

      if (this.isOnlineMode) {
        // 在线模式下，状态变化由 WS 消息驱动，此处只做本地 UI 适配
        if (newStatus === 'dealing') {
          this.showArrangePanel = false
          this.layoutLocked = false
          this.reported = false
          this.stopCountdown()
          return
        }
        if (newStatus === 'arranging') {
          if (!this.onlineDealInProgress) {
            this.enterOnlineArrangeStage()
          }
        } else if (newStatus === 'comparing') {
          this.stopCountdown()
        } else if (newStatus === 'settling') {
          this.stopCountdown()
        }
        return
      }

      if (newStatus === 'dealing') {
        // 开始发牌动画
        this.startDealingAnimation()
      } else if (newStatus === 'arranging') {
        // 开始摆牌阶段
        this.revealStage = 0
        this.resetRevealedRows()
        this.layoutLocked = false
        this.reported = false
        this.aiSuggestions = []
        this.selectedSuggestion = -1
        this.selectedSpecial = false

        const arrangePlayer = this.currentArrangePlayer
        // 特殊牌型玩家也进入摆牌界面：默认普通比牌，点击报道后按特殊计分
        this.showArrangePanel = true
        this.arrangeMode = 'smart'
        this.startAIArrange()
        if (arrangePlayer && arrangePlayer.hands && arrangePlayer.hands.length === 13) {
          this.$nextTick(() => {
            this.generateAISuggestions()
            if (this.aiSuggestions.length > 0) {
              this.applySuggestion(0)
            }
          })
        }
      } else if (newStatus === 'comparing' && oldStatus !== 'comparing') {
        // 开始比牌动画
        this.startCompareAnimation()
      } else if (newStatus === 'settling') {
        // 局数未结束时停留在牌桌等待下一局；局数已结束时只显示返回大厅
      }
    },
    // 手牌内容真正变化时才生成推荐；轮询刷新同一手牌不能覆盖玩家已选/已拖动的摆型
    currentArrangeHandsKey: {
      immediate: true,
      handler(newKey) {
        if (!newKey || this.gameStatus !== 'arranging' || this.onlineDealInProgress || this.layoutLocked) return
        const hands = this.currentArrangePlayer && this.currentArrangePlayer.hands
        if (!hands || hands.length !== 13) return
        this.$nextTick(() => {
          if (this.aiSuggestions.length === 0) {
            this.generateAISuggestions()
          }
          if (this.arrangeMode === 'smart' && this.aiSuggestions.length > 0 && !this.hasLayout) {
            this.applySuggestion(0)
          }
        })
      }
    }
  },
  mounted() {
    this.setupChat()
    this.updateViewportMode()
    window.addEventListener('resize', this.updateViewportMode)
    window.addEventListener('orientationchange', this.updateViewportMode)

    // 初始化音频上下文（浏览器需要用户交互才能播放声音）
    this._initAudioContext = () => {
      const AudioContext = window.AudioContext || window.webkitAudioContext
      if (AudioContext) {
        const ctx = new AudioContext()
        if (ctx.state === 'suspended') {
          ctx.resume()
        }
      }
      document.removeEventListener('click', this._initAudioContext)
      document.removeEventListener('touchstart', this._initAudioContext)
    }
    document.addEventListener('click', this._initAudioContext, { once: true })
    document.addEventListener('touchstart', this._initAudioContext, { once: true })

    // 调试工具：控制台运行 __toggleTestCards__() 切换测试牌模式
    window.__toggleTestCards__ = () => {
      this.$store.state.config.debugTestCards = !this.$store.state.config.debugTestCards
      console.log('debugTestCards:', this.$store.state.config.debugTestCards ? 'ON（下局发三顺子：鬼+♦3+♥5 / ♣456♦7♥8 / ♠10♥J♦Q♣K♥A）' : 'OFF')
    }

    this.startRoomClock()

    if (this.isOnlineMode) {
      if (!this.room) {
        this.$router.push('/')
        return
      }
      this.setupOnlineMode()
      return
    }

    // 如果没有玩家，返回首页
    if (this.players.length === 0) {
      this.$router.push('/')
      return
    }

    // 开始游戏
    this.$store.dispatch('startGame')
  },
  beforeDestroy() {
    this.compareAnimationRunId++
    this.sweepAnimation = null
    this.homeRunAnimation = null
    this.specialHandAnimation = null
    window.removeEventListener('resize', this.updateViewportMode)
    window.removeEventListener('orientationchange', this.updateViewportMode)
    this.wsUnsubscribers.forEach(fn => fn())
    this.wsUnsubscribers = []
    this.stopRoomPolling()
    this.stopCountdown()
    this.stopRoomClock()
    this.clearNextRoundTimer()
  },
  methods: {
    startRoomClock() {
      this.stopRoomClock()
      this.roomClockNow = Date.now()
      this.roomClockTimer = setInterval(() => {
        this.roomClockNow = Date.now()
      }, 1000)
    },

    stopRoomClock() {
      if (this.roomClockTimer) {
        clearInterval(this.roomClockTimer)
        this.roomClockTimer = null
      }
    },

    formatRoomDuration(ms) {
      const totalSeconds = Math.max(0, Math.ceil(ms / 1000))
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      const pad = value => String(value).padStart(2, '0')
      return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
    },

    /**
     * 更新移动端/竖屏标记。
     *
     * 竖屏布局不是通过纯 CSS media query 判断，而是记录在组件状态里；
     * 这样模板里的玩家位置、打枪动画位置也能使用同一套判断。
     */
    updateViewportMode() {
      const mobileLike = window.innerWidth <= 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      this.isMobile = mobileLike
      this.isPortrait = mobileLike && window.innerHeight >= window.innerWidth
    },

    getPlayerById(playerId) {
      return this.players.find(p => p.id === playerId)
    },

    /**
     * 竖屏时给玩家追加 left/right/center 分类。
     *
     * 左右侧玩家需要把头像、名称、分数分别向两侧对齐，
     * 中间玩家则保持居中，给牌堆留出可读空间。
     */
    getPortraitPlayerSideClass(index) {
      if (!this.isMobile || !this.isPortrait) return ''
      const style = this.getPlayerPosition(index, this.positionedPlayers.length)
      const left = parseFloat(style.left)
      if (Number.isNaN(left)) return ''
      const center = window.innerWidth / 2
      if (left < center - 35) return 'portrait-left-player'
      if (left > center + 35) return 'portrait-right-player'
      return 'portrait-center-player'
    },

    getSweepRole(playerId) {
      const result = this.sweepAnimation && this.sweepAnimation.result
      if (!result) return null
      if (result.winner === playerId) return 'shooter'
      if (result.loser === playerId) return 'target'
      return null
    },

    getHomeRunPlayerIds() {
      if (!this.compareResults || this.compareResults.length === 0) return []
      if (this.compareResults.some(r => r.type === 'special')) return []
      const validPlayers = this.players.filter(p => !p.isFolded)
      if (this.players.length <= 2) return []

      const validIds = validPlayers.map(p => p.id)
      const allIds = this.players.map(p => p.id)
      const sweepResults = this.compareResults.filter(r => r.sweep && r.type !== 'special' && r.type !== 'odd')

      return validIds.filter(playerId => {
        const sweptOpponents = new Set()
        sweepResults.forEach(result => {
          if (result.winner === playerId) sweptOpponents.add(result.loser)
        })
        const otherIds = allIds.filter(id => id !== playerId)
        return otherIds.length > 0 && otherIds.every(id => sweptOpponents.has(id))
      })
    },

    isPlayerSweep(playerId) {
      if (!this.compareResults || this.compareResults.length === 0) return false
      return this.compareResults.some(r => r.sweep && r.winner === playerId)
    },

    isPlayerTarget(playerId) {
      if (!this.compareResults || this.compareResults.length === 0) return false
      return this.compareResults.some(r => r.sweep && r.loser === playerId)
    },

    getRowScoreClass(playerId, row) {
      const score = this.getRowScore(playerId, row)
      if (score.startsWith('+')) return 'positive'
      if (score.startsWith('-')) return 'negative'
      return 'zero'
    },

    async goHome() {
      const confirmed = await this.$confirm({
        title: '退出游戏',
        message: '确定要退出游戏吗？',
        confirmText: '退出',
        cancelText: '取消'
      })
      if (confirmed) {
        if (this.isOnlineMode) {
          this.stopRoomPolling()
          await this.$store.dispatch('multiplayer/leaveRoom')
        }
        this.$router.push('/')
      }
    },
    
    // 判断是否该显示某玩家的牌
    shouldShowCards(player) {
      if (!player || player.isFolded) {
        if (!player || !player.isFolded) return false
        if (this.gameStatus !== 'comparing' && this.gameStatus !== 'settling') return false
        return ['front', 'middle', 'back'].every(row => this.isRowRevealed(player.id, row))
      }
      if (this.gameStatus !== 'comparing' && this.gameStatus !== 'settling') return false
      if (player.specialHand && Array.isArray(player.hands) && player.hands.length === 13) return true
      return player.isLayoutConfirmed
    },

    getPlayerDisplayLayout(player) {
      if (!player) return { front: [], middle: [], back: [] }

      const layout = player.layout || {}
      const hasCompleteLayout = layout.front?.length === 3 &&
        layout.middle?.length === 5 &&
        layout.back?.length === 5
      if (hasCompleteLayout) {
        return layout
      }

      if (player.specialHand && Array.isArray(player.hands) && player.hands.length === 13) {
        return resolveSpecialHandLayout(player.hands, player.specialHand)
      }

      return layout
    },

    hasDisplayLayout(player) {
      const layout = this.getPlayerDisplayLayout(player)
      return layout.front?.length === 3 &&
        layout.middle?.length === 5 &&
        layout.back?.length === 5
    },

    sortPlayerLayoutRow(row) {
      const cards = this.playerLayout[row]
      if (!Array.isArray(cards) || cards.length <= 1) return

      const manualSelectedId = this.selectedManualSlotCard?.row === row
        ? cards[this.selectedManualSlotCard.index]?.id
        : null
      const smartSelectedId = this.selectedSmartCard?.row === row
        ? cards[this.selectedSmartCard.index]?.id
        : null

      const sorted = sortByRank(cards, true)
      this.playerLayout[row] = sorted

      if (manualSelectedId) {
        const index = sorted.findIndex(card => card.id === manualSelectedId)
        if (index >= 0) {
          this.selectedManualSlotCard = { row, index }
        }
      }
      if (smartSelectedId) {
        const index = sorted.findIndex(card => card.id === smartSelectedId)
        if (index >= 0) {
          this.selectedSmartCard = { row, index }
        }
      }
    },

    sortAllPlayerLayoutRows() {
      ;['front', 'middle', 'back'].forEach(row => this.sortPlayerLayoutRow(row))
    },

    // 判断是否为当前玩家
    isCurrentPlayer(player) {
      const myId = this.isOnlineMode ? this.$store.state.playerId : this.humanPlayer?.id
      return player.id === myId
    },

    getCurrentOnlinePlayer() {
      if (!this.isOnlineMode) return this.humanPlayer
      return this.players.find(p => p.id === this.$store.state.playerId)
    },

    getDealingDisplayHands(player) {
      if (this.isOnlineMode && !this.isCurrentPlayer(player)) {
        const realCount = player.hands ? player.hands.length : 0
        const dealtCount = this.onlineDealtCounts[player.id] || 0
        if (realCount === 0 && this.gameStatus === 'dealing') {
          return Array.from({ length: Math.min(dealtCount, 13) }, (_, index) => ({
            id: `deal-back-${player.id}-${index}`
          }))
        }
        if (realCount === 0 && this.gameStatus === 'arranging' && !player.isLayoutConfirmed) {
          return Array.from({ length: 13 }, (_, index) => ({
            id: `arrange-back-${player.id}-${index}`
          }))
        }
      }
      return player.hands || []
    },

    getDealFlyVars(playerId) {
      if (this.gameStatus !== 'dealing') return {}
      const index = this.players.findIndex(p => p.id === playerId)
      if (index === -1) return {}
      const cx = window.innerWidth / 2
      const cy = window.innerHeight / 2
      const pos = this.getCardsPosition(index, this.positionedPlayers.length)
      const left = parseFloat(pos.left) || cx
      const top = parseFloat(pos.top) || cy
      return {
        '--fly-x': (cx - left) + 'px',
        '--fly-y': (cy - top) + 'px'
      }
    },

    // 根据屏幕尺寸动态选择 margin
    getDynamicMargins() {
      const sw = window.innerWidth
      const sh = window.innerHeight
      if (sh <= 500) {
        return { marginX: 55, marginY: 40 }
      }
      if (sw <= 768) {
        return { marginX: 65, marginY: 45 }
      }
      return { marginX: 120, marginY: 100 }
    },

    getDynamicCardMargins() {
      const sw = window.innerWidth
      const sh = window.innerHeight
      if (sh <= 500) {
        return { marginX: 145, marginY: 100 }
      }
      if (sw <= 768) {
        return { marginX: 160, marginY: 120 }
      }
      return { marginX: 280, marginY: 220 }
    },

    // 根据玩家数量和索引计算位置样式（圆角矩形边界弧长均匀分布）
    getPlayerPosition(index, totalPlayers) {
      const { marginX, marginY } = this.getDynamicMargins()
      return this.getRoundedRectPosition(index, totalPlayers, marginX, marginY)
    },

    // 计算牌的独立位置（圆角矩形边界弧长均匀分布，比玩家位置小一圈）
    getCardsPosition(index, totalPlayers) {
      const { marginX, marginY } = this.getDynamicCardMargins()
      return this.getRoundedRectPosition(index, totalPlayers, marginX, marginY, true)
    },

    // 圆角矩形边界弧长均匀分布的通用计算
    // marginX/marginY 为内边距，值越大圆角矩形越小
    getRoundedRectPosition(index, totalPlayers, marginX, marginY, isCardsPosition = false) {
      const myId = this.isOnlineMode ? this.$store.state.playerId : this.humanPlayer?.id
      const humanIndex = this.players.findIndex(p => p.id === myId)

      const screenWidth = window.innerWidth
      const screenHeight = window.innerHeight
      const centerX = screenWidth / 2
      const centerY = screenHeight / 2
      const halfW = (screenWidth - marginX * 2) / 2
      const halfH = (screenHeight - marginY * 2) / 2
      const isMobile = screenWidth <= 768 || screenHeight <= 500
      const cornerR = Math.min(halfW, halfH) * (isMobile ? 0.12 : 0.35)
      const transform = isMobile && isCardsPosition
        ? 'translate(-50%, -50%) scale(0.55)'
        : 'translate(-50%, -50%)'

      let positionIndex
      if (index === humanIndex) {
        positionIndex = 0
      } else if (index > humanIndex) {
        positionIndex = index - humanIndex
      } else {
        positionIndex = totalPlayers - (humanIndex - index)
      }

      // 圆角矩形边界分段（从底部中点出发，沿边界逆时针方向）
      const straightH = halfW - cornerR  // 水平直边半长
      const straightV = halfH - cornerR  // 垂直直边半长
      const arcLen = (Math.PI * cornerR) / 2
      const perimeter = 4 * straightH + 4 * straightV + 4 * arcLen
      const s = (positionIndex / totalPlayers) * perimeter

      let x, y, acc = 0

      // 段1: 底部中点 → 右下弧起点（右下直边）
      let seg = straightH
      if (s < acc + seg) {
        const u = s - acc
        x = centerX + u
        y = centerY + halfH
        return { position: 'absolute', left: `${x}px`, top: `${y}px`, transform }
      }
      acc += seg

      // 段2: 右下弧起点 → 右边下端点（右下圆弧）
      seg = arcLen
      if (s < acc + seg) {
        const u = s - acc
        const alpha = -Math.PI / 2 + u / cornerR
        x = centerX + (halfW - cornerR) + cornerR * Math.cos(alpha)
        y = centerY + halfH - cornerR - cornerR * Math.sin(alpha)
        return { position: 'absolute', left: `${x}px`, top: `${y}px`, transform }
      }
      acc += seg

      // 段3: 右边下端点 → 右边上端点（右边直边）
      seg = 2 * straightV
      if (s < acc + seg) {
        const u = s - acc
        x = centerX + halfW
        y = centerY + halfH - cornerR - u
        return { position: 'absolute', left: `${x}px`, top: `${y}px`, transform }
      }
      acc += seg

      // 段4: 右边上端点 → 顶部右端点（右上圆弧）
      seg = arcLen
      if (s < acc + seg) {
        const u = s - acc
        const alpha = u / cornerR
        x = centerX + (halfW - cornerR) + cornerR * Math.cos(alpha)
        y = centerY - halfH + cornerR - cornerR * Math.sin(alpha)
        return { position: 'absolute', left: `${x}px`, top: `${y}px`, transform }
      }
      acc += seg

      // 段5: 顶部右端点 → 顶部左端点（上边直边）
      seg = 2 * straightH
      if (s < acc + seg) {
        const u = s - acc
        x = centerX + halfW - cornerR - u
        y = centerY - halfH
        return { position: 'absolute', left: `${x}px`, top: `${y}px`, transform }
      }
      acc += seg

      // 段6: 顶部左端点 → 左边上端点（左上圆弧）
      seg = arcLen
      if (s < acc + seg) {
        const u = s - acc
        const alpha = Math.PI / 2 + u / cornerR
        x = centerX + (-halfW + cornerR) + cornerR * Math.cos(alpha)
        y = centerY - halfH + cornerR - cornerR * Math.sin(alpha)
        return { position: 'absolute', left: `${x}px`, top: `${y}px`, transform }
      }
      acc += seg

      // 段7: 左边上端点 → 左边下端点（左边直边）
      seg = 2 * straightV
      if (s < acc + seg) {
        const u = s - acc
        x = centerX - halfW
        y = centerY - halfH + cornerR + u
        return { position: 'absolute', left: `${x}px`, top: `${y}px`, transform }
      }
      acc += seg

      // 段8: 左边下端点 → 底部左端点（左下圆弧）
      seg = arcLen
      if (s < acc + seg) {
        const u = s - acc
        const alpha = Math.PI + u / cornerR
        x = centerX + (-halfW + cornerR) + cornerR * Math.cos(alpha)
        y = centerY + halfH - cornerR - cornerR * Math.sin(alpha)
        return { position: 'absolute', left: `${x}px`, top: `${y}px`, transform }
      }
      acc += seg

      // 段9: 底部左端点 → 底部中点（左下直边）
      const u = s - acc
      x = centerX + (-halfW + cornerR) + u
      y = centerY + halfH
      return { position: 'absolute', left: `${x}px`, top: `${y}px`, transform }
    },

    // 计算扇形排列的牌位置
    // 目标：后面的牌只覆盖前面牌的 2/3，露出 1/3 牌头
    getFanCardStyle(index, total, rowType, cardSize) {
      // 扇形角度范围：3张牌用30度，5张牌用50度（展开角度更大）
      const fanAngle = total === 3 ? 30 : 50
      const angleStep = fanAngle / (total - 1)
      const startAngle = -fanAngle / 2
      const angle = startAngle + index * angleStep

      const rad = (angle * Math.PI) / 180
      const isMobile = window.innerWidth <= 768

      // 根据牌 size 确定牌宽
      const cardWidth = isMobile
        ? (cardSize === 'small' ? 76 : 60)
        : (cardSize === 'small' ? 54 : 38)

      // 目标相邻牌中心水平间距 = 牌宽 / 3（露出1/3，覆盖2/3）
      const sinStep = total === 3
        ? Math.sin(15 * Math.PI / 180)
        : Math.sin(12.5 * Math.PI / 180)
      const targetSpacing = cardWidth / 3
      let radius = targetSpacing / sinStep

      // 限制在合理范围内，避免过于分散或拥挤
      const maxRadius = total === 3
        ? (isMobile ? 120 : 80)
        : (isMobile ? 150 : 110)
      const minRadius = total === 3
        ? (isMobile ? 40 : 30)
        : (isMobile ? 50 : 40)
      radius = Math.max(minRadius, Math.min(maxRadius, Math.round(radius)))

      const x = Math.sin(rad) * radius
      const y = Math.cos(rad) * radius * 0.15 // 减小垂直偏移

      // z-index: 后一张(index大)显示在前一张上面
      return {
        transform: `translateX(${x}px) translateY(${y}px) rotate(${angle}deg)`,
        zIndex: index + 1
      }
    },

    // 计算摆牌阶段背面牌的扇形位置
    // 与比牌阶段 getFanCardStyle 保持完全一致，确保两种状态下牌的位置相同
    getStackFanStyle(index, total, cardSize) {
      // 复用 getFanCardStyle 的逻辑
      return this.getFanCardStyle(index, total, null, cardSize)
    },
    
    // 开始发牌动画
    startDealingAnimation() {
      const queue = this.$store.state.dealingQueue
      if (!queue || queue.length === 0) {
        this.$store.dispatch('finishDeal')
        return
      }

      // 控制整体在3秒以内完成，每张牌至少间隔30ms
      const totalCards = queue.length
      const interval = Math.max(30, Math.floor(3000 / totalCards))

      const dealInterval = setInterval(() => {
        if (queue.length === 0 || this.$store.state.gameStatus !== 'dealing') {
          clearInterval(dealInterval)
          if (queue.length === 0) {
            setTimeout(() => {
              this.$store.dispatch('finishDeal')
            }, 1000) // 等最后一张牌动画显示完 1 秒后进入摆牌
          }
          return
        }
        playDealSound()
        this.$store.commit('DEAL_ONE_CARD')
      }, interval)
    },

    startOnlineDealingAnimation(playerId, hands, payload = {}) {
      if (!Array.isArray(hands) || hands.length === 0) return
      this.onlineDealInProgress = true
      this.prepareOnlineRound(payload)
      this.onlineDealtCount = 0
      this.onlineDealtCounts = {}
      this.players.forEach(player => {
        this.$set(this.onlineDealtCounts, player.id, 0)
      })
      this.$store.commit('SET_PLAYER_HANDS', { playerId, hands: [] })

      const dealMode = payload.dealMode || this.config.dealMode || 'single'
      const handsByPlayerId = { [playerId]: hands }
      const queue = buildDealingQueue(this.players, handsByPlayerId, dealMode)
      this.$store.commit('SET_DEALING_QUEUE', queue)
      this.$store.commit('SET_GAME_STATUS', 'dealing')

      const totalCards = queue.length
      const interval = Math.max(30, Math.floor(3000 / Math.max(totalCards, 1)))
      const dealInterval = setInterval(() => {
        const currentQueue = this.$store.state.dealingQueue
        if (!currentQueue || currentQueue.length === 0 || this.$store.state.gameStatus !== 'dealing') {
          clearInterval(dealInterval)
          if (this.$store.state.gameStatus === 'dealing') {
            setTimeout(() => {
              this.onlineDealInProgress = false
              this.onlineDealtCount = 13
              this.$store.commit('SET_PLAYER_HANDS', { playerId, hands })
              this.$store.commit('SET_GAME_STATUS', 'arranging')
              this.enterOnlineArrangeStage(payload)
            }, 1000)
          }
          return
        }
        playDealSound()
        const nextItem = this.$store.state.dealingQueue[0]
        if (nextItem && nextItem.playerId) {
          this.$set(
            this.onlineDealtCounts,
            nextItem.playerId,
            (this.onlineDealtCounts[nextItem.playerId] || 0) + 1
          )
        }
        this.$store.commit('DEAL_ONE_CARD')
        this.onlineDealtCount = Math.min(this.onlineDealtCount + 1, currentQueue.length)
      }, interval)
    },

    // 开始比牌动画
    /**
     * 比牌动画总入口。
     *
     * 动画顺序：
     * 1. 3/2/1 倒计时；
     * 2. 播放“开始比牌”；
     * 3. 有报道牌时逐个播放特殊牌型动画并亮牌；
     * 4. 头/中/尾三道按牌力从小到大逐个玩家翻开；
     * 4. 展示每道浮动分；
     * 5. 播放打枪动画；
     * 6. 若触发全垒打，播放全垒打动画；
     * 7. 进入 settling。局数未结束显示“下一局”；创建房间局数已结束显示“返回大厅”。
     */
    startCompareAnimation() {
      if (this.compareCountdown > 0 || this.revealStage > 0 || this.sweepAnimation) return

      const runId = ++this.compareAnimationRunId
      this.revealStage = 0
      this.resetRevealedRows()
      this.sweepAnimation = null // 重置打枪动画
      this.homeRunAnimation = null // 重置全垒打动画
      this.wulongAnimation = null
      this.specialHandAnimation = null
      this.floatingScores = []
      this.moneyPops = []
      this.pendingOnlineScores = null

      // 倒计时 3 2 1
      this.compareCountdown = 3
      playCompareCountdownSound(3)
      const countdownTimer = setInterval(() => {
        if (runId !== this.compareAnimationRunId) {
          clearInterval(countdownTimer)
          return
        }
        this.compareCountdown--
        if (this.compareCountdown > 0) {
          playCompareCountdownSound(this.compareCountdown)
        } else {
          clearInterval(countdownTimer)
          this.compareCountdown = 0
          // 播放比牌开始音效
          playCompareStartSound()
          this.startRevealStages()
        }
      }, 1000)
    },

    resetRevealedRows() {
      this.revealedRows = {
        front: [],
        middle: [],
        back: []
      }
    },

    isRowRevealed(playerId, row) {
      return this.revealedRows[row] && this.revealedRows[row].includes(playerId)
    },

    getRowHandType(player, row) {
      if (!player) return ''
      if (player.specialHand) return player.specialHand.name
      const layout = this.getPlayerDisplayLayout(player)
      if (!layout[row] || !layout[row].length) return ''
      try {
        const cards = layout[row]
        const result = row === 'front' ? evaluateThreeCards(cards) : evaluateFiveCards(cards)
        return result.rank?.name || ''
      } catch (e) {
        return ''
      }
    },

    isSpecialHand(row, player) {
      // 整手特殊牌型（一条龙等）已在牌组顶部统一展示，不在头/中/尾道重复显示
      if (player?.specialHand) return false
      const layout = this.getPlayerDisplayLayout(player)
      if (!layout[row] || !layout[row].length) return false
      try {
        const cards = layout[row]
        const result = row === 'front' ? evaluateThreeCards(cards) : evaluateFiveCards(cards)
        const rankValue = result.rank?.value || 0
        if (row === 'front') {
          return rankValue === 4
        } else if (row === 'middle') {
          return rankValue >= 7 && rankValue <= 10
        } else if (row === 'back') {
          return rankValue >= 8 && rankValue <= 10
        }
        return false
      } catch (e) {
        return false
      }
    },

    getPlayersByRowStrength(row) {
      return this.players
        .filter(player => {
          if (player.specialHand) return false
          if (!this.shouldShowCards(player)) return false
          const layout = this.getPlayerDisplayLayout(player)
          return layout[row] && layout[row].length > 0
        })
        .sort((a, b) => {
          const layoutA = this.getPlayerDisplayLayout(a)
          const layoutB = this.getPlayerDisplayLayout(b)
          const compare = row === 'front'
            ? compareThreeCards(layoutA.front, layoutB.front)
            : compareFiveCards(layoutA[row], layoutB[row])
          return compare
        })
    },

    specialHandTheme(type) {
      const themes = {
        DRAGON: { mark: '龙' },
        ALL_BIG: { mark: '大' },
        ALL_SMALL: { mark: '小' },
        SIX_PAIRS_HALF: { mark: '对' },
        THREE_FLUSH: { mark: '花' },
        THREE_STRAIGHT: { mark: '顺' }
      }
      return themes[type] || { mark: '特' }
    },

    isSpecialHandBurst(player) {
      return !!(this.specialHandAnimation && this.specialHandAnimation.show && this.specialHandAnimation.playerId === player.id)
    },

    isSpecialHandShown(player) {
      if (!player?.specialHand) return false
      return ['front', 'middle', 'back'].every(row => this.isRowRevealed(player.id, row))
    },

    fanCardStyle(index, total, row, player, delayOffset = 0) {
      const style = this.getFanCardStyle(index, total, row, 'small')
      if (this.isSpecialHandBurst(player)) {
        style.animationDelay = `${(delayOffset + index) * 0.045}s`
      }
      return style
    },

    revealPlayerRows(playerId) {
      ;['front', 'middle', 'back'].forEach(row => {
        if (!this.revealedRows[row].includes(playerId)) {
          this.$set(this.revealedRows, row, [...this.revealedRows[row], playerId])
        }
      })
    },

    /** 报道牌：比牌前逐个亮牌，播放牌型动画和得分飘字 */
    async revealSpecialHandPlayers(runId) {
      const specials = this.players
        .filter(player => player.specialHand && !player.isFolded)
        .sort((a, b) => (b.specialHand.score || 0) - (a.specialHand.score || 0))

      if (specials.length === 0) {
        this.specialHandAnimation = null
        return true
      }

      for (const player of specials) {
        if (runId !== this.compareAnimationRunId) return false
        const theme = this.specialHandTheme(player.specialHand.type)
        this.specialHandAnimation = {
          show: true,
          playerId: player.id,
          playerName: player.name,
          type: player.specialHand.type || '',
          name: player.specialHand.name,
          score: player.specialHand.score,
          mark: theme.mark
        }
        this.revealPlayerRows(player.id)
        playSpecialHandSound(player.specialHand.type)
        playHandTypeVoice(player.specialHand.name)
        await this.wait(420)
        if (runId !== this.compareAnimationRunId) return false
        this.showSpecialFloatingScores(player)
        await this.wait(1700)
        if (runId !== this.compareAnimationRunId) return false
        this.specialHandAnimation = { ...this.specialHandAnimation, show: false }
        await this.wait(180)
      }

      this.specialHandAnimation = null
      return true
    },

    showSpecialFloatingScores(player) {
      if (!this.compareResults || !player) return
      const scores = {}
      this.players.forEach(p => { scores[p.id] = 0 })
      this.compareResults.forEach(result => {
        if (result.type !== 'special' || result.winner !== player.id) return
        scores[result.winner] = (scores[result.winner] || 0) + result.score
        scores[result.loser] = (scores[result.loser] || 0) - result.score
      })
      this.pushFloatingScores(scores)
    },

    /**
     * 翻开指定一道牌。
     *
     * 同一道内按牌力从小到大翻开，每翻一位玩家播放对应牌型语音。
     * runId 用来防止用户点击下一局后，上一局未结束的 await 继续修改界面。
     */
    async revealRowByStrength(row, stage, rowSoundIndex, runId) {
      this.revealStage = stage
      playRevealSound(rowSoundIndex)

      const orderedPlayers = this.getPlayersByRowStrength(row)
      for (const player of orderedPlayers) {
        if (runId !== this.compareAnimationRunId) return false

        if (!this.revealedRows[row].includes(player.id)) {
          this.$set(this.revealedRows, row, [...this.revealedRows[row], player.id])
        }

        playHandTypeVoice(this.getRowHandType(player, row))
        await this.wait(800)
      }

      if (runId !== this.compareAnimationRunId) return false
      this.showFloatingScores(row)
      await this.wait(300)
      return true
    },

    /** 倒水（乌龙）玩家：比牌前一次性亮牌并播放乌龙动画 */
    async revealWulongPlayers(runId) {
      const folded = this.players.filter(p => p.isFolded && !p.specialHand)
      if (folded.length === 0) {
        this.wulongAnimation = null
        return true
      }

      const playerIds = folded.map(p => p.id)
      this.wulongAnimation = { show: true, playerIds }

      folded.forEach(player => {
        ;['front', 'middle', 'back'].forEach(row => {
          if (!this.revealedRows[row].includes(player.id)) {
            this.$set(this.revealedRows, row, [...this.revealedRows[row], player.id])
          }
        })
      })

      playHandTypeVoice('乌龙')
      await this.wait(1600)
      if (runId !== this.compareAnimationRunId) return false

      this.wulongAnimation = { show: false, playerIds }
      return true
    },

    // 依次显示头道、中道、尾道；每道内按牌力从小到大逐个玩家翻开
    /** 依次翻开头道、中道、尾道。 */
    async startRevealStages() {
      const runId = this.compareAnimationRunId
      if (!await this.revealSpecialHandPlayers(runId)) return
      if (!await this.revealWulongPlayers(runId)) return
      if (!await this.revealRowByStrength('front', 1, 1, runId)) return
      if (!await this.revealRowByStrength('middle', 2, 2, runId)) return
      if (!await this.revealRowByStrength('back', 3, 3, runId)) return
      if (runId !== this.compareAnimationRunId) return
      this.revealStage = 4
      this.checkAndShowSweepAnimation()
    },

    // 显示比牌浮动分数
    showFloatingScores(row) {
      if (!this.compareResults || this.compareResults.length === 0) return

      const scores = {}
      this.players.forEach(p => { scores[p.id] = 0 })

      this.compareResults.forEach(result => {
        if (result.type === 'special' || result.type === 'odd') return
        if (result[row] && result[row].winner) {
          const winnerId = result[row].winner
          const loserId = result[row].winner === result.player1 ? result.player2 : result.player1
          scores[winnerId] += result[row].score
          scores[loserId] -= result[row].score
        }
      })

      this.pushFloatingScores(scores)
    },

    pushFloatingScores(scores) {
      const newFloats = []
      this.players.forEach((player, index) => {
        const score = scores[player.id]
        if (!score) return

        const posStyle = this.getPlayerPosition(index, this.players.length)
        const left = parseFloat(posStyle.left)
        const top = parseFloat(posStyle.top)

        const floatId = 'float-' + Date.now() + '-' + Math.random()
        newFloats.push({
          id: floatId,
          text: score > 0 ? '+' + score : String(score),
          left,
          top,
          isPositive: score > 0
        })
      })

      if (newFloats.length > 0) {
        this.floatingScores.push(...newFloats)
        setTimeout(() => {
          this.floatingScores = this.floatingScores.filter(f => !newFloats.some(nf => nf.id === f.id))
        }, 1500)
      }
    },

    /** 比牌动画全部结束后，按本局净分播放赢钱或输钱动画。 */
    playRoundMoneyAnimation() {
      const pops = []
      this.players.forEach((player, index) => {
        const score = Number(player.score) || 0
        if (!score) return

        const posStyle = this.getPlayerPosition(index, this.players.length)
        const coinCount = Math.min(8, Math.max(4, Math.round(Math.abs(score) / 3)))
        pops.push({
          id: 'money-' + player.id + '-' + Date.now(),
          left: parseFloat(posStyle.left),
          top: parseFloat(posStyle.top),
          text: score > 0 ? '+' + score : String(score),
          isPositive: score > 0,
          coins: Array.from({ length: coinCount }, (_, i) => ({
            id: i,
            dx: Math.round((i - (coinCount - 1) / 2) * 16),
            delay: i * 0.05
          }))
        })
      })

      this.moneyPops = pops
      const runId = this.compareAnimationRunId
      const delay = pops.length ? 1800 : 0
      setTimeout(() => {
        if (runId !== this.compareAnimationRunId) return
        this.moneyPops = []
        if (this.gameStatus === 'settling') {
          this.roundResultDialog = true
        }
      }, delay)
    },

    // 检查并按顺序显示打枪动画
    async checkAndShowSweepAnimation() {
      // 查找打枪结果
      const sweepResults = this.compareResults.filter(r => r.sweep)
      if (sweepResults.length > 0) {
        await this.playSweepAnimationQueue(sweepResults)
      }
      await this.checkAndShowHomeRunAnimation()
      this.finishCompareAnimation()
    },

    async playSweepAnimationQueue(sweepResults) {
      const runId = this.compareAnimationRunId

      for (const result of sweepResults) {
        if (runId !== this.compareAnimationRunId) return

        // 判断被打玩家是否在打枪玩家的左边
        const shooterPlayer = this.getPlayerById(result.winner)
        const targetPlayer = this.getPlayerById(result.loser)
        const { rotation, needFlip } = this.calculateGunRotation(shooterPlayer, targetPlayer)

        this.sweepAnimation = {
          show: true,
          result,
          gunRotation: rotation,
          gunFlip: needFlip
        }
        playGunSound()
        setTimeout(() => playHitSound(), 200)
        setTimeout(() => playHitSound(), 500)

        await this.wait(800)
        if (runId !== this.compareAnimationRunId) return
        this.sweepAnimation = null
        await this.wait(250)
      }
    },

    async checkAndShowHomeRunAnimation() {
      const homeRunPlayerIds = this.getHomeRunPlayerIds()
      if (homeRunPlayerIds.length === 0) return

      const runId = this.compareAnimationRunId
      this.homeRunAnimation = {
        show: true,
        playerIds: homeRunPlayerIds
      }
      playHomeRunSound()

      await this.wait(1400)
      if (runId !== this.compareAnimationRunId) return
      this.homeRunAnimation = null
      this.wulongAnimation = null
      await this.wait(150)
    },

    // 计算枪口应该旋转的角度，使其指向目标玩家
    calculateGunRotation(shooter, target) {
      if (!shooter || !target) return { rotation: 0, needFlip: false }
      
      const shooterIndex = this.positionedPlayers.findIndex(p => p.id === shooter.id)
      const targetIndex = this.positionedPlayers.findIndex(p => p.id === target.id)
      
      if (shooterIndex === -1 || targetIndex === -1) return { rotation: 0, needFlip: false }
      
      // 获取两个玩家的位置样式
      const shooterPos = this.getCardsPosition(shooterIndex, this.positionedPlayers.length)
      const targetPos = this.getCardsPosition(targetIndex, this.positionedPlayers.length)
      
      // 解析位置坐标
      const shooterLeft = parseFloat(shooterPos.left)
      const shooterTop = parseFloat(shooterPos.top)
      const targetLeft = parseFloat(targetPos.left)
      const targetTop = parseFloat(targetPos.top)
      
      // 判断目标是否在左边
      const needFlip = targetLeft < shooterLeft
      
      // 如果目标在左边，先翻转坐标系
      const effectiveDeltaX = needFlip ? -(targetLeft - shooterLeft) : (targetLeft - shooterLeft)
      const deltaY = targetTop - shooterTop
      
      // 计算两点之间的角度（弧度）
      const angleRad = Math.atan2(deltaY, effectiveDeltaX)
      
      // 转换为角度
      let angleDeg = angleRad * (180 / Math.PI)
      
      console.log('枪口旋转计算:', {
        shooter: shooter.name,
        target: target.name,
        shooterPos: { x: shooterLeft, y: shooterTop },
        targetPos: { x: targetLeft, y: targetTop },
        needFlip,
        deltaX: targetLeft - shooterLeft,
        effectiveDeltaX,
        deltaY,
        rotation: angleDeg
      })
      
      return { rotation: angleDeg, needFlip }
    },

    wait(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    finishCompareAnimation() {
      this.sweepAnimation = null
      this.homeRunAnimation = null
      this.wulongAnimation = null
      this.specialHandAnimation = null
      if (!this.isOnlineMode) {
        this.$store.dispatch('settleGame')
      } else {
        this.applyPendingOnlineScores()
        this.$store.commit('SET_GAME_STATUS', 'settling')
      }
      this.$nextTick(() => this.playRoundMoneyAnimation())
    },

    applyPendingOnlineScores() {
      if (!this.pendingOnlineScores) return
      const scores = this.pendingOnlineScores.scores || this.pendingOnlineScores
      const roomRateScores = this.pendingOnlineScores.roomRateScores || {}
      Object.entries(scores).forEach(([playerId, score]) => {
        this.$store.commit('UPDATE_PLAYER_SCORE', {
          playerId,
          score,
          roomRateScore: roomRateScores[playerId] || 0
        })
      })
      this.pendingOnlineScores = null
    },

    openScoreDetail(player) {
      if (!player) return
      const detail = this.buildScoreDetail(player.id)
      this.scoreDetailDialog = {
        show: true,
        playerId: player.id,
        playerName: player.name,
        total: player.score || 0,
        detailSum: detail.detailSum,
        roomRate: player.roomRateScore || 0,
        sections: detail.sections
      }
    },

    closeScoreDetail() {
      this.scoreDetailDialog = {
        show: false,
        playerId: '',
        playerName: '',
        total: 0,
        detailSum: 0,
        roomRate: 0,
        sections: []
      }
    },

    buildScoreDetail(playerId) {
      const sections = [
        { key: 'special', title: '特殊牌型 / 奇牌', lines: [], subtotal: 0 },
        { key: 'base', title: '三道基础分', lines: [], subtotal: 0 },
        { key: 'sweep', title: '打枪', lines: [], subtotal: 0 },
        { key: 'fold', title: '乌龙', lines: [], subtotal: 0 },
        { key: 'homeRun', title: '全垒打', lines: [], subtotal: 0 },
        { key: 'yucai', title: '油菜', lines: [], subtotal: 0 },
        { key: 'roomRate', title: '开房积分', lines: [], subtotal: 0 }
      ]
      const byKey = Object.fromEntries(sections.map(section => [section.key, section]))
      const baseNetByResult = new Map()

      if (!this.compareResults || this.compareResults.length === 0) {
        return { sections, detailSum: 0 }
      }

      this.compareResults.forEach((result, index) => {
        const resultKey = this.getResultKey(result, index)
        if (result.type === 'special' || result.type === 'odd') {
          this.appendSpecialDetailLine(byKey.special.lines, playerId, result)
          return
        }

        const foldTag = this.getFoldCompareTag(result)
        let pairBaseNet = 0
        ;['front', 'middle', 'back'].forEach(row => {
          const rowResult = result[row]
          if (!rowResult) return

          const opponentId = result.player1 === playerId ? result.player2 : result.player1
          const isParticipant = playerId === result.player1 || playerId === result.player2
          if (!isParticipant) return

          if (!rowResult.winner) {
            byKey.base.lines.push({
              text: `${this.getRowName(row)} vs ${this.getPlayerName(opponentId)}${foldTag}`,
              score: 0,
              detail: '平局，不计分'
            })
            return
          }

          const loserId = rowResult.winner === result.player1 ? result.player2 : result.player1
          const horseMult = this.getHorseMultiplierForPair(result.player1, result.player2)
          const rawScore = rowResult.score * this.baseScoreValue()
          const finalScore = rawScore * horseMult
          pairBaseNet += rowResult.winner === result.player1 ? rawScore : -rawScore

          if (playerId === rowResult.winner || playerId === loserId) {
            const signed = playerId === rowResult.winner ? finalScore : -finalScore
            const opponentIdForRow = playerId === rowResult.winner ? loserId : rowResult.winner
            byKey.base.lines.push({
              text: `${this.getRowName(row)} vs ${this.getPlayerName(opponentIdForRow)}${foldTag}`,
              score: signed,
              detail: this.formatScoreFormula(rowResult.score, {
                horseMult,
                suffix: result.type === 'fold' ? '乌龙三道全输/全赢' : ''
              })
            })
          }
        })
        baseNetByResult.set(resultKey, pairBaseNet)

        if (result.sweep && result.winner && result.loser) {
          const horseMult = this.getHorseMultiplierForPair(result.winner, result.loser)
          const sweepBonus = this.getSweepBonusForResult(result)
          const rawSweep = sweepBonus * this.baseScoreValue()
          const finalSweep = rawSweep * horseMult
          if (playerId === result.winner || playerId === result.loser) {
            const signed = playerId === result.winner ? finalSweep : -finalSweep
            const opponentId = playerId === result.winner ? result.loser : result.winner
            byKey.sweep.lines.push({
              text: `${playerId === result.winner ? '打枪' : '被打'} ${this.getPlayerName(opponentId)}${foldTag}`,
              score: signed,
              detail: `${this.getSweepFormulaText(result)}${horseMult > 1 ? ' × 马牌2倍' : ''}`
            })
          }
        }
      })

      this.appendFoldScoreDetails(byKey.fold.lines, playerId)
      this.appendHomeRunScoreDetails(byKey.homeRun.lines, playerId)
      this.appendYucaiScoreDetails(byKey.yucai.lines, playerId, baseNetByResult)
      this.appendRoomRateDetails(byKey.roomRate.lines, playerId)

      let detailSum = 0
      sections.forEach(section => {
        section.subtotal = section.lines.reduce((sum, line) => sum + (Number(line.score) || 0), 0)
        if (section.key !== 'roomRate') {
          detailSum += section.subtotal
        }
      })

      return { sections, detailSum }
    },

    appendSpecialDetailLine(lines, playerId, result) {
      if (playerId !== result.winner && playerId !== result.loser) return
      const winnerId = result.winner
      const loserId = result.loser
      const horseMult = this.getHorseMultiplierForPair(winnerId, loserId)
      const rawScore = Number(result.score) || 0
      const finalScore = rawScore * horseMult
      const signed = playerId === winnerId ? finalScore : -finalScore
      const opponentId = playerId === winnerId ? loserId : winnerId
      const handName = result.specialHand?.name || (result.type === 'odd' ? '奇牌' : '特殊牌型')
      const unitScore = this.baseScoreValue() > 0 ? rawScore / this.baseScoreValue() : rawScore
      lines.push({
        text: `${result.type === 'odd' ? '奇牌' : '特殊牌型'} vs ${this.getPlayerName(opponentId)}`,
        score: signed,
        detail: `${handName}，${this.formatScoreFormula(unitScore, { horseMult })}`
      })
    },

    appendFoldScoreDetails(lines, playerId) {
      const player = this.getPlayerById(playerId)
      if (player?.isFolded) {
        lines.push({
          text: '本局乌龙',
          score: 0,
          detail: '与未乌龙玩家比牌时三道全输；下方「三道基础分」「打枪」已列出每一道得分。'
        })
        return
      }

      const foldedOpponents = this.compareResults
        .filter(result => result.type === 'fold' && result.winner === playerId)
        .map(result => this.getPlayerName(result.loser))
      if (foldedOpponents.length > 0) {
        lines.push({
          text: '对手乌龙',
          score: 0,
          detail: `对 ${foldedOpponents.join('、')} 按三道全赢计算；下方「三道基础分」「打枪」已列出每一道得分。`
        })
      }
    },

    appendHomeRunScoreDetails(lines, playerId) {
      const homeRunPlayerIds = this.getHomeRunPlayerIds()
      if (homeRunPlayerIds.length === 0) return

      const unfoldedIds = this.players.filter(p => !p.isFolded).map(p => p.id)
      homeRunPlayerIds.forEach(homeRunId => {
        const opponents = unfoldedIds.filter(id => id !== homeRunId)
        opponents.forEach(opponentId => {
          if (playerId !== homeRunId && playerId !== opponentId) return
          const sweepResult = this.compareResults.find(r => r.sweep && r.winner === homeRunId && r.loser === opponentId)
          const unitScore = sweepResult ? sweepResult.score : 5
          const horseMult = this.getHorseMultiplierForPair(homeRunId, opponentId)
          const rawScore = unitScore * this.baseScoreValue()
          const finalScore = rawScore * horseMult
          const signed = playerId === homeRunId ? finalScore : -finalScore
          const otherId = playerId === homeRunId ? opponentId : homeRunId
          lines.push({
            text: `${playerId === homeRunId ? '全垒打' : '被全垒打'} ${this.getPlayerName(otherId)}`,
            score: signed,
            detail: this.formatScoreFormula(unitScore, { horseMult })
          })
        })
      })
    },

    appendYucaiScoreDetails(lines, playerId, baseNetByResult) {
      const yucaiScore = this.config.enableYucai ? Math.max(Number(this.config.yucaiScore) || 0, 0) : 0
      const yucaiBonus = yucaiScore * this.baseScoreValue()
      if (yucaiBonus <= 0) return

      this.compareResults.forEach((result, index) => {
        if (result.type === 'special' || result.type === 'odd') return
        const pairBaseNet = baseNetByResult.get(this.getResultKey(result, index)) || 0
        if (pairBaseNet === 0) return
        const winnerId = pairBaseNet > 0 ? result.player1 : result.player2
        const loserId = pairBaseNet > 0 ? result.player2 : result.player1
        if (playerId !== winnerId && playerId !== loserId) return
        const signed = playerId === winnerId ? yucaiBonus : -yucaiBonus
        const opponentId = playerId === winnerId ? loserId : winnerId
        lines.push({
          text: `油菜 vs ${this.getPlayerName(opponentId)}`,
          score: signed,
          detail: `三道基础分合计${playerId === winnerId ? '赢' : '输'}，${yucaiScore} × 底分${this.baseScoreValue()}，油菜不套马牌`
        })
      })
    },

    appendRoomRateDetails(lines, playerId) {
      if (!this.config.allowRoomRate) return
      const percent = Number(this.config.roomRatePercent) || 2

      this.compareResults.forEach(result => {
        if (result.type === 'special' && result.winner === playerId && result.loser) {
          const horseMult = this.getHorseMultiplierForPair(result.winner, result.loser)
          const finalScore = (Number(result.score) || 0) * horseMult
          const bonus = this.computeRoomRateBonus(finalScore)
          if (bonus > 0) {
            lines.push({
              text: `特殊牌型 vs ${this.getPlayerName(result.loser)}`,
              score: bonus,
              detail: `赢分 ${finalScore} × ${percent}% 向上取整`
            })
          }
        }
      })

      const homeRunPlayerIds = this.getHomeRunPlayerIds()
      homeRunPlayerIds.forEach(homeRunId => {
        if (homeRunId !== playerId) return
        const unfoldedIds = this.players.filter(p => !p.isFolded).map(p => p.id)
        unfoldedIds.filter(id => id !== homeRunId).forEach(opponentId => {
          const sweepResult = this.compareResults.find(r => r.sweep && r.winner === homeRunId && r.loser === opponentId)
          const unitScore = sweepResult ? sweepResult.score : 5
          const horseMult = this.getHorseMultiplierForPair(homeRunId, opponentId)
          const finalScore = unitScore * this.baseScoreValue() * horseMult
          const bonus = this.computeRoomRateBonus(finalScore)
          if (bonus > 0) {
            lines.push({
              text: `全垒打 ${this.getPlayerName(opponentId)}`,
              score: bonus,
              detail: `赢分 ${finalScore} × ${percent}% 向上取整`
            })
          }
        })
      })

      if (lines.length === 0) {
        const player = this.getPlayerById(playerId)
        if (player?.roomRateScore) {
          lines.push({
            text: '本局开房积分',
            score: player.roomRateScore,
            detail: `按可计积分赢分向上取整 ${percent}%，不计入本局分`
          })
        }
      }
    },

    getFoldCompareTag(result) {
      return result.type === 'fold' ? '（乌龙）' : ''
    },

    formatScoreFormula(unitScore, options = {}) {
      const baseScore = this.baseScoreValue()
      const horseMult = Number(options.horseMult) || 1
      let formula = `${unitScore} × 底分${baseScore}`
      if (horseMult > 1) {
        formula += ' × 马牌2倍'
      }
      if (options.suffix) {
        formula += `（${options.suffix}）`
      }
      return formula
    },

    computeRoomRateBonus(finalScore) {
      const score = Number(finalScore) || 0
      if (!this.config.allowRoomRate || score <= 0) return 0
      const percent = Number(this.config.roomRatePercent) || 2
      return Math.ceil(score * Math.max(percent, 0) / 100)
    },

    getResultKey(result, index) {
      return `${result.player1 || result.winner || ''}-${result.player2 || result.loser || ''}-${index}`
    },

    getSweepFormulaText(result) {
      if (this.config.sweepScoreType === 'multiplier') {
        const rowScoreTotal = ['front', 'middle', 'back'].reduce((sum, row) => {
          return sum + (result[row] && result[row].winner ? result[row].score : 0)
        }, 0)
        const value = Number(this.config.sweepScoreValue) || 2
        return `三道赢分${rowScoreTotal} × (${value} - 1) × 底分${this.baseScoreValue()}`
      }
      return `${Number(this.config.sweepScoreValue) || 2} × 底分${this.baseScoreValue()}`
    },

    getHorseMultiplierForPair(playerId1, playerId2) {
      if (!this.config.enableHorse) return 1
      return this.playerHasHorseCard(playerId1) || this.playerHasHorseCard(playerId2) ? 2 : 1
    },

    playerHasHorseCard(playerId) {
      const player = this.getPlayerById(playerId)
      if (!player) return false
      const horseRank = Number(this.config.horseRank) || 1
      const horseSuit = this.config.horseSuit || 'hearts'
      const cards = [
        ...(Array.isArray(player.hands) ? player.hands : []),
        ...(Array.isArray(player.layout?.front) ? player.layout.front : []),
        ...(Array.isArray(player.layout?.middle) ? player.layout.middle : []),
        ...(Array.isArray(player.layout?.back) ? player.layout.back : [])
      ]
      return cards.some(card => {
        return card &&
          card.rank &&
          card.suit &&
          Number(card.rank.value) === horseRank &&
          card.suit.name === horseSuit
      })
    },

    baseScoreValue() {
      return Number(this.config.baseScore) || 1
    },

    getRowName(row) {
      return ({ front: '头道', middle: '中道', back: '尾道' })[row] || row
    },

    getPlayerName(playerId) {
      return this.getPlayerById(playerId)?.name || playerId || ''
    },

    formatSigned(value) {
      const numeric = Number(value) || 0
      return numeric > 0 ? `+${numeric}` : `${numeric}`
    },

    scoreTone(value) {
      const numeric = Number(value) || 0
      if (numeric > 0) return 'positive'
      if (numeric < 0) return 'negative'
      return 'zero'
    },

    closeRoundResult() {
      this.roundResultDialog = false
    },

    formatRankName(rank) {
      const rankValue = Number(rank) || 1
      const names = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' }
      return names[rankValue] || String(rankValue)
    },

    formatSuitName(suit) {
      const names = {
        hearts: '红桃',
        spades: '黑桃',
        clubs: '梅花',
        diamonds: '方块'
      }
      return names[suit] || suit || ''
    },

    formatExtraSuits(extraSuits = []) {
      if (!Array.isArray(extraSuits) || extraSuits.length === 0) return ''
      const counts = {}
      extraSuits.forEach(suit => {
        if (!suit) return
        counts[suit] = (counts[suit] || 0) + 1
      })
      return Object.keys(counts).map(suit => {
        const count = counts[suit]
        const label = this.formatSuitName(suit)
        return count > 1 ? `${label}×${count}` : label
      }).join(' ')
    },

    // 获取玩家某道的分数
    getRowScore(playerId, row) {
      if (!this.compareResults || this.compareResults.length === 0) return ''

      let totalScore = 0
      this.compareResults.forEach(result => {
        if (!result[row] || !result[row].winner) return
        const isParticipant = result.player1 === playerId || result.player2 === playerId
        if (!isParticipant) return

        const rowScore = result[row].score * this.config.baseScore
        if (result[row].winner === playerId) {
          totalScore += rowScore
        } else {
          totalScore -= rowScore
        }
      })

      if (totalScore > 0) return `+${totalScore}`
      if (totalScore < 0) return `${totalScore}`
      return '0'
    },

    // 获取玩家头中尾道总分数（用于验证）
    getTotalRowScore(playerId) {
      const front = parseInt(this.getRowScore(playerId, 'front')) || 0
      const middle = parseInt(this.getRowScore(playerId, 'middle')) || 0
      const back = parseInt(this.getRowScore(playerId, 'back')) || 0
      return front + middle + back
    },

    getSweepBonusForResult(result) {
      const rowScoreTotal = ['front', 'middle', 'back'].reduce((sum, row) => {
        return sum + (result[row] && result[row].winner ? result[row].score : 0)
      }, 0)
      const value = Number(this.config.sweepScoreValue) || 2

      if (this.config.sweepScoreType === 'multiplier') {
        return rowScoreTotal * Math.max(value - 1, 0)
      }

      return value
    },

    // 获取玩家打枪得分（打中别人）
    getSweepScore(playerId) {
      if (!this.compareResults || this.compareResults.length === 0) return 0

      let sweepScore = 0
      this.compareResults.forEach(result => {
        if (result.sweep && result.winner === playerId) {
          sweepScore += this.getSweepBonusForResult(result) * this.config.baseScore
        }
      })
      return sweepScore
    },

    // 获取玩家被枪得分（被别人打中）
    getBeatenScore(playerId) {
      if (!this.compareResults || this.compareResults.length === 0) return 0

      let beatenScore = 0
      this.compareResults.forEach(result => {
        if (result.sweep && result.loser === playerId) {
          beatenScore -= this.getSweepBonusForResult(result) * this.config.baseScore
        }
      })
      return beatenScore
    },

    // 获取玩家特殊牌型得分
    getSpecialHandScore(playerId) {
      if (!this.compareResults || this.compareResults.length === 0) return 0

      let specialScore = 0
      this.compareResults.forEach(result => {
        if (result.type === 'special' || result.type === 'odd') {
          if (result.winner === playerId) {
            specialScore += result.score
          } else if (result.loser === playerId) {
            specialScore -= result.score
          }
        }
      })
      return specialScore
    },

    /**
     * 汇总单个玩家本局展示用结果。
     *
     * 这里仅为 PlayerInfo 生成 UI 摘要：头/中/尾净分、打枪次数、被打次数、特殊牌型。
     * 最终结算分仍来自 store 或后端，避免 UI 展示逻辑反向影响计分。
     */
    getPlayerResultSummary(playerId) {
      if (!this.compareResults || this.compareResults.length === 0) return null
      if (this.gameStatus !== 'settling' && this.revealStage < 4) return null

      const player = this.players.find(p => p.id === playerId)
      let sweepsMade = 0
      let timesBeaten = 0
      let specialScore = 0
      let specialHand = player?.specialHand || null
      const homeRunPlayerIds = this.getHomeRunPlayerIds()

      this.compareResults.forEach(result => {
        if (result.sweep && result.winner === playerId) sweepsMade += 1
        if (result.sweep && result.loser === playerId) timesBeaten += 1

        if (result.type === 'special' || result.type === 'odd') {
          if (result.winner === playerId) {
            specialScore += result.score
            specialHand = specialHand || result.specialHand
          } else if (result.loser === playerId) {
            specialScore -= result.score
          }
        }
      })

      return {
        rows: {
          front: this.getRowScore(playerId, 'front'),
          middle: this.getRowScore(playerId, 'middle'),
          back: this.getRowScore(playerId, 'back')
        },
        rowClasses: {
          front: this.getRowScoreClass(playerId, 'front'),
          middle: this.getRowScoreClass(playerId, 'middle'),
          back: this.getRowScoreClass(playerId, 'back')
        },
        sweepsMade,
        timesBeaten,
        isHomeRun: homeRunPlayerIds.includes(playerId),
        isFolded: player?.isFolded === true,
        specialHand,
        specialScore
      }
    },

    // 获取分数样式类
    getRowScoreClass(playerId, row) {
      if (!this.compareResults || this.compareResults.length === 0) return ''

      let totalScore = 0
      this.compareResults.forEach(result => {
        if (!result[row] || !result[row].winner) return
        const isParticipant = result.player1 === playerId || result.player2 === playerId
        if (!isParticipant) return

        const rowScore = result[row].score * this.config.baseScore
        if (result[row].winner === playerId) {
          totalScore += rowScore
        } else {
          totalScore -= rowScore
        }
      })

      if (totalScore > 0) return 'positive'
      if (totalScore < 0) return 'negative'
      return 'zero'
    },

    returnSlotSelectedToHand() {
      const slotSelected = this.selectedCards.filter(c =>
        this.playerLayout.front.some(sc => sc.id === c.id) ||
        this.playerLayout.middle.some(sc => sc.id === c.id) ||
        this.playerLayout.back.some(sc => sc.id === c.id)
      )

      if (slotSelected.length > 0) {
        slotSelected.forEach(c => {
          ['front', 'middle', 'back'].forEach(pos => {
            const idx = this.playerLayout[pos].findIndex(sc => sc.id === c.id)
            if (idx > -1) {
              this.playerLayout[pos].splice(idx, 1)
            }
          })
        })
        this.selectedCards = []
        return true
      }
      return false
    },

    onCardClick(card) {
      if (this.gameStatus !== 'arranging') return

      // 如果有手动选中的三道牌，先取消选中，再处理手牌区牌的选中
      if (this.selectedManualSlotCard) {
        this.selectedManualSlotCard = null
      }

      if (this.returnSlotSelectedToHand()) return

      const index = this.selectedCards.findIndex(c => c.id === card.id)
      if (index > -1) {
        this.selectedCards.splice(index, 1)
      } else {
        this.selectedCards.push(card)
      }
    },

    onHandAreaClick() {
      if (this.gameStatus !== 'arranging') return

      // 如果有手动选中的三道牌，把它移回手牌区
      if (this.selectedManualSlotCard) {
        const { row, index } = this.selectedManualSlotCard
        this.playerLayout[row].splice(index, 1)
        this.selectedManualSlotCard = null
        this.selectedCards = []
        return
      }

      this.returnSlotSelectedToHand()
    },

    onSlotCardClick(card, position, index) {
      if (this.gameStatus !== 'arranging') return

      // 如果已有手动选中的三道牌
      if (this.selectedManualSlotCard) {
        const { row: selectedRow, index: selectedIndex } = this.selectedManualSlotCard
        const selectedCard = this.playerLayout[selectedRow][selectedIndex]

        // 点击同一张牌，取消选中
        if (selectedRow === position && selectedIndex === index) {
          this.selectedManualSlotCard = null
          return
        }

        // 点击同一道不同位置的牌，切换选中
        if (selectedRow === position && selectedIndex !== index) {
          this.selectedManualSlotCard = { row: position, index }
          this.selectedCards = []
          return
        }

        // 点击不同道的牌，执行交换
        const targetCard = this.playerLayout[position][index]
        const fromCards = [...this.playerLayout[selectedRow]]
        const toCards = [...this.playerLayout[position]]
        fromCards[selectedIndex] = targetCard
        toCards[index] = selectedCard

        this.playerLayout = {
          ...this.playerLayout,
          [selectedRow]: fromCards,
          [position]: toCards
        }
        this.sortPlayerLayoutRow(selectedRow)
        this.sortPlayerLayoutRow(position)
        this.selectedManualSlotCard = null
        this.selectedCards = []
        return
      }

      // 没有选中的三道牌，选中当前牌（同时清空手牌区选中）
      this.selectedCards = []
      this.selectedManualSlotCard = { row: position, index }
    },
    
    onSlotClick(position) {
      if (this.gameStatus !== 'arranging') return

      // 如果有手动选中的三道牌，先取消选中（避免误操作）
      if (this.selectedManualSlotCard) {
        this.selectedManualSlotCard = null
        return
      }

      if (this.selectedCards.length === 0) return

      const maxCards = position === 'front' ? 3 : 5
      const currentCards = this.playerLayout[position]
      const availableSlots = maxCards - currentCards.length

      if (availableSlots <= 0) return

      // 检查哪些牌可以添加（排除已在其他位置的牌）
      const allCards = [
        ...this.playerLayout.front,
        ...this.playerLayout.middle,
        ...this.playerLayout.back
      ]
      const cardsToAdd = this.selectedCards
        .filter(c => !allCards.some(ac => ac.id === c.id))
        .slice(0, availableSlots)

      if (cardsToAdd.length === 0) return

      this.playerLayout[position] = [...currentCards, ...cardsToAdd]
      this.sortPlayerLayoutRow(position)

      // 清空选中
      this.selectedCards = []
    },
    
    onDragStart(event, card) {
      event.dataTransfer.setData('card', JSON.stringify(card))
    },

    onDropToSlot(event, position) {
      if (this.gameStatus !== 'arranging') return
      try {
        const cardData = event.dataTransfer.getData('card')
        if (!cardData) return
        const card = JSON.parse(cardData)
        const maxCards = position === 'front' ? 3 : 5
        const currentCards = this.playerLayout[position]
        if (currentCards.length >= maxCards) return
        const allCards = [
          ...this.playerLayout.front,
          ...this.playerLayout.middle,
          ...this.playerLayout.back
        ]
        if (allCards.some(c => c.id === card.id)) return
        this.playerLayout[position] = [...currentCards, card]
        this.sortPlayerLayoutRow(position)
      } catch (e) {
        console.error('Drop error:', e)
      }
    },

    onCardDrop({ card, position }) {
      if (this.gameStatus !== 'arranging') return
      
      const maxCards = position === 'front' ? 3 : 5
      const currentCards = this.playerLayout[position]
      
      if (currentCards.length >= maxCards) return
      
      // 检查牌是否已经在其他位置
      const allCards = [
        ...this.playerLayout.front,
        ...this.playerLayout.middle,
        ...this.playerLayout.back
      ]
      
      if (allCards.some(c => c.id === card.id)) return

      this.playerLayout[position] = [...currentCards, card]
      this.sortPlayerLayoutRow(position)
    },

    removeCardFromSlot({ card, position }) {
      if (this.gameStatus !== 'arranging') return

      // 从指定位置移除牌
      this.playerLayout[position] = this.playerLayout[position].filter(c => c.id !== card.id)
    },

    // 清空某一墩
    clearRow(position) {
      if (this.gameStatus !== 'arranging') return
      this.playerLayout[position] = []
      this.selectedCards = []
      if (this.selectedManualSlotCard && this.selectedManualSlotCard.row === position) {
        this.selectedManualSlotCard = null
      }
    },

    // 从墩中移除单张牌
    removeOneCard(card, position) {
      if (this.gameStatus !== 'arranging') return
      const idx = this.playerLayout[position].findIndex(c => c.id === card.id)
      if (idx > -1) {
        this.playerLayout[position].splice(idx, 1)
      }
      // 如果移除的是当前选中的三道牌，清空选中
      if (this.selectedManualSlotCard &&
          this.selectedManualSlotCard.row === position &&
          idx === this.selectedManualSlotCard.index) {
        this.selectedManualSlotCard = null
      }
    },

    // 一键应用最佳方案：使用 hard 级别的全枚举最优算法
    applyBestSuggestion() {
      const playerId = this.isOnlineMode ? this.$store.state.playerId : this.humanPlayer?.id
      const player = this.players.find(p => p.id === playerId)
      if (!player || player.hands.length === 0) return

      const bestLayout = aiArrange(player.hands)
      if (bestLayout) {
        this.playerLayout = {
          front: [...bestLayout.front],
          middle: [...bestLayout.middle],
          back: [...bestLayout.back]
        }
        this.sortAllPlayerLayoutRows()
        this.selectedCards = []
        this.selectedSuggestion = -1
        this.selectedSpecial = false
      }
    },

    // 生成从 n 个元素中选 k 个的所有组合索引
    generateCombinations(n, k) {
      const result = []
      const combo = []
      function backtrack(start) {
        if (combo.length === k) {
          result.push([...combo])
          return
        }
        for (let i = start; i < n; i++) {
          combo.push(i)
          backtrack(i + 1)
          combo.pop()
        }
      }
      backtrack(0)
      return result
    },

    // 点击牌型按钮，选中所选牌型对应的牌
    selectCardsByHandType(type) {
      const cards = this.playerHands
      if (cards.length === 0) return
      const selected = this.findCardsForHandType(cards, type)
      if (selected && selected.length > 0) {
        this.selectedCards = selected
      }
    },

    // 在手牌中找到组成指定牌型的牌
    findCardsForHandType(cards, type) {
      const { evaluateFiveCards, evaluateThreeCards } = require('@/utils/handEvaluator')
      const rankToKey = {
        10: 'straight_flush',
        9: 'four_kind',
        8: 'full_house',
        7: 'flush',
        6: 'straight',
        5: 'three_kind',
        4: 'two_pair',
        3: 'pair'
      }

      // 3张牌型（对子、三条）
      if (['pair', 'three_kind'].includes(type)) {
        for (let i = 0; i < cards.length - 2; i++) {
          for (let j = i + 1; j < cards.length - 1; j++) {
            for (let k = j + 1; k < cards.length; k++) {
              const combo = [cards[i], cards[j], cards[k]]
              try {
                const eval3 = evaluateThreeCards(combo)
                if (rankToKey[eval3.rank.value] === type) {
                  return combo
                }
              } catch (e) {}
            }
          }
        }
      }

      // 5张牌型
      if (cards.length >= 5) {
        const combos5 = this.generateCombinations(cards.length, 5)
        let bestCombo = null
        let bestScore = -1
        for (const combo of combos5) {
          const hand = combo.map(idx => cards[idx])
          try {
            const eval5 = evaluateFiveCards(hand)
            if (rankToKey[eval5.rank.value] === type) {
              const score = eval5.rank.value
              if (score > bestScore) {
                bestScore = score
                bestCombo = hand
              }
            }
          } catch (e) {}
        }
        return bestCombo
      }

      return null
    },

    // 当前摆法本身是否构成三顺子 / 三同花（不把「整手还能另摆成特殊牌」算进来）
    layoutEnablesSpecial(layout) {
      if (!this.config.enableSpecialHands || !layout) return false
      if (layout.front?.length !== 3 || layout.middle?.length !== 5 || layout.back?.length !== 5) return false
      return checkThreeStraight(layout) || checkThreeFlush(layout)
    },

    // 生成智能摆牌方案。能凑成特殊牌型的摆法排到最前，默认就选中它，报道区才能显示牌型名。
    generateAISuggestions() {
      const playerId = this.isOnlineMode ? this.$store.state.playerId : this.humanPlayer?.id
      const player = this.players.find(p => p.id === playerId)
      if (!player || player.hands.length === 0) return

      const { getTopArrangements } = require('@/utils/ai')
      const topArrangements = getTopArrangements(player.hands, 30)
      const mapped = topArrangements.map(item => ({
        layout: item.layout,
        score: item.score != null ? item.score : this.calculateLayoutScore(item.layout),
        specialHand: null
      }))

      const specials = []
      const normals = []
      mapped.forEach(item => {
        if (this.layoutEnablesSpecial(item.layout)) specials.push(item)
        else normals.push(item)
      })

      this.aiSuggestions = [...specials, ...normals].slice(0, 3).map((item, index) => ({
        ...item,
        difficulty: index === 0 ? 'hard' : 'medium'
      }))
      this.selectedSuggestion = -1
      this.selectedSpecial = false
    },

    // 计算摆牌方案评分
    calculateLayoutScore(layout) {
      const { evaluateThreeCards, evaluateFiveCards, isValidLayout } = require('@/utils/handEvaluator')

      const valid = isValidLayout(layout.front, layout.middle, layout.back)
      if (!valid.valid) return 0

      const front = evaluateThreeCards(layout.front)
      const middle = evaluateFiveCards(layout.middle)
      const back = evaluateFiveCards(layout.back)

      // 基础分：各道牌型等级 (使用 rank.value)
      let score = front.rank.value * 10 + middle.rank.value * 20 + back.rank.value * 30

      // 平衡加分：三道都比较强
      const minRank = Math.min(front.rank.value, middle.rank.value, back.rank.value)
      score += minRank * 5

      return Math.round(score)
    },

    // 获取方案的牌型名称
    getSuggestionHandType(cards, row) {
      if (!cards || cards.length === 0) return '-'

      const { evaluateThreeCards, evaluateFiveCards } = require('@/utils/handEvaluator')
      const result = row === 'front' ? evaluateThreeCards(cards) : evaluateFiveCards(cards)

      // result.rank 是 HAND_RANKS 中的对象，包含 name 属性
      if (result.rank && result.rank.name) {
        return result.rank.name
      }

      return '散牌'
    },

    // 应用选中的方案
    applySuggestion(index) {
      if (this.layoutLocked) return
      if (index < 0 || index >= this.aiSuggestions.length) return

      this.selectedSuggestion = index
      this.selectedSpecial = false
      this.selectedSmartCard = null
      this.playerLayout = {
        front: [...this.aiSuggestions[index].layout.front],
        middle: [...this.aiSuggestions[index].layout.middle],
        back: [...this.aiSuggestions[index].layout.back]
      }
      this.sortAllPlayerLayoutRows()
    },

    // 应用末尾特殊牌型摆法
    applySpecialSuggestion() {
      if (this.layoutLocked || !this.availableSpecialHand) return
      const player = this.currentArrangePlayer
      if (!player || !Array.isArray(player.hands) || player.hands.length !== 13) return

      const layout = resolveSpecialHandLayout(player.hands, this.availableSpecialHand)
      if (!layout?.front?.length) return

      this.selectedSuggestion = -1
      this.selectedSpecial = true
      this.selectedSmartCard = null
      this.playerLayout = {
        front: [...layout.front],
        middle: [...layout.middle],
        back: [...layout.back]
      }
      this.sortAllPlayerLayoutRows()
    },

    // 智能摆牌：点击选中牌，点击另一道的牌进行交换
    selectSmartCard(row, index) {
      if (this.layoutLocked) return
      // 如果点击的是已选中的牌，取消选中
      if (this.selectedSmartCard &&
          this.selectedSmartCard.row === row &&
          this.selectedSmartCard.index === index) {
        this.selectedSmartCard = null
        return
      }

      // 如果没有选中牌，或者点击同一道上的另一张牌，则选中当前牌
      if (!this.selectedSmartCard || this.selectedSmartCard.row === row) {
        this.selectedSmartCard = { row, index }
        return
      }

      // 点击了不同道上的牌，执行交换
      const fromRow = this.selectedSmartCard.row
      const fromIndex = this.selectedSmartCard.index
      const toRow = row
      const toIndex = index

      // 交换牌
      const fromCards = [...this.playerLayout[fromRow]]
      const toCards = [...this.playerLayout[toRow]]
      const temp = fromCards[fromIndex]
      fromCards[fromIndex] = toCards[toIndex]
      toCards[toIndex] = temp

      this.playerLayout = {
        ...this.playerLayout,
        [fromRow]: fromCards,
        [toRow]: toCards
      }
      this.sortPlayerLayoutRow(fromRow)
      this.sortPlayerLayoutRow(toRow)

      // 交换后不再是AI建议的方案，清除选中方案
      this.selectedSuggestion = -1
      this.selectedSpecial = false
      this.selectedSmartCard = null
    },

    clearLayout() {
      if (this.layoutLocked) return
      // 将所有摆好的牌移回手牌
      this.playerLayout = {
        front: [],
        middle: [],
        back: []
      }
      this.selectedCards = []
      this.selectedManualSlotCard = null
    },

    switchToManual() {
      if (this.layoutLocked) return
      this.arrangeMode = 'manual'
    },

    switchToSmart() {
      if (this.layoutLocked) return
      this.arrangeMode = 'smart'
      // 重新应用最优方案
      if (this.aiSuggestions.length > 0) {
        this.applySuggestion(0)
      }
    },

    confirmLayout() {
      if (this.layoutLocked) return
      if (!this.canConfirmLayout) return

      const myId = this.$store.state.playerId
      const reportSpecial = this.reported
      this.layoutLocked = true
      this.selectedSmartCard = null
      this.selectedManualSlotCard = null

      // 已报道：有特殊牌型则按特殊计分；layout 不完整时用特殊牌型摆法
      let layout = {
        front: [...this.playerLayout.front],
        middle: [...this.playerLayout.middle],
        back: [...this.playerLayout.back]
      }
      if (reportSpecial && this.availableSpecialHand && !this.isLayoutComplete) {
        const player = this.currentArrangePlayer
        layout = resolveSpecialHandLayout(player.hands, this.availableSpecialHand)
      }

      if (this.isOnlineMode) {
        this.$store.dispatch('multiplayer/sendConfirmLayout', { layout, reportSpecial })
        if (!reportSpecial) {
          this.$store.commit('SET_PLAYER_SPECIAL_HAND', { playerId: myId, specialHand: null })
        }
        this.$store.commit('SET_PLAYER_LAYOUT', { playerId: myId, layout })
        this.$store.commit('SET_PLAYER_CONFIRMED', { playerId: myId, confirmed: true })
        this.showArrangePanel = false
        return
      }

      const human = this.humanPlayer
      this.$store.dispatch('confirmLayout', {
        playerId: human.id,
        layout,
        reportSpecial
      })

      this.showArrangePanel = false
    },

    /**
     * 切换报道状态：报道 ↔ 已报道。
     * 点击报道后自动将三道牌拍为特殊牌型摆法；出牌时按特殊牌型计分。
     */
    toggleReport() {
      if (this.layoutLocked) return
      if (!this.availableSpecialHand) return
      this.reported = !this.reported
      if (this.reported) {
        this.applySpecialSuggestion()
      }
    },
    
    async startAIArrange() {
      // AI摆牌 - 使用Promise确保所有AI都完成摆牌
      const aiCount = this.aiPlayers.length
      
      for (let i = 0; i < aiCount; i++) {
        const ai = this.aiPlayers[i]
        // AI 有特殊牌型时主动报道
        if (ai.specialHand) {
          const thinkTime = getAIThinkTime()
          await new Promise(resolve => setTimeout(resolve, thinkTime))
          const layout = resolveSpecialHandLayout(ai.hands, ai.specialHand)
          await this.$store.dispatch('confirmLayout', {
            playerId: ai.id,
            layout,
            reportSpecial: true
          })
          continue
        }

        const thinkTime = getAIThinkTime()
        
        // 等待每个AI的思考时间
        await new Promise(resolve => setTimeout(resolve, thinkTime))
        
        const layout = aiArrange(ai.hands)
        await this.$store.dispatch('confirmLayout', {
          playerId: ai.id,
          layout,
          reportSpecial: false
        })
      }
    },
    
    /**
     * 下一局入口。先播放 3 秒倒计时，结束后再真正开局。
     */
    nextRound() {
      if (this.showReturnToLobby || this.nextRoundCountdown > 0) return
      this.roundResultDialog = false
      this.clearNextRoundTimer()
      this.nextRoundCountdown = 3
      playCompareCountdownSound(3)
      this.nextRoundTimer = setInterval(() => {
        this.nextRoundCountdown--
        if (this.nextRoundCountdown > 0) {
          playCompareCountdownSound(this.nextRoundCountdown)
          return
        }
        this.clearNextRoundTimer()
        this.beginNextRound()
      }, 1000)
    },

    clearNextRoundTimer() {
      if (this.nextRoundTimer) {
        clearInterval(this.nextRoundTimer)
        this.nextRoundTimer = null
      }
      this.nextRoundCountdown = 0
    },

    /**
     * 倒计时结束后进入下一局。
     *
     * 清理当前界面的临时交互状态和动画状态，再通知 store/服务端进入下一局。
     */
    beginNextRound() {
      if (this.showReturnToLobby) return
      this.compareAnimationRunId++
      this.playerLayout = { front: [], middle: [], back: [] }
      this.selectedCards = []
      this.layoutLocked = false
      this.reported = false
      this.revealStage = 0
      this.resetRevealedRows()
      this.compareCountdown = 0
      this.sweepAnimation = null
      this.specialHandAnimation = null
      this.moneyPops = []
      this.pendingOnlineScores = null
      if (this.isOnlineMode) {
        this.$store.dispatch('multiplayer/sendNextRound')
        return
      }
      this.$store.dispatch('nextRound')
    },

    /**
     * 房间局数或时长已结束，直接离开房间并回到大厅。
     */
    async returnToLobby() {
      this.compareAnimationRunId++
      this.clearNextRoundTimer()
      this.stopCountdown()
      if (this.isOnlineMode) {
        this.stopRoomPolling()
        try {
          await this.$store.dispatch('multiplayer/leaveRoom', { silent: true })
        } catch (e) {
          this.$store.commit('multiplayer/CLEAR_ROOM')
        }
      }
      this.$router.push('/')
    },

    // 在线模式相关方法
    buildOnlineRoomConfig(source = {}, fallback = this.config) {
      const pick = (key, defaultValue) => {
        if (source[key] !== undefined && source[key] !== null) return source[key]
        if (fallback && fallback[key] !== undefined && fallback[key] !== null) return fallback[key]
        return defaultValue
      }

      return {
        playerCount: pick('playerCount', source.maxPlayers || this.players.length || 2),
        baseScore: pick('baseScore', 1),
        arrangeTime: pick('arrangeTime', 60),
        enableRandomSeat: pick('enableRandomSeat', true),
        enableIpLimit: pick('enableIpLimit', true),
        enableSpecialHands: pick('enableSpecialHands', true),
        enableHomeRun: pick('enableHomeRun', true),
        allowOddCards: pick('allowOddCards', false),
        allowFouled: pick('allowFouled', false),
        enableHorse: pick('enableHorse', false),
        horseSuit: pick('horseSuit', 'hearts'),
        horseRank: pick('horseRank', 1),
        enableYucai: pick('enableYucai', false),
        yucaiScore: pick('yucaiScore', 0),
        allowRoomRate: pick('allowRoomRate', false),
        roomRatePercent: pick('roomRatePercent', 2),
        dragonScore: pick('dragonScore', 15),
        allBigScore: pick('allBigScore', 10),
        allSmallScore: pick('allSmallScore', 10),
        sixPairsHalfScore: pick('sixPairsHalfScore', 7),
        threeFlushScore: pick('threeFlushScore', 7),
        threeStraightScore: pick('threeStraightScore', 7),
        rowSpecialScores: pick('rowSpecialScores', fallback?.rowSpecialScores || {
          frontThreeKind: 3,
          middleFullHouse: 2,
          middleFourKind: 8,
          middleStraightFlush: 10,
          middleFiveKind: 20,
          backFourKind: 4,
          backStraightFlush: 5,
          backFiveKind: 10
        }),
        sweepScoreType: pick('sweepScoreType', 'score'),
        sweepScoreValue: pick('sweepScoreValue', 2),
        dealMode: pick('dealMode', 'single'),
        ghostMode: pick('ghostMode', 0),
        roundCount: pick('roundCount', 20),
        limitType: pick('limitType', source.durationMinutes ? 'time' : 'round'),
        durationMinutes: pick('durationMinutes', 0),
        expireTime: pick('expireTime', 0),
        extraSuits: pick('extraSuits', [])
      }
    },

    prepareOnlineRound(payload = {}) {
      this.compareAnimationRunId++
      this.onlineScores = {}
      this.pendingOnlineScores = null
      this.playerLayout = { front: [], middle: [], back: [] }
      this.selectedCards = []
      this.selectedSmartCard = null
      this.selectedManualSlotCard = null
      this.layoutLocked = false
      this.aiSuggestions = []
      this.selectedSuggestion = -1
      this.selectedSpecial = false
      this.showArrangePanel = false
      this.arrangeMode = 'smart'
      this.revealStage = 0
      this.resetRevealedRows()
      this.compareCountdown = 0
      this.sweepAnimation = null
      this.homeRunAnimation = null
      this.wulongAnimation = null
      this.specialHandAnimation = null
      this.floatingScores = []
      this.moneyPops = []
      this.roomSessionFinished = false
      this.onlineDealtCount = 0
      this.onlineDealtCounts = {}
      this.players.forEach(player => {
        this.$set(this.onlineDealtCounts, player.id, 0)
      })
      this.stopCountdown()
      this.$store.commit('SET_COMPARE_RESULTS', [])
      if (payload.round) {
        this.$store.commit('SET_ROUND', payload.round)
      }
      if (payload.roundCount) {
        this.$store.commit('SET_CONFIG', { roundCount: payload.roundCount })
      }
      this.$store.commit('SET_CONFIG', this.buildOnlineRoomConfig(payload))
      this.players.forEach(player => {
        this.$store.commit('SET_PLAYER_HANDS', { playerId: player.id, hands: [] })
        this.$store.commit('SET_PLAYER_LAYOUT', {
          playerId: player.id,
          layout: { front: [], middle: [], back: [] }
        })
        this.$store.commit('SET_PLAYER_CONFIRMED', { playerId: player.id, confirmed: false })
        this.$store.commit('SET_PLAYER_FOLDED', { playerId: player.id, isFolded: false })
        this.$store.commit('SET_PLAYER_SPECIAL_HAND', { playerId: player.id, specialHand: null })
      })
      this.$store.commit('SET_COUNTDOWN', 0)
      this.$store.commit('SET_GAME_STATUS', 'dealing')
    },

    enterOnlineArrangeStage(payload = {}) {
      const player = this.getCurrentOnlinePlayer()
      if (!player || !player.hands || player.hands.length !== 13) return

      const specialHand = payload.specialHand || player.specialHand
      if (specialHand) {
        this.$store.commit('SET_PLAYER_SPECIAL_HAND', { playerId: player.id, specialHand })
      }

      // 特殊牌型也进入摆牌：默认普通比牌，点击报道后按特殊计分
      this.revealStage = 0
      this.resetRevealedRows()
      this.showArrangePanel = true
      this.arrangeMode = 'smart'
      this.layoutLocked = false
      this.reported = false
      this.aiSuggestions = []
      this.selectedSuggestion = -1
      this.selectedSpecial = false
      this.playerLayout = { front: [], middle: [], back: [] }
      this.selectedCards = []
      this.startCountdown()
      this.$nextTick(() => {
        this.generateAISuggestions()
        if (this.aiSuggestions.length > 0) {
          this.applySuggestion(0)
        }
      })
    },

    setupOnlineMode() {
      // 将房间玩家同步到 store
      this.syncRoomPlayersToStore()
      // 连接 WS（如果还没连接）
      if (!this.$store.state.multiplayer.wsConnected) {
        this.$store.dispatch('multiplayer/connectWS')
      } else {
        this.requestChatHistory()
      }
      // 设置 WS 监听器
      this.setupWSListeners()
      // 设置房间轮询
      this.startRoomPolling()

      const myId = this.$store.state.playerId

      // 先检查是否在 Room.vue 缓存了 DEAL 消息（页面切换时 WS 消息可能丢失）
      const dealMsg = this.$store.state.multiplayer.dealPayload
      if (dealMsg && dealMsg.playerId === myId) {
        this.applyDealPayload(dealMsg)
        this.$store.commit('multiplayer/SET_DEAL_PAYLOAD', null)
        return
      }

      // 如果已经有手牌数据，直接进入摆牌状态（重连兜底）
      const me = this.players.find(p => p.id === myId)
      if (me && me.hands && me.hands.length === 13 && this.gameStatus === 'idle') {
        this.arrangeEndTime = this.room.arrangeEndTime || (Date.now() + (this.room.countdown || 60) * 1000)
        this.$store.commit('SET_GAME_STATUS', 'arranging')
        this.$store.commit('SET_COUNTDOWN', this.room.countdown || 60)
        if (this.room.round) {
          this.$store.commit('SET_ROUND', this.room.round)
        }
        if (me.specialHand) {
          this.enterOnlineArrangeStage({ specialHand: me.specialHand })
          return
        }
        this.enterOnlineArrangeStage()
      }
    },

    applyDealPayload(msg) {
      const payload = msg.payload
      if (!payload) return
      const playerId = msg.playerId
      const hands = payload.hands
      this.arrangeEndTime = payload.arrangeEndTime || (Date.now() + (payload.countdown || 60) * 1000)
      this.$store.commit('SET_CONFIG', this.buildOnlineRoomConfig(payload))
      this.startOnlineDealingAnimation(playerId, hands, payload)
    },

    syncRoomPlayersToStore() {
      if (!this.room || !this.room.players) return
      const existingPlayers = this.players || []
      const sortedPlayers = [...this.room.players].sort((a, b) => {
        const seatA = Number.isFinite(a.seat) ? a.seat : 999
        const seatB = Number.isFinite(b.seat) ? b.seat : 999
        return seatA - seatB
      })
      const mapped = sortedPlayers.map(p => {
        const existing = existingPlayers.find(ep => ep.id === p.id)
        const isMe = p.id === this.$store.state.playerId
        // 发牌动画期间不要让轮询返回的手牌覆盖本地逐张发牌状态。
        // 摆牌期间也保留本地完整手牌，避免 3 秒轮询换成新数组后把推荐摆型重置回第一套。
        const keepLocalHands = this.onlineDealInProgress
          || this.gameStatus === 'dealing'
          || (this.gameStatus === 'arranging' && existing && existing.hands && existing.hands.length === 13)
        const keepLocalLayout = this.gameStatus === 'arranging' && isMe && existing && !p.isLayoutConfirmed
        // 后端会在 COMPARE_START 后立即结算；前端比牌动画结束前先保留旧分数。
        const keepLocalScores = this.isOnlineMode && this.gameStatus === 'comparing'
        const hands = keepLocalHands
          ? (existing ? existing.hands : [])
          : ((p.hands && p.hands.length > 0) ? p.hands : (existing ? existing.hands : []))
        return {
          id: p.id,
          name: p.name,
          avatar: p.avatar,
          isAI: p.isAI,
          isReady: p.isReady,
          hands: hands,
          layout: keepLocalLayout
            ? (existing.layout || { front: [], middle: [], back: [] })
            : (p.layout || { front: [], middle: [], back: [] }),
          isFolded: p.isFolded,
          specialHand: p.specialHand,
          score: keepLocalScores && existing ? (existing.score || 0) : (p.score || 0),
          totalScore: keepLocalScores && existing ? (existing.totalScore || 0) : (p.totalScore || 0),
          roomRateScore: keepLocalScores && existing ? (existing.roomRateScore || 0) : (p.roomRateScore || 0),
          totalRoomRateScore: keepLocalScores && existing ? (existing.totalRoomRateScore || 0) : (p.totalRoomRateScore || 0),
          isLayoutConfirmed: p.isLayoutConfirmed || false,
          seat: Number.isFinite(p.seat) ? p.seat : undefined,
          online: p.isOnline
        }
      })
      this.$store.commit('SET_PLAYERS', mapped)
      const me = mapped.find(p => p.id === this.$store.state.playerId)
      if (me) {
        this.$store.commit('SET_CURRENT_PLAYER', me.id)
      } else if (mapped.length > 0) {
        this.$store.commit('SET_CURRENT_PLAYER', mapped[0].id)
      }
      this.$store.commit('SET_CONFIG', this.buildOnlineRoomConfig({
        ...this.room,
        playerCount: this.room.maxPlayers
      }))
    },

    startRoomPolling() {
      const roomApi = require('@/api/room').default
      this.pollInterval = setInterval(async () => {
        if (!this.room) return
        try {
          const res = await roomApi.getRoom(this.room.id, this.$store.state.playerId)
          if (res.success) {
            this.$store.commit('multiplayer/UPDATE_ROOM', res.data)
            this.syncRoomPlayersToStore()
          }
        } catch (e) {
          // ignore poll errors
        }
      }, 3000)
    },

    stopRoomPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
    },

    startCountdown() {
      this.stopCountdown()
      if (this.isOnlineMode && this.arrangeEndTime) {
        const tick = () => {
          const remaining = Math.max(0, Math.ceil((this.arrangeEndTime - Date.now()) / 1000))
          this.$store.commit('SET_COUNTDOWN', remaining)
          if (remaining <= 0) {
            this.stopCountdown()
          }
        }
        tick()
        this.countdownTimer = setInterval(tick, 1000)
        return
      }

      // 离线模式由本地 store 维护倒计时。
      if (!this.isOnlineMode) {
        this.$store.commit('CLEAR_COUNTDOWN')
      }
      this.countdownTimer = setInterval(() => {
        if (this.$store.state.countdown > 0) {
          this.$store.commit('DECREMENT_COUNTDOWN')
        } else {
          this.stopCountdown()
        }
      }, 1000)
    },

    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },

    setupWSListeners() {
      const ws = require('@/api/ws').default
      this.wsUnsubscribers = [
        ws.on('DEAL', (msg) => this.onDeal(msg)),
        ws.on('GAME_START', (msg) => this.onGameStart(msg)),
        ws.on('COMPARE_START', (msg) => this.onCompareStart(msg)),
        ws.on('SETTLE', (msg) => this.onSettle(msg)),
        ws.on('AUTO_ARRANGED', (msg) => this.onAutoArranged(msg)),
        ws.on('PLAYER_DISCONNECTED', (msg) => this.onPlayerDisconnected(msg)),
        ws.on('PLAYER_RECONNECTED', (msg) => this.onPlayerReconnected(msg)),
        ws.on('PLAYER_LEFT', (msg) => this.onPlayerLeft(msg)),
        ws.on('CONFIRM_LAYOUT', (msg) => this.onConfirmLayout(msg)),
        ws.on('ERROR', (msg) => this.onWSError(msg)),
        ws.on('ROOM_FINISHED', () => {
          this.roomSessionFinished = true
        })
      ]
    },

    onDeal(msg) {
      const payload = msg.payload
      if (!payload) return
      const playerId = msg.playerId
      const hands = payload.hands
      this.arrangeEndTime = payload.arrangeEndTime || (Date.now() + (payload.countdown || 60) * 1000)
      this.$store.commit('SET_CONFIG', this.buildOnlineRoomConfig(payload))
      this.startOnlineDealingAnimation(playerId, hands, payload)
    },

    onGameStart(msg = {}) {
      this.onlineDealInProgress = true
      this.prepareOnlineRound(msg.payload || {})
    },

    onCompareStart(msg) {
      const payload = msg.payload || {}
      const results = payload.results
      const playerLayouts = payload.players || []
      if (results) {
        this.$store.commit('SET_COMPARE_RESULTS', results)
      }
      // 同步其他玩家的 layout
      playerLayouts.forEach(pd => {
        if (!pd.id) return
        if (pd.layout) {
          this.$store.commit('SET_PLAYER_LAYOUT', { playerId: pd.id, layout: pd.layout })
        }
        if (pd.specialHand) {
          this.$store.commit('SET_PLAYER_SPECIAL_HAND', { playerId: pd.id, specialHand: pd.specialHand })
        }
        if (Array.isArray(pd.hands) && pd.hands.length > 0) {
          this.$store.commit('SET_PLAYER_HANDS', { playerId: pd.id, hands: pd.hands })
        }
        this.$store.commit('SET_PLAYER_CONFIRMED', { playerId: pd.id, confirmed: true })
        this.$store.commit('SET_PLAYER_FOLDED', { playerId: pd.id, isFolded: pd.folded === true })
      })
      this.$store.commit('SET_GAME_STATUS', 'comparing')
      this.startCompareAnimation()
    },

    onSettle(msg) {
      const scores = msg.payload
      this.onlineScores = scores || {}
      this.pendingOnlineScores = scores || null
      if (scores && scores.roomFinished) {
        this.roomSessionFinished = true
      }
      if (this.gameStatus === 'settling') {
        this.applyPendingOnlineScores()
      }
    },

    onAutoArranged(msg) {
      const payload = msg.payload || {}
      const playerId = payload.playerId
      const player = this.players.find(p => p.id === playerId)
      if (player) {
        player.isLayoutConfirmed = true
      }
    },

    onPlayerDisconnected(msg) {
      const payload = msg.payload || {}
      const player = this.players.find(p => p.id === payload.playerId)
      if (player) {
        player.online = false
      }
    },

    onPlayerReconnected(msg) {
      const player = this.players.find(p => p.id === msg.playerId)
      if (player) {
        player.online = true
      }
    },

    onPlayerLeft() {
      this.refreshOnlineRoom()
    },

    async refreshOnlineRoom() {
      if (!this.room) return
      const roomApi = require('@/api/room').default
      try {
        const res = await roomApi.getRoom(this.room.id, this.$store.state.playerId)
        if (res.success) {
          this.$store.commit('multiplayer/UPDATE_ROOM', res.data)
          this.syncRoomPlayersToStore()
        }
      } catch (e) {
        // ignore refresh errors
      }
    },

    onConfirmLayout(msg) {
      const payload = msg.payload || {}
      const player = this.players.find(p => p.id === payload.playerId)
      if (player) {
        player.isLayoutConfirmed = true
        if (payload.specialHand) {
          this.$store.commit('SET_PLAYER_SPECIAL_HAND', {
            playerId: payload.playerId,
            specialHand: payload.specialHand
          })
        } else {
          this.$store.commit('SET_PLAYER_SPECIAL_HAND', {
            playerId: payload.playerId,
            specialHand: null
          })
        }
        if (payload.playerId === this.$store.state.playerId) {
          this.showArrangePanel = false
          this.layoutLocked = true
        }
      }
    },

    onWSError(msg) {
      const payload = msg.payload
      if (typeof payload === 'string') {
        this.$message.error('错误: ' + payload)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.game-page {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.table-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #0d5a3a 0%, #1a7a4a 50%, #0d5a3a 100%);
  z-index: 0;
  
  background: url('@/assets/images/bg1_1.jpg') no-repeat center center;
  background-size: cover;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 80%, rgba(0,0,0,0.3) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(0,0,0,0.3) 0%, transparent 50%);
  }
}

// 顶部栏
.top-bar {
  position: relative;
  z-index: 45;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
}

.top-left {
  display: flex;
  align-items: center;
  gap: 10px;

  .back-btn-room {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    width: 32px;
    height: 32px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.9);
    font-size: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    line-height: 1;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 215, 0, 0.4);
      color: #ffd700;
    }
  }
}

.back-btn {
  padding: 4px 8px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  line-height: 1;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.round {
  color: white;
  font-weight: bold;
  font-size: 14px;
}

.room-code {
  margin-left: 10px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  font-weight: 600;
}

.game-info {
  display: flex;
  align-items: center;
  gap: 16px;
  color: white;

  .countdown {
    color: #ffd700;
    font-weight: bold;
  }
}

.score-info {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #ffe885;
  font-size: 13px;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.55);
}

.room-config-btn {
  padding: 6px 14px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 215, 0, 0.4);
    color: #ffd700;
  }
}

.info-modal {
  position: fixed;
  inset: 0;
  z-index: 1600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px;
  background: rgba(0, 0, 0, 0.66);
}

.info-modal-content {
  width: min(720px, 94vw);
  max-height: min(82vh, 760px);
  overflow-y: auto;
  border: 2px solid rgba(248, 239, 148, 0.88);
  border-radius: 10px;
  background: rgba(36, 51, 34, 0.96);
  color: #fff4b8;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45), inset 0 0 0 2px rgba(92, 62, 40, 0.8);
  padding: 20px;
}

.info-modal-header {
  position: sticky;
  top: -20px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: -20px -20px 14px;
  padding: 20px 20px 12px;
  background: rgba(36, 51, 34, 0.98);
  border-bottom: 1px solid rgba(255, 232, 133, 0.14);

  h3 {
    margin: 0;
    color: #ffe885;
    font-size: 22px;
    letter-spacing: 0;
  }
}

.info-close-btn {
  width: 34px;
  height: 34px;
  border: 1px solid rgba(255, 232, 133, 0.6);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.28);
  color: #ffe885;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 14px;
}

.config-item {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  min-height: 34px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 232, 133, 0.14);
}

.config-label,
.score-line-detail,
.score-rule-note,
.score-empty {
  color: rgba(255, 244, 184, 0.72);
}

.config-value {
  color: #ffffff;
  font-weight: 700;
  text-align: right;
}

.score-total-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 10px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.28);
  font-size: 16px;

  strong {
    font-size: 22px;
  }
}

.score-rule-note {
  margin-bottom: 14px;
  font-size: 12px;
  line-height: 1.6;
}

.score-section {
  padding: 12px 0;
  border-top: 1px solid rgba(255, 232, 133, 0.18);
}

.score-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;

  h4 {
    margin: 0;
    color: #ffe885;
    font-size: 15px;
  }

  strong {
    min-width: 48px;
    text-align: right;
    font-size: 15px;
  }
}

.score-detail-sum {
  margin-top: 8px;
  border-top: 1px solid rgba(255, 232, 133, 0.18);
}

.score-detail-mismatch {
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(255, 114, 114, 0.12);
  color: #ff9f9f;
  font-size: 12px;
  line-height: 1.5;
}

.score-line {
  padding: 7px 0;
}

.score-line-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;

  strong {
    min-width: 48px;
    text-align: right;
  }
}

.score-line-detail {
  margin-top: 3px;
  font-size: 12px;
  line-height: 1.45;
}

.positive {
  color: #45e06f;
}

.negative {
  color: #ff7272;
}

.zero {
  color: rgba(255, 244, 184, 0.72);
}

.room-rate-summary {
  margin-top: 12px;
  padding: 9px 10px;
  border-radius: 6px;
  background: rgba(255, 232, 133, 0.1);
  color: #ffe885;
  font-weight: 700;
}

// 玩家位置分布 - 使用椭圆布局，位置由JS动态计算
.player-position {
  position: absolute;
  z-index: 35;
  pointer-events: auto;
  // background: rgba(0, 0, 0, 0.5);
  //   border: 1px solid rgba(255, 255, 0, 0.6);
  //   border-radius: 5px;
}

// 牌的独立位置 - 使用比玩家位置小一圈的椭圆
.cards-position {
  position: absolute;
  z-index: 5;
}

.game-page.is-mobile {
  .dealing-hands {
    .dealing-card-row {
      &.row-2 {
        margin-top: -76px;
      }

      .dealt-card {
        margin-left: -50px;

        ::v-deep .playing-card.dealing-card-override {
          width: 76px !important;
          height: 106px !important;
          border-radius: 10px !important;

          .card-corner {
            font-size: 18px !important;

            .rank {
              font-size: 22px !important;
            }

            .suit {
              font-size: 10px !important;
            }

            &.top-left {
              top: 6px !important;
              left: 6px !important;
            }

            &.bottom-right {
              bottom: 6px !important;
              right: 6px !important;
            }
          }

          .card-center .suit-large {
            font-size: 38px !important;
          }
        }

        .card-back-small-dealing {
          width: 76px;
          height: 106px;
          border-radius: 10px;

          &::after {
            width: 56px;
            height: 70px;
          }
        }
      }
    }
  }

  .ai-cards-fan {
    width: 240px;
    height: 190px;

    .fan-row {
      height: 106px;

      &.middle-row {
        top: 42px;
      }

      &.back-row {
        top: 85px;
      }
    }
  }

  .ai-cards-back {
    .three-stacks-fan {
      width: 240px;
      height: 190px;
      padding: 3px;

      .stack-row-fan {
        height: 106px;

        &.middle-stack {
          top: 42px;
        }

        &.back-stack {
          top: 85px;
        }

        .card-back-small {
          width: 76px;
          height: 106px;
          border-radius: 10px;

          &::after {
            width: 56px;
            height: 70px;
          }
        }
      }
    }
  }
}

// AI状态
.ai-status {
  padding: 4px 12px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 12px;
  font-size: 12px;
  
  .confirmed {
    color: #27ae60;
  }
  
  .thinking {
    color: #ffd700;
    animation: blink 1s infinite;
  }
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

// AI牌展示 - 扇形三摞（头道→中道→尾道，尾道遮挡中道，中道遮挡头道）
.ai-cards-fan,
.ai-cards-back {
  animation: layoutAppear 0.4s ease-out;
}

.ai-cards-fan {
  position: relative;
  width: 270px;
  height: 165px;

  .fan-row-wrapper {
    position: absolute;
    width: 100%;
    height: 75px;
    left: 0;
  }

  .fan-row {
    position: absolute;
    width: 100%;
    height: 75px;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    opacity: 0.6;
    transition: all 0.5s ease;
    left: 0;
    
    // 根据玩家位置调整牌的对齐方式
    .pos-top-left &,
    .pos-left-bottom & {
      justify-content: flex-end; // 左上角和左下角：牌在右侧
    }
    
    .pos-top-right &,
    .pos-right-bottom & {
      justify-content: flex-start; // 右上角和右下角：牌在左侧
    }

    &.revealed {
      opacity: 1;
    }

    // 头道 - 显示在最上面，但层级最低（被遮挡最多）
    &.front-row {
      top: 0;
      z-index: 1;
    }

    // 中道 - 显示在中间，层级居中，向下遮挡头道一半
    &.middle-row {
      top: 30px;
      z-index: 2;
    }

    // 尾道 - 显示在最下面，层级最高，遮挡中道一半
    &.back-row {
      top: 60px;
      z-index: 3;
    }

    .hand-type-badge {
      position: absolute;
      top: -8px;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: bold;
      background: linear-gradient(135deg, #ffd700, #ff8c00);
      color: #fff;
      z-index: 10;
      white-space: nowrap;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      animation: badgePopIn 0.3s ease;

      .pos-top-left &,
      .pos-left-bottom & {
        right: 0;
      }

      .pos-top-right &,
      .pos-right-bottom & {
        left: 0;
      }
    }

    .fan-card {
      position: absolute;
      top: 0;
      transition: all 0.3s ease;

      &:hover {
        z-index: 100 !important;
        transform: translateY(-10px) scale(1.1) !important;
      }
    }

    .row-score {
      position: absolute;
      top: -6px;
      right: 4px;
      padding: 1px 6px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: bold;
      background: rgba(0, 0, 0, 0.75);
      color: #fff;
      z-index: 10;
      white-space: nowrap;
      animation: scorePopIn 0.4s ease;

      &.positive {
        color: #27ae60;
      }

      &.negative {
        color: #e74c3c;
      }

      &.zero {
        color: #bdc3c7;
      }
    }

  }

}

// 摆牌阶段牌堆背面 - 三摞扇形显示（头道在上被遮挡，尾道在下最前面）
// 尺寸与位置与比牌阶段的 .ai-cards-fan 保持一致
.ai-cards-back {
  .three-stacks-fan {
    position: relative;
    width: 270px;
    height: 165px;
    padding: 8px;

    .stack-row-fan {
      position: absolute;
      width: 100%;
      height: 75px;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      left: 0;

      // 头道 - 显示在最上面，但层级在最下面（被遮挡最多）
      &.front-stack {
        top: 0;
        z-index: 1;
      }

      // 中道 - 显示在中间，层级也在中间
      &.middle-stack {
        top: 30px;
        z-index: 2;
      }

      // 尾道 - 显示在最下面，但层级在最前面（遮挡其他）
      &.back-stack {
        top: 60px;
        z-index: 3;
      }

      .stack-card-fan {
        position: absolute;

        .card-back-small {
          // 与 PlayingCard size="small" 完全一致：54x75px
          width: 54px;
          height: 75px;
          background: linear-gradient(135deg, #1e5799 0%, #2989d8 50%, #207cca 51%, #7db9e8 100%);
          border-radius: 6px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
          position: relative;

          &::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 40px;
            height: 50px;
            background: repeating-linear-gradient(
              45deg,
              rgba(255, 255, 255, 0.1),
              rgba(255, 255, 255, 0.1) 3px,
              transparent 3px,
              transparent 6px
            );
            border-radius: 3px;
          }
        }
      }
    }
  }

}

// 发牌/摆牌阶段显示（两排牌）
.dealing-hands {
  animation: fadeInRow 0.3s ease-out;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 120px;

    .dealing-card-row {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 4px;

      &.row-2 {
      margin-top: -52px;
    }

    .dealt-card {
      margin-left: -34px;
      transition: all 0.2s ease;

      &:first-child {
        margin-left: 0;
      }

      &.dealt-fly {
        animation: cardFlyIn 0.4s ease-out both;
      }

      &.last-dealt {
        animation: cardPopIn 0.25s ease-out;
      }

      // 当前用户牌面与比牌阶段 small 牌尺寸保持一致
      ::v-deep .playing-card.dealing-card-override {
        width: 54px !important;
        height: 75px !important;
        border-radius: 6px !important;

        .card-corner {
          font-size: 12px !important;

          .rank {
            font-size: 15px !important;
          }

          .suit {
            font-size: 9px !important;
          }

          &.top-left {
            top: 3px !important;
            left: 3px !important;
          }

          &.bottom-right {
            bottom: 3px !important;
            right: 3px !important;
          }
        }

        .card-center .suit-large {
          font-size: 27px !important;
        }
      }

      .card-back-small-dealing {
        width: 54px;
        height: 75px;
        background: linear-gradient(135deg, #1e5799 0%, #2989d8 50%, #207cca 51%, #7db9e8 100%);
        border-radius: 6px;
        border: 1px solid rgba(255, 255, 255, 0.3);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        position: relative;

        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 40px;
          height: 50px;
          background: repeating-linear-gradient(
            45deg,
            rgba(255, 255, 255, 0.1),
            rgba(255, 255, 255, 0.1) 3px,
            transparent 3px,
            transparent 6px
          );
          border-radius: 3px;
        }
      }
    }
  }

  .dealing-label {
    color: rgba(255, 255, 255, 0.8);
    font-size: 12px;
    margin-top: 4px;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
    white-space: nowrap;
  }
}

@keyframes cardPopIn {
  0% {
    opacity: 0;
    transform: scale(0.4) translateY(15px);
  }
  60% {
    transform: scale(1.15) translateY(-3px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes cardFlyIn {
  0% {
    opacity: 0;
    transform: translate(var(--fly-x), var(--fly-y)) scale(0.2);
  }
  60% {
    opacity: 1;
    transform: translate(0, 0) scale(1.05);
  }
  100% {
    opacity: 1;
    transform: translate(0, 0) scale(1);
  }
}

@keyframes fadeInRow {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes layoutAppear {
  from {
    opacity: 0;
    transform: scale(0.85);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

// 比牌浮动分数
.floating-score {
  position: fixed;
  z-index: 150;
  pointer-events: none;
  font-size: 22px;
  font-weight: bold;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.7);
  animation: floatUpFade 1.2s ease-out forwards;
  transform: translate(-50%, -50%);
  white-space: nowrap;

  &.positive {
    color: #2ecc71;
  }

  &.negative {
    color: #e74c3c;
  }
}

@keyframes floatUpFade {
  0% {
    opacity: 1;
    transform: translate(-50%, -50%) translateY(0) scale(1);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) translateY(-30px) scale(1.1);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) translateY(-60px) scale(0.8);
  }
}

.money-pop {
  position: fixed;
  z-index: 160;
  pointer-events: none;
  transform: translate(-50%, -70%);
  width: 0;
  height: 0;

  .money-amount {
    position: absolute;
    left: 0;
    top: -8px;
    transform: translate(-50%, 0);
    font-size: 30px;
    font-weight: 800;
    letter-spacing: 0.5px;
    white-space: nowrap;
    animation: moneyAmount 1.6s ease-out forwards;
  }

  .money-coin {
    position: absolute;
    left: 0;
    top: 0;
    width: 16px;
    height: 16px;
    margin-left: -8px;
    margin-top: -8px;
    border-radius: 50%;
    background:
      radial-gradient(circle at 35% 32%, #fff6c2 0 22%, transparent 24%),
      radial-gradient(circle at 50% 50%, #ffe56a 0 42%, #e2a51a 70%, #a86a08 100%);
    box-shadow: inset 0 0 0 1px rgba(120, 70, 0, 0.35), 0 2px 4px rgba(0, 0, 0, 0.35);
  }

  &.positive {
    .money-amount {
      color: #ffe14a;
      text-shadow: 0 2px 0 #8a5a00, 0 4px 10px rgba(0, 0, 0, 0.55);
    }

    .money-coin {
      animation: coinWin 1.35s ease-out forwards;
    }
  }

  &.negative {
    .money-amount {
      color: #ff5a5a;
      text-shadow: 0 2px 0 #6e1010, 0 4px 10px rgba(0, 0, 0, 0.55);
    }

    .money-coin {
      filter: grayscale(0.25) brightness(0.85);
      animation: coinLose 1.35s ease-in forwards;
    }
  }
}

@keyframes moneyAmount {
  0% {
    opacity: 0;
    transform: translate(-50%, 8px) scale(0.6);
  }
  18% {
    opacity: 1;
    transform: translate(-50%, -6px) scale(1.18);
  }
  70% {
    opacity: 1;
    transform: translate(-50%, -28px) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -52px) scale(0.92);
  }
}

@keyframes coinWin {
  0% {
    opacity: 0;
    transform: translate(0, 8px) scale(0.4);
  }
  20% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(var(--dx), -78px) scale(1);
  }
}

@keyframes coinLose {
  0% {
    opacity: 0;
    transform: translate(0, -10px) scale(0.7);
  }
  20% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(var(--dx), 64px) scale(0.55);
  }
}

// 中央游戏区
.game-center {
  flex: 1;
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.status-message {
  padding: 12px 24px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  color: #ffd700;
  font-size: 18px;
  font-weight: bold;
}

.compare-stage {
  margin-top: 20px;
}

.table-round-actions {
  margin-top: 14px;
  display: flex;
  justify-content: center;
  pointer-events: auto;
}

.next-round-countdown {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 160px;
  padding: 10px 28px 14px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.55);
  border: 2px solid rgba(255, 215, 0, 0.45);
  box-shadow: 0 0 24px rgba(255, 215, 0, 0.25);
}

.next-round-count {
  font-size: 56px;
  font-weight: 800;
  line-height: 1;
  color: #ffd700;
  text-shadow: 0 3px 8px rgba(0, 0, 0, 0.45);
  animation: countdownPulse 0.8s ease-in-out infinite;
}

.next-round-label {
  font-size: 14px;
  font-weight: 700;
  color: #fff6d0;
  letter-spacing: 1px;
}

.stage-indicator {
  padding: 12px 32px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50px;
  color: white;
  font-size: 32px;
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 2px solid rgba(255, 255, 255, 0.1);
  animation: stagePopIn 0.5s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  .stage-icon {
    line-height: 1;
  }

  &.stage-1 {
    color: #ffd700;
    border-color: rgba(255, 215, 0, 0.5);
    box-shadow: 0 0 30px rgba(255, 215, 0, 0.4), inset 0 0 20px rgba(255, 215, 0, 0.1);
    animation: stagePopIn 0.5s ease, stageGlowGold 1.5s ease-in-out infinite alternate;
  }

  &.stage-2 {
    color: #ff8c00;
    border-color: rgba(255, 140, 0, 0.5);
    box-shadow: 0 0 30px rgba(255, 140, 0, 0.4), inset 0 0 20px rgba(255, 140, 0, 0.1);
    animation: stagePopIn 0.5s ease, stageGlowOrange 1.5s ease-in-out infinite alternate;
  }

  &.stage-3 {
    color: #ff6347;
    border-color: rgba(255, 99, 71, 0.5);
    box-shadow: 0 0 30px rgba(255, 99, 71, 0.4), inset 0 0 20px rgba(255, 99, 71, 0.1);
    animation: stagePopIn 0.5s ease, stageGlowRed 1.5s ease-in-out infinite alternate;
  }

  .countdown {
    font-size: 48px;
    font-weight: bold;
    color: #ffd700;
    animation: countdownPulse 0.8s ease-in-out infinite;
  }
}

@keyframes stagePopIn {
  0% {
    opacity: 0;
    transform: scale(0.5) translateY(20px);
  }
  60% {
    transform: scale(1.1) translateY(-5px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes stageGlowGold {
  0% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.3), inset 0 0 10px rgba(255, 215, 0, 0.05); }
  100% { box-shadow: 0 0 40px rgba(255, 215, 0, 0.6), inset 0 0 30px rgba(255, 215, 0, 0.15); }
}

@keyframes stageGlowOrange {
  0% { box-shadow: 0 0 20px rgba(255, 140, 0, 0.3), inset 0 0 10px rgba(255, 140, 0, 0.05); }
  100% { box-shadow: 0 0 40px rgba(255, 140, 0, 0.6), inset 0 0 30px rgba(255, 140, 0, 0.15); }
}

@keyframes stageGlowRed {
  0% { box-shadow: 0 0 20px rgba(255, 99, 71, 0.3), inset 0 0 10px rgba(255, 99, 71, 0.05); }
  100% { box-shadow: 0 0 40px rgba(255, 99, 71, 0.6), inset 0 0 30px rgba(255, 99, 71, 0.15); }
}

@keyframes countdownPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
}

@keyframes scorePopIn {
  0% { opacity: 0; transform: scale(0.5) translateY(10px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}

@keyframes badgePopIn {
  0% { opacity: 0; transform: scale(0.3); }
  60% { transform: scale(1.1); }
  100% { opacity: 1; transform: scale(1); }
}

@keyframes sweepPopIn {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
  60% { transform: translate(-50%, -50%) scale(1.1); }
  100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}

@keyframes homeRunPop {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.45) rotate(-8deg); }
  55% { opacity: 1; transform: translate(-50%, -50%) scale(1.12) rotate(2deg); }
  100% { opacity: 1; transform: translate(-50%, -50%) scale(1) rotate(0); }
}

@keyframes homeRunRaySpin {
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
}

@keyframes homeRunTitleGlow {
  0%, 100% { text-shadow: 0 3px 0 #7a2400, 0 0 16px rgba(255, 217, 0, 0.95); }
  50% { text-shadow: 0 3px 0 #7a2400, 0 0 30px rgba(255, 94, 0, 1); }
}

.home-run-overlay {
  position: fixed;
  inset: 0;
  z-index: 260;
  pointer-events: none;
}

.home-run-burst {
  position: absolute;
  left: 50%;
  top: 42%;
  width: min(360px, 80vw);
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: homeRunPop 0.55s ease both;
}

.home-run-rays {
  position: absolute;
  left: 50%;
  top: 50%;
  width: min(340px, 78vw);
  height: min(340px, 78vw);
  border-radius: 50%;
  background:
    conic-gradient(from 0deg, rgba(255, 235, 59, 0.9), rgba(255, 128, 0, 0.12), rgba(255, 235, 59, 0.9), rgba(255, 45, 85, 0.12), rgba(255, 235, 59, 0.9));
  filter: blur(1px);
  opacity: 0.75;
  transform: translate(-50%, -50%);
  animation: homeRunRaySpin 1.4s linear infinite;
}

.home-run-title {
  position: relative;
  z-index: 1;
  color: #ffe66d;
  font-size: 54px;
  font-weight: 900;
  line-height: 1;
  letter-spacing: 0;
  -webkit-text-stroke: 2px #8a2b00;
  animation: homeRunTitleGlow 0.7s ease infinite;
}

.home-run-players {
  position: relative;
  z-index: 1;
  margin-top: 12px;
  max-width: 100%;
  padding: 5px 14px;
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.42);
  color: #fff8c9;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.compare-stage .stage-text.special-stage {
  color: #ffe08a;
  text-shadow: 0 0 14px rgba(255, 196, 0, 0.95);
  font-size: 28px;
  letter-spacing: 4px;
}

.special-announce-overlay {
  position: fixed;
  inset: 0;
  z-index: 255;
  pointer-events: none;
}

.special-announce-burst {
  position: absolute;
  left: 50%;
  top: 42%;
  width: min(380px, 86vw);
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: specialAnnouncePop 0.45s ease both;
}

.special-announce-flash {
  position: absolute;
  left: 50%;
  top: 46%;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
  transform: translate(-50%, -50%);
}

.special-announce-flash.flash-f2 {
  width: min(420px, 92vw);
  height: min(420px, 92vw);
  animation: homeRunRaySpin 2.6s linear infinite;
}

.special-announce-flash.flash-f1 {
  width: min(300px, 68vw);
  height: min(300px, 68vw);
  animation: homeRunRaySpin 1.3s linear infinite reverse;
}

.special-announce-mark {
  position: relative;
  z-index: 1;
  width: 64px;
  height: 64px;
  margin-bottom: 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 900;
  color: #2a1600;
  animation: specialMarkSpin 0.7s ease both;
}

.special-announce-title {
  position: relative;
  z-index: 1;
  font-size: 52px;
  font-weight: 900;
  line-height: 1;
  letter-spacing: 6px;
  animation: homeRunTitleGlow 0.7s ease infinite;
}

.special-announce-score,
.special-announce-player {
  position: relative;
  z-index: 1;
  margin-top: 8px;
  padding: 4px 14px;
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.45);
  color: #fff8d6;
  font-size: 16px;
  font-weight: bold;
}

.special-announce-overlay.type-DRAGON {
  .special-announce-mark { background: radial-gradient(circle, #fff3b0, #ffb300); box-shadow: 0 0 24px rgba(255, 193, 7, 0.9); }
  .special-announce-title { color: #ffe082; -webkit-text-stroke: 2px #8a4b00; }
}

.special-announce-overlay.type-ALL_BIG {
  .special-announce-mark { background: radial-gradient(circle, #ffd0c2, #e53935); color: #fff; box-shadow: 0 0 22px rgba(229, 57, 53, 0.85); }
  .special-announce-title { color: #ff8a80; -webkit-text-stroke: 2px #6d1212; }
}

.special-announce-overlay.type-ALL_SMALL {
  .special-announce-mark { background: radial-gradient(circle, #e0f7fa, #00acc1); color: #08343a; box-shadow: 0 0 22px rgba(0, 172, 193, 0.85); }
  .special-announce-title { color: #80deea; -webkit-text-stroke: 2px #004d56; }
}

.special-announce-overlay.type-SIX_PAIRS_HALF {
  .special-announce-mark { background: radial-gradient(circle, #f3e5f5, #8e24aa); color: #fff; box-shadow: 0 0 22px rgba(142, 36, 170, 0.85); }
  .special-announce-title { color: #e1bee7; -webkit-text-stroke: 2px #4a148c; }
}

.special-announce-overlay.type-THREE_FLUSH {
  .special-announce-mark { background: radial-gradient(circle, #e8f5e9, #2e7d32); color: #fff; box-shadow: 0 0 22px rgba(46, 125, 50, 0.85); }
  .special-announce-title { color: #a5d6a7; -webkit-text-stroke: 2px #1b5e20; }
}

.special-announce-overlay.type-THREE_STRAIGHT {
  .special-announce-mark { background: radial-gradient(circle, #fff3e0, #ef6c00); color: #3e2200; box-shadow: 0 0 22px rgba(239, 108, 0, 0.85); }
  .special-announce-title { color: #ffcc80; -webkit-text-stroke: 2px #e65100; }
}

@keyframes specialAnnouncePop {
  0% { opacity: 0; transform: translate(-50%, -46%) scale(0.55); }
  70% { opacity: 1; transform: translate(-50%, -50%) scale(1.06); }
  100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}

@keyframes specialMarkSpin {
  0% { transform: scale(0.2) rotate(-18deg); opacity: 0; }
  100% { transform: scale(1) rotate(0); opacity: 1; }
}

.ai-cards-fan.is-special-burst {
  filter: drop-shadow(0 0 10px rgba(255, 214, 64, 0.85));

  .fan-card.special-flip {
    animation: specialCardIn 0.38s ease both;
  }
}

.special-hand-display-badge.type-DRAGON { background: linear-gradient(135deg, #ffe082, #ffb300); }
.special-hand-display-badge.type-ALL_BIG { background: linear-gradient(135deg, #ff8a80, #e53935); color: #fff; }
.special-hand-display-badge.type-ALL_SMALL { background: linear-gradient(135deg, #80deea, #00838f); color: #fff; }
.special-hand-display-badge.type-SIX_PAIRS_HALF { background: linear-gradient(135deg, #e1bee7, #8e24aa); color: #fff; }
.special-hand-display-badge.type-THREE_FLUSH { background: linear-gradient(135deg, #a5d6a7, #2e7d32); color: #fff; }
.special-hand-display-badge.type-THREE_STRAIGHT { background: linear-gradient(135deg, #ffcc80, #ef6c00); color: #3e2200; }

@keyframes specialCardIn {
  0% { opacity: 0; filter: brightness(2.4); }
  100% { opacity: 1; filter: brightness(1); }
}

// 乌龙（倒水）动画 - 显示在玩家牌位上方
.wulong-overlay {
  position: absolute;
  z-index: 210;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 2px;

  .wulong-badge {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 10px 16px;
    border-radius: 16px;
    background: linear-gradient(135deg, #8e44ad, #6c3483);
    color: #fff;
    box-shadow: 0 4px 20px rgba(142, 68, 173, 0.65);
    animation: wulongBadgePop 0.35s ease;

    &.is-active {
      animation: wulongBadgePop 0.35s ease, wulongPulse 0.8s ease-in-out 2;
    }

    .wulong-label {
      font-size: 22px;
      font-weight: 900;
      letter-spacing: 4px;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.35);
    }

    .wulong-player-name {
      font-size: 12px;
      font-weight: bold;
      opacity: 0.95;
    }
  }
}

@keyframes wulongBadgePop {
  0% { transform: scale(0.4); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes wulongPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

.compare-stage .stage-text.wulong-stage {
  color: #e8b4ff;
  text-shadow: 0 0 12px rgba(142, 68, 173, 0.9);
  font-size: 28px;
  letter-spacing: 6px;
}

// 打枪动画 - 显示在每个玩家位置上方
.sweep-overlay {
  position: absolute;
  z-index: 200;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 2px;

  .sweep-badge {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 6px 10px;
    border-radius: 14px;
    font-size: 13px;
    font-weight: bold;
    white-space: nowrap;
    animation: sweepBadgePop 0.3s ease;

    &.shooter {
      // background: linear-gradient(135deg, #ffd700, #ff8c00);
      // color: #1a1a2e;
      // box-shadow: 0 4px 16px rgba(255, 215, 0, 0.6);

      .gun-wrapper {
        position: relative;
        width: 164px;
        height: 164px;
        display: flex;
        align-items: center;
        justify-content: center;

        .gun-image {
          width: 128px;
          height: 128px;
          object-fit: contain;
          animation: gunRecoil 0.25s ease infinite alternate;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
        }

        .muzzle-flash {
          position: absolute;
          top: 50%;
          right: -4px;
          width: 20px;
          height: 20px;
          transform: translateY(-50%);
          background: radial-gradient(circle, #fff 0%, #ffeb3b 30%, #ff9800 60%, transparent 80%);
          border-radius: 50%;
          opacity: 0;
          animation: muzzleFlash 0.25s ease infinite alternate;
          filter: blur(1px);
        }
      }

      .sweep-label {
        text-shadow: 0 1px 2px rgba(255,255,255,0.4);
      }
    }

    &.target {
      // background: linear-gradient(135deg, #e74c3c, #c0392b);
      // color: white;
      // box-shadow: 0 4px 16px rgba(231, 76, 60, 0.6);
      animation: targetShake 0.15s ease infinite;

      .bullet-holes-wrapper {
        position: relative;
        width: 168px;
        height: 168px;

        .bullet-hole {
          position: absolute;
          width: 56px;
          height: 56px;
          object-fit: contain;
          opacity: 0;
          filter: drop-shadow(0 2px 5px rgba(0,0,0,0.6));
          animation: holeAppear 0.25s ease forwards;
        }

        .hole-1 {
          top: 18px;
          left: 14px;
          animation-delay: 0.1s;
        }

        .hole-2 {
          top: 68px;
          left: 82px;
          animation-delay: 0.25s;
        }

        .hole-3 {
          top: 12px;
          left: 98px;
          animation-delay: 0.4s;
        }

        .hole-4 {
          top: 96px;
          left: 32px;
          animation-delay: 0.55s;
        }

        .hole-5 {
          top: 48px;
          left: 128px;
          animation-delay: 0.7s;
        }
      }

      .target-label {
        text-shadow: 0 1px 2px rgba(0,0,0,0.3);
      }
    }
  }
}

@keyframes sweepBadgePop {
  from { opacity: 0; transform: scale(0.5) translateY(-10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

@keyframes gunRecoil {
  from { transform: rotate(-8deg) translateY(0) scale(1); }
  to { transform: rotate(8deg) translateY(-2px) scale(1.08); }
}

@keyframes muzzleFlash {
  0% { opacity: 0; transform: translateY(-50%) scale(0.4); }
  100% { opacity: 1; transform: translateY(-50%) scale(1.2); }
}

@keyframes targetShake {
  0%, 100% { transform: translate(0, 0); }
  20% { transform: translate(-3px, 2px); }
  40% { transform: translate(3px, -2px); }
  60% { transform: translate(-2px, -3px); }
  80% { transform: translate(2px, 3px); }
}

@keyframes holesBurst {
  0% { opacity: 0; transform: scale(0.3) rotate(-10deg); }
  60% { opacity: 1; transform: scale(1.15) rotate(2deg); }
  100% { opacity: 1; transform: scale(1) rotate(0deg); }
}

@keyframes holeAppear {
  0% { opacity: 0; transform: scale(0.2); }
  60% { opacity: 1; transform: scale(1.2); }
  100% { opacity: 1; transform: scale(1); }
}

// 摆牌面板背景遮罩
.arrange-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
}

// 浮动摆牌面板 - 居中显示
.arrange-panel {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  // border: 1px solid rgba(255, 255, 255, 0.2);
  // box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);

  // background-color: rgba(83, 52, 46, 0.9);
  border-radius: 25px;
  border: 1px solid rgba(248, 239, 148, 0.9);
  box-shadow: 0 5px 5px 5px rgba(0, 0, 0, 0.3), inset 0 0 0 5px rgb(60, 37, 30), inset 0 0 0 6px rgb(110, 76, 60);

  // width: 980px;
  // height: 440px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .arrange-panel-inner {

    padding: 12px;
    gap: 4px;
    width: 100%;
    height: 100%;


    background-image: url('@/assets/images/baipai_bk.png');
    background-repeat: no-repeat;
    background-size: 90%;
    background-position: center bottom;
    background-clip: padding-box;

    display: flex;

    .arrange-main{
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 2px;
      padding: 0;
      flex: 1;
    }

  }

  .arrange-side{

    width: 100px;
    // 在 Flex 布局中，让两个子 div 高度保持一致是非常简单的。Flexbox 有一个非常贴心的默认机制：同一行的 Flex 子元素天生就是等高的。
    // 你只需要确保父容器开启了 Flex 布局，并且不要给子元素设置固定的 height 即可
    // height: 100%;

    display: flex;
    flex-direction: column;
    // align-items: flex-end;
    gap: 2px;

    .arrange-actions {
      display: flex;
      flex: 1;
      flex-direction: column;
      justify-content: flex-end;
      align-items: flex-end;
      gap: 8px;

      .action-btn-onekey,
      .action-btn-confirm,
      .action-btn-reset {
        display: flex;
        padding: 8px 0;
        border: none;
        border-radius: 8px;
        font-size: 13px;
        font-weight: bold;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        width: 100px;
        height: 28px;
        background: none;

        &:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          color: #eee;
        }
      }

      .action-btn-onekey {
        // background: linear-gradient(135deg, #9b59b6, #8e44ad);
        color: white;

        background-image: url('@/assets/images/baipai_btn_bk1.png');
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center bottom;
        background-clip: padding-box;

        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(155, 89, 182, 0.4);
        }

        .onekey-icon {
          font-size: 14px;
        }
      }

      .action-btn-confirm {
        // background: linear-gradient(135deg, #27ae60, #2ecc71);
        color: white;

        background-image: url('@/assets/images/baipai_btn_bk2.png');
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center bottom;
        background-clip: padding-box;

        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(39, 174, 96, 0.4);
        }
      }

      .action-btn-reset {
        // background: rgba(255, 255, 255, 0.15);
        color: white;

        background-image: url('@/assets/images/baipai_btn_bk3.png');
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center bottom;
        background-clip: padding-box;

        &:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-2px);
        }
      }
    }

    .arrange-smart-btn {
      flex: 1;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      .action-btn-smart {
        
        width: 48px;
        height: 24px;
        border-radius: 5px;
        border: 2px solid #27ae60;
        background: linear-gradient(135deg, #2ecc71, #27ae60);
        color: white;
        font-size: 13px;
        font-weight: bold;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(39, 174, 96, 0.5);
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .arrange-top-bar {

      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-end;

      .arrange-countdown {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        // background: rgba(0, 0, 0, 0.35);
        padding: 6px 14px;
        border-radius: 20px;
        

        width: 100px;
        margin-top: 0px;

        .clock-icon {
          // font-size: 32px;
          background-image: url('@/assets/images/timing.png');
          background-repeat: no-repeat;
          background-size: contain;
          background-position: center bottom;
          background-clip: padding-box;
          width: 48px;
          height: 48px;
        }

        color: #ffd700;
        font-weight: bold;
        font-size: 18px;
        user-select: none;
        --text-color: #301803; 
        text-shadow:
            -2px -2px 0 var(--text-color),
            2px -2px 0 var(--text-color),
            -2px 2px 0 var(--text-color),
            2px 2px 0 var(--text-color),
            0 -2px 0 var(--text-color),
            0 2px 0 var(--text-color),
            -2px 0 0 var(--text-color),
            2px 0 0 var(--text-color),
            0 3px 4px var(--text-color);
      }

      .arrange-close-btn {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: none;
        background: rgba(255, 255, 255, 0.15);
        color: white;
        font-size: 22px;
        line-height: 1;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;

        background-image: url('@/assets/images/close.png');
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center bottom;
        background-clip: padding-box;


        &:hover {
          // background: rgba(231, 76, 60, 0.8);
          transform: translateY(-2px);
        }
      }
    }
  }


  .layout-rows {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;

    .row-front{
      width: 300px;
    }

    .row-middle{
      width: 400px;
    }
    .row-back{
      width: 400px;
    }

    .layout-row {

      position: relative;
      height: 68px;
      border: solid transparent;
      border-width: 5px 20px 5px 90px;
      border-image: url('@/assets/images/three_row_bk.png') 8 30 8 140 fill;


      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      // background: rgba(0, 0, 0, 0.25);
      border-radius: 10px;
      padding: 8px 12px;
      // border: 1px solid rgba(255, 255, 255, 0.08);
      transition: all 0.2s ease;
      cursor: pointer;


      &:hover {
        // background: rgba(0, 0, 0, 0.35);
        // border-color: rgba(255, 255, 255, 0.15);
      }

      &.row-active {
        border-color: rgba(255, 215, 0, 0.4);
        background: rgba(255, 215, 0, 0.08);
      }

      .row-label-text {

        flex-shrink: 0;
        color: rgba(255, 255, 255, 0.85);
        font-size: 14px;
        font-weight: bold;
        text-align: center;

        display: flex;
        align-items: center;
        justify-content: center;
        width: 72px;
        height: 30px;
        background: url('@/assets/images/btn_bk.png') no-repeat ;
        background-size: contain;
        position: absolute;
        left: -78px;
        font-size: 14px;
        font-weight: bold;
        font-family: 'Arial Black', 'Impact', 'Arial', sans-serif;
        color: #F5E6C8;
        text-shadow:
            -2px -2px 0 #6B1220,
            2px -2px 0 #6B1220,
            -2px 2px 0 #6B1220,
            2px 2px 0 #6B1220,
            0 -2px 0 #6B1220,
            0 2px 0 #6B1220,
            -2px 0 0 #6B1220,
            2px 0 0 #6B1220,
            0 3px 4px rgba(107, 18, 32, 0.3);
        line-height: 1;
        user-select: none;
      }

      .row-slot {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 58px;
        // background: rgba(0, 0, 0, 0.2);
        border-radius: 12px;
        padding: 2px 4px;
        border: 1px solid rgba(255, 255, 255, 0);

        position: absolute;
        left: -2px;
        right: 0;

        &.slot-valid {
          // border-color: #27ae60;
          // background: rgba(39, 174, 96, 0.1);
          // border-style: solid;
        }

        &.slot-invalid {
          border-color: #e74c3c;
          background: rgba(231, 76, 60, 0.4);
          border-style: solid;
        }

        .slot-placeholder {
          color: rgba(255, 255, 255, 0.35);
          font-size: 13px;
        }

        .slot-card-item {
          flex-shrink: 0;

          .playing-card {
            transition: none;
          }

          &:hover {
            z-index: 100 !important;
            transform: translateY(-4px);
          }
        }
      }

      .row-clear-btn {
        position: absolute;
        right: 5px;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        border: none;
        background: rgba(231, 76, 60, 0.8);
        color: white;
        font-size: 13px;
        line-height: 1;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: all 0.2s ease;

        &:hover {
          background: #e74c3c;
          transform: scale(1.1);
        }
      }

      .row-clear-placeholder {
        width: 22px;
        flex-shrink: 0;
      }
    }
  }

  .hand-type-hints {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 6px;
    padding: 4px 0;

    // background: rgba(0,0,0, 0.2);
    // border-radius: 5px;
    // padding: 5px 5px;
    // margin: 2px;
    // border: 1px solid #fee5a3;

    .hint-item {
      padding: 3px 8px;
      border-radius: 5px;
      background: rgba(255, 255, 255, 0.08);
      color: #fff;
      font-size: 14px;
      cursor: pointer;
      user-select: none;

      user-select: none;
      border-radius: 18px;


      &.hint-active {
        color: rgba(108, 41, 14, 1);
        font-weight: bold;
        background: linear-gradient(180deg, #fee5a3, #dd984b);
        box-shadow: 
            0 0 0 2px #e7c4ab,
            inset 0 0 1px 1px #5d3b11
            ;
        
        user-select: none;
        // --text-color: #301803; 
        // text-shadow:
        //     -2px -2px 0 var(--text-color),
        //     2px -2px 0 var(--text-color),
        //     -2px 2px 0 var(--text-color),
        //     2px 2px 0 var(--text-color),
        //     0 -2px 0 var(--text-color),
        //     0 2px 0 var(--text-color),
        //     -2px 0 0 var(--text-color),
        //     2px 0 0 var(--text-color),
        //     0 3px 4px var(--text-color);
        
      }

      &.hint-active:hover {
        color: #fff;
        background: linear-gradient(180deg, #f45f5f, #770303);
      }

    }
  }

  .arrange-bottom {
    display: flex;
    gap: 12px;
    align-items: stretch;

    .hands-area-new {

      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 50px;
      flex: 1;
      border-radius: 10px;

      border: solid transparent;

      border-width: 10px 30px;
      border-image-source: url("@/assets/images/shoupai_bk.png") ; /* 你的边框图 */
      border-image-slice: 14 60 fill;    /* 从四边切25%，保留圆角 */
      border-image-width: 1;      /* 显示宽度 = 切片大小（不变形） */
      border-image-repeat: stretch; /* 边拉伸，角不变 */


      .hands-cards-new {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 2px;
        // overflow-x: auto;

        width: 492px;
        height: 50px;

        .playing-card {
          flex-shrink: 0;
          cursor: pointer;

          &:hover {
            z-index: 100;
            transform: translateY(-4px);
          }

          &.selected {
            transform: translateY(-7px);
            z-index: 50;
            box-shadow: 0 0 0 2px #ffd700, 0 4px 8px rgba(0, 0, 0, 0.3);
          }
        }
      }
    }

    
  }
}

// PC端摆牌面板内缩小牌面尺寸
.arrange-panel ::v-deep .playing-card.small {
  width: 36px;
  height: 50px;
  border-radius: 4px;

  .card-corner {
    font-size: 8px;

    .rank {
      font-size: 10px;
    }

    .suit {
      font-size: 5px;
    }

    &.top-left {
      top: 2px;
      left: 2px;
    }

    &.bottom-right {
      bottom: 2px;
      right: 2px;
    }
  }

  .card-center .suit-large {
    font-size: 16px;
  }
}

// 显示摆牌面板按钮 - 右下角
.show-arrange-btn {
  position: fixed;
  bottom: 12px;
  right: 12px;
  z-index: 99;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, #27ae60, #2ecc71);
  color: white;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(39, 174, 96, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(39, 174, 96, 0.5);
  }
}

.special-hand-self-hands {
  position: relative;
}

.special-hand-self-badge,
.special-hand-display-badge {
  position: absolute;
  top: -22px;
  left: 50%;
  transform: translateX(-50%);
  padding: 2px 10px;
  border-radius: 10px;
  background: rgba(241, 196, 15, 0.92);
  color: #2c2410;
  font-size: 11px;
  font-weight: bold;
  white-space: nowrap;
  z-index: 2;
}

.ai-cards-fan.is-special-hand {
  position: relative;
  padding-top: 8px;
}

.bottom-room-rate {
  position: fixed;
  bottom: 50px;
  right: 12px;
  z-index: 98;
  padding: 6px 14px;
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid #f9e098;
  border-radius: 8px;
  color: #f9e098;
  font-size: 14px;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

// 显示结算窗口按钮 - 右下角
.show-settle-btn {
  position: fixed;
  bottom: 12px;
  right: 12px;
  z-index: 99;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, #ffd700, #ff8c00);
  color: #1a1a2e;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 215, 0, 0.5);
  }
}

// 结算弹窗
.settle-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.settle-content {
  // background: linear-gradient(135deg, rgba(55, 60, 56, 1), rgba(55, 60, 56, 1));
  border-radius: 16px;
  padding: 24px 32px 32px;
  max-width: 1200px;
  max-height: 88vh;
  width: 100%;
  position: relative;

  background-color: rgba(83, 52, 46, 0.5);
  border-radius: 25px;
  border: 3px solid rgba(248, 239, 148,0.9);
  box-shadow: 
      0 5px 5px 5px rgba(0, 0, 0, 0.3),
      inset 0 0 0 5px rgb(60, 37, 30),
      inset 0 0 0 6px rgba(110, 76, 60,1)
      ;

  .settle-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    
    .settle-title {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      top: -40px;
      width: 160px;
      height: 80px;

      background: url('@/assets/images/settle_title_bk.png') no-repeat center / contain;

      display: flex;
      align-items: center;
      justify-content: center;

      color: #ffd700;
      text-align: center;
      margin: 0;
      flex: 1;
      font-size: 22px;
    }

    .close-settle-btn {
      position: absolute;
      top: -10px;
      right: -10px;
      width: 32px;
      height: 32px;
      border: none;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.1);
      color: white;
      font-size: 20px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      // background: 
      //           linear-gradient(180deg, rgb(255, 155, 73) 0%, rgba(180, 51, 0, 1) 50%,rgba(228, 81, 8, 1) 100%), /* 内层背景（盖住中间） */
      //           linear-gradient(180deg, #ffe491, #dd9543, #9d3802); 
      // box-shadow:  0 5px 20px 0px #000;
      // background-clip: content-box, border-box;
      // padding: 3px; 

      background: url('@/assets/images/close.png') no-repeat center / contain;

      &:hover {
        background: rgba(231, 76, 60, 0.8);
      }
    }
  }

  .result-section {
    margin-bottom: 10px;
    margin-top: 20px;
    overflow-y: auto;
    --width-calc: calc(100% -30px);
    height: var(--width-calc);
    .section-title {
      color: rgba(255, 255, 255, 0.7);
      font-size: 14px;
      margin-bottom: 8px;
      font-weight: 500;
    }
  }

  .settle-players {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    
  }

  .settle-player-card {
    // background: rgba(0, 0, 0, 0.25);
    border-radius: 12px;
    padding: 12px;
    border: 1px solid rgba(255, 255, 255, 0.08);

    // background:linear-gradient(180deg,  #865745 0%, #6b4a3f 100%);
    background: linear-gradient(180deg, rgba(255, 215, 0, 0.01) 0%, rgba(255, 215, 0, 0.31) 100%);
    border-radius: 15px;
    // border: 2px solid rgba(248, 239, 148,0.7);
    box-shadow: 0 5px 5px 5px rgba(0, 0, 0, 0.3);

    min-width: 0;
    overflow: hidden;

    &.highlight-human {
      border-color: rgba(255, 215, 0, 0.3);
      background: rgba(255, 215, 0, 0.06);
      border: 1px solid rgba(248, 239, 148, 0.7);
    }

    .player-header {
      display: flex;
      align-items: center;
      gap: 8px;

      > .player-total {
        margin-left: auto;
        font-size: 15px;
        font-weight: bold;
        font-family: monospace;
        white-space: nowrap;

        &.positive { color: #7fff7f; }
        &.negative { color: #ff7f7f; }
        &.zero { color: rgba(255, 255, 255, 0.75); }
      }

      .player-avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        flex-shrink: 0;

        &.ai-1 { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); }
        &.ai-2 { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
        &.ai-3 { background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); }
        &.ai-4 { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%); }
        &.player { background: linear-gradient(135deg, #ffd700 0%, #ff8c00 100%); }
      }

      .player-row-scores {
          display: flex;
          flex-direction: row;
          gap: 4px;
          font-size: 11px;
          font-family: monospace;
          white-space: nowrap;
          text-shadow:
            -1px -1px 0 rgba(0, 0, 0, 0.9),
            1px -1px 0 rgba(0, 0, 0, 0.9),
            -1px 1px 0 rgba(0, 0, 0, 0.9),
            1px 1px 0 rgba(0, 0, 0, 0.9),
            0 -1px 0 rgba(0, 0, 0, 0.9),
            0 1px 0 rgba(0, 0, 0, 0.9),
            -1px 0 0 rgba(0, 0, 0, 0.9),
            1px 0 0 rgba(0, 0, 0, 0.9),
            0 2px 4px rgba(0, 0, 0, 0.7);

          .row-score {
            &.positive { color: #7fff7f; }
            &.negative { color: #ff7f7f; }
            &.zero { color: rgba(255, 255, 255, 0.5); }
          }
        }

      .player-meta {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex-shrink: 0;

        .player-name {
          color: #fff;
          font-size: 14px;
          font-weight: bold;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 90px;
          text-shadow:
            -1px -1px 0 rgba(0, 0, 0, 0.9),
            1px -1px 0 rgba(0, 0, 0, 0.9),
            -1px 1px 0 rgba(0, 0, 0, 0.9),
            1px 1px 0 rgba(0, 0, 0, 0.9),
            0 -1px 0 rgba(0, 0, 0, 0.9),
            0 1px 0 rgba(0, 0, 0, 0.9),
            -1px 0 0 rgba(0, 0, 0, 0.9),
            1px 0 0 rgba(0, 0, 0, 0.9),
            0 2px 4px rgba(0, 0, 0, 0.7);
        }

        .player-total {
          font-size: 15px;
          font-weight: bold;
          font-family: monospace;
          margin-left: auto;
          text-shadow:
            -1px -1px 0 rgba(0, 0, 0, 0.9),
            1px -1px 0 rgba(0, 0, 0, 0.9),
            -1px 1px 0 rgba(0, 0, 0, 0.9),
            1px 1px 0 rgba(0, 0, 0, 0.9),
            0 -1px 0 rgba(0, 0, 0, 0.9),
            0 1px 0 rgba(0, 0, 0, 0.9),
            -1px 0 0 rgba(0, 0, 0, 0.9),
            1px 0 0 rgba(0, 0, 0, 0.9),
            0 2px 4px rgba(0, 0, 0, 0.7);

          &.positive { color: #7fff7f; }
          &.negative { color: #ff7f7f; }
          &.zero { color: rgba(255, 255, 255, 0.75); }
        }

        .player-tags {
          display: flex;
          gap: 3px;
          flex-wrap: wrap;

          .tag {
            display: inline-block;
            padding: 1px 4px;
            border-radius: 3px;
            font-size: 9px;
            font-weight: bold;

            &.sweep {
              background: #e74c3c;
              color: white;
            }

            &.beaten {
              background: rgba(255, 255, 255, 0.2);
              color: #ff9999;
            }
          }
        }
      }

      .player-cards {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        height: 52px;
        gap: 0;
        flex: 1;
        min-width: 0;
        overflow: hidden;

        .card-group {
          display: flex;
          align-items: center;

          .playing-card {
            margin-left: -16px;

            &:first-child {
              margin-left: 0;
            }
          }
        }

        .card-divider {
          width: 10px;
          flex-shrink: 0;
        }
      }
    }
  }

  .positive {
    color: #27ae60;
  }

  .negative {
    color: #e74c3c;
  }

  .zero {
    color: rgba(255, 255, 255, 0.5);
  }
}

// 结算表格样式已移除（牌型明细已删除）

.settle-buttons {
  display: flex;
  gap: 16px;
  margin-top: 10px;
  justify-content: center;
  align-items: center;
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
}

.next-btn {
  // flex: 1;
  padding: 6px;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 120px;

  background: url('@/assets/images/settle_btn_bk.png') no-repeat center / contain;
  user-select: none;
  --text-color: #fb7500; 
  text-shadow:
      -2px -2px 0 var(--text-color),
      2px -2px 0 var(--text-color),
      -2px 2px 0 var(--text-color),
      2px 2px 0 var(--text-color),
      0 -2px 0 var(--text-color),
      0 2px 0 var(--text-color),
      -2px 0 0 var(--text-color),
      2px 0 0 var(--text-color),
      0 3px 4px var(--text-color);

  &.is-lobby {
    width: 150px;
  }

  &.primary {
    // background: linear-gradient(135deg, #ffd700, #ff8c00);
    color: #fff;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
    }
  }

  
}

.back-btn{
  padding: 6px;
  border: none;
  border-radius: 50px;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;

  background: url('@/assets/images/back.png') no-repeat center / contain;

   &.secondary {
    // background: rgba(255, 255, 255, 0.1);
    color: #fff;
    // border: 1px solid rgba(255, 255, 255, 0.3);
    width: 26px;
    height: 26px;
    position: absolute;
    top: 10px;
    left: 10px;

    &:hover {
      // background: rgba(255, 255, 255, 0.2);
      transform: translateY(-2px);
    }
  }
}

// 移动端样式（通过 JS 类名触发）
.game-page.is-mobile {
  .status-message {
    padding: 6px 12px;
    font-size: 12px;
  }

  .stage-indicator {
    padding: 8px 20px;
    font-size: 20px;
  }

  .ai-cards-display {
    padding: 8px;
    min-width: 100px;
  }

  .hands-cards-new {
    .playing-card {
      margin-left: 0;
    }
  }

  .settle-content {
    padding: 16px;
    max-width: 1200px;
    max-height: 88vh;

    .settle-players {
      grid-template-columns: 1fr 1fr;
      gap: 3px;
    }

    .settle-player-card {
      padding: 3px;


      .player-header {
        gap: 6px;

        .player-avatar {
          width: 32px;
          height: 32px;
          font-size: 16px;
        }

        .player-meta {
          .player-name {
            font-size: 13px;
            max-width: 80px;
          }

          .player-row-scores {
            font-size: 10px;
            gap: 4px;
          }

          .player-total {
            font-size: 14px;
          }

          .player-tags {
            .tag {
              font-size: 8px;
              padding: 1px 3px;
            }
          }
        }

        .player-cards {
          .card-divider {
            width: 6px;
          }
        }
      }
    }
  }
}

// 移动端 + 横屏
.game-page.is-mobile.is-landscape {
  .arrange-panel {
    // width: 760px;
    // height: 380px;
    padding: 12px;

    .arrange-panel-inner {
      // padding: 10px 14px 14px;
      padding: 0;
      gap: 2px;
      width: 100%;
      height: 100%;
      box-sizing: border-box;
    }

    .arrange-top-bar {
      .arrange-close-btn {
        width: 26px;
        height: 26px;
        font-size: 18px;
      }

      .arrange-countdown {
        font-size: 13px;
        padding: 4px 10px;
      }
    }

    .layout-rows {
      gap: 4px;

      .layout-row {
        padding: 5px 8px;
        gap: 6px;
        border-radius: 8px;

        .row-label-text {
          // width: 34px;
          // font-size: 12px;
        }

        .row-slot {
          height: 58px;
          min-width: 58px;
          padding: 2px 5px;

          .slot-placeholder {
            font-size: 11px;
          }
        }

        .row-clear-btn,
        .row-clear-placeholder {
          width: 24px;
          height: 24px;
          font-size: 13px;
        }
      }
    }

    .hand-type-hints {
      gap: 3px;
      padding: 2px 0;

      .hint-item {
        padding: 2px 6px;
        font-size: 9px;
        border-radius: 8px;
      }
    }

    .arrange-bottom {
      flex-direction: row;
      gap: 8px;

      .hands-area-new {
        flex: 1;
        min-height: 40px;
        padding: 0;
        border-radius: 8px;

        .hands-cards-new {
          justify-content: center;
          gap: 1px;

          .playing-card {
            width: 38px;
            height: 53px;
            border-radius: 3px;

            .card-corner {
              font-size: 7px;

              .rank {
                font-size: 8px;
              }

              .suit {
                font-size: 5px;
              }

              &.top-left {
                top: 1px;
                left: 1px;
              }

              &.bottom-right {
                bottom: 1px;
                right: 1px;
              }
            }

            .card-center .suit-large {
              font-size: 12px;
            }
          }
        }
      }

      
    }
  }
}

// 手动面板中的智能按钮
.action-btn-smart {
  display: flex;
  padding: 8px 0;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100px;
  height: 28px;
  background: none;
  color: white;
  background-image: url('@/assets/images/baipai_btn_bk1.png');
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center bottom;
  background-clip: padding-box;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(155, 89, 182, 0.4);
  }
}

// 智能摆牌面板
.smart-arrange-panel {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 25px;
  border: 1px solid rgba(248, 239, 148, 0.9);
  box-shadow: 0 5px 5px 5px rgba(0, 0, 0, 0.3), inset 0 0 0 5px rgb(60, 37, 30), inset 0 0 0 6px rgb(110, 76, 60);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .smart-arrange-inner {
    padding: 12px;
    gap: 4px;
    width: 100%;
    height: 100%;
    background-image: url('@/assets/images/baipai_bk.png');
    background-repeat: no-repeat;
    background-size: 90%;
    background-position: center bottom;
    background-clip: padding-box;
    display: flex;
    flex-direction: column;
  }

  .smart-arrange-top {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 0 8px;
    position: relative;

    .smart-countdown {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      padding: 6px 14px;
      border-radius: 20px;
      width: 80px;

      position: absolute;
      right: 220px;
      top: 10px;

      .clock-icon {
        background-image: url('@/assets/images/timing.png');
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center bottom;
        width: 40px;
        height: 40px;
      }

      color: #ffd700;
      font-weight: bold;
      font-size: 18px;
      user-select: none;
      --text-color: #301803;
      text-shadow:
        -2px -2px 0 var(--text-color),
        2px -2px 0 var(--text-color),
        -2px 2px 0 var(--text-color),
        2px 2px 0 var(--text-color),
        0 -2px 0 var(--text-color),
        0 2px 0 var(--text-color),
        -2px 0 0 var(--text-color),
        2px 0 0 var(--text-color),
        0 3px 4px var(--text-color);
    }
  }

  .smart-arrange-body {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 8px;

    .smart-layout-rows {
      display: flex;
      flex-direction: column;
      gap: 16px;

      .smart-row {
        display: flex;
        align-items: center;
        gap: 10px;
        position: relative;
        height: 68px;
        border: solid transparent;
        border-width: 5px 20px 5px 90px;
        border-image: url('@/assets/images/three_row_bk.png') 8 30 8 140 fill;
        border-radius: 10px;
        padding: 8px 12px;

        .smart-row-stat {
          display: flex;
          align-items: center;
          flex-direction: column;
          gap: 4px;
          position: absolute;
          left: -0;


          .smart-row-check {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #27ae60;
            color: white;
            font-size: 14px;
            font-weight: bold;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 6px rgba(39, 174, 96, 0.5);
          }

          .smart-row-type {
            font-size: 14px;
            color: #ffd700;
            font-weight: bold;
            text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
            white-space: nowrap;
          }
        }
        .smart-row-info {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 4px;
          position: absolute;
          left: -88px;

          .smart-row-label {
            width: 72px;
            height: 30px;
            background: url('@/assets/images/btn_bk.png') no-repeat;
            background-size: contain;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: bold;
            font-family: 'Arial Black', 'Impact', 'Arial', sans-serif;
            color: #F5E6C8;
            text-shadow:
              -2px -2px 0 #6B1220,
              2px -2px 0 #6B1220,
              -2px 2px 0 #6B1220,
              2px 2px 0 #6B1220,
              0 -2px 0 #6B1220,
              0 2px 0 #6B1220,
              -2px 0 0 #6B1220,
              2px 0 0 #6B1220,
              0 3px 4px rgba(107, 18, 32, 0.3);
          }

          
        }

        .smart-row-cards {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          position: absolute;
          left: -2px;
          right: 0;

          .smart-placeholder {
            color: rgba(255, 255, 255, 0.35);
            font-size: 13px;
          }
        }
      }

      .row-front {
        width: 300px;
      }

      .row-middle,
      .row-back {
        width: 400px;
      }
    }

    .smart-switch-area {
      display: flex;
      align-items: center;
      justify-content: center;

      .smart-manual-btn {
        width: 24px;
        height: 48px;
        border-radius: 5px;
        border: 2px solid #27ae60;
        background: linear-gradient(135deg, #2ecc71, #27ae60);
        color: white;
        font-size: 13px;
        font-weight: bold;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(39, 174, 96, 0.5);
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(39, 174, 96, 0.6);
        }
      }
    }

    .smart-right-side {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      width: 190px;
      

      .smart-suggestions {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
        padding: 8px;
        background: rgba(0, 0, 0, 0.5);
        border-radius: 10px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        overflow-y: auto;
        overflow-x: hidden;
        width: 200px;
        height: 200px;
        // 优化滚动性能
        will-change: scroll-position;
        -webkit-overflow-scrolling: touch;

        .smart-suggestion-item {
          display: flex;
          flex-direction: column;
          

          gap: 2px;
          padding: 6px 8px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.2);
          cursor: pointer;
          transition: all 0.2s ease;
          user-select: none;
          min-height: 70px;
          flex-shrink: 0;

          .suggestion-row {
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 12px;

            .suggestion-label {
              width: 18px;
              height: 18px;
              border-radius: 3px;
              background: rgba(255, 255, 255, 0.15);
              color: rgba(255, 255, 255, 0.7);
              font-size: 10px;
              font-weight: bold;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            }

            .suggestion-type {
              color: rgba(255, 255, 255, 0.6);
              font-size: 12px;
            }
          }

          &:hover {
            background: rgba(255, 255, 255, 0.15);
          }

          &.suggestion-disabled {
            cursor: not-allowed;
            opacity: 0.55;
            pointer-events: none;
          }

          &.suggestion-active {
            background: linear-gradient(180deg, #fee5a3, #dd984b);
            box-shadow: 0 0 0 2px #e7c4ab, inset 0 0 1px 1px #5d3b11;

            .suggestion-row {
              .suggestion-label {
                background: rgba(108, 41, 14, 0.2);
                color: rgba(108, 41, 14, 0.9);
              }

              .suggestion-type {
                color: rgba(108, 41, 14, 1);
                font-weight: bold;
              }
            }
          }

          &.suggestion-special {
            background: rgba(192, 57, 43, 0.28);
            border: 1px solid rgba(231, 76, 60, 0.55);
            justify-content: center;

            .suggestion-special-row {
              justify-content: center;
              flex: 1;
              align-items: center;

              .suggestion-type {
                color: #ffd7d0;
                font-size: 14px;
                font-weight: bold;
              }
            }

            &.suggestion-active {
              background: linear-gradient(180deg, #fee5a3, #dd984b);
              border-color: transparent;

              .suggestion-special-row .suggestion-type {
                color: rgba(108, 41, 14, 1);
              }
            }
          }
        }

        .smart-report-row {
          display: flex;
          flex-direction: column;
          flex-wrap: nowrap;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          width: 100%;
          min-height: 40px;
          padding: 6px 8px;
          border-radius: 8px;
          background: rgba(192, 57, 43, 0.28);
          border: 1px solid rgba(231, 76, 60, 0.55);
          box-sizing: border-box;
          flex-shrink: 0;

          .smart-report-name {
            flex: 1 1 auto;
            min-width: 0;
            color: #ffd7d0;
            font-size: 14px;
            font-weight: bold;
            line-height: 1.2;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;

            &.is-clickable {
              cursor: pointer;
            }
          }

          .smart-report-btn {
            flex: 0 0 auto;
            padding: 4px 12px;
            border: none;
            border-radius: 4px;
            background: rgba(192, 57, 43, 0.95);
            color: #fff;
            font-size: 12px;
            font-weight: bold;
            cursor: pointer;
            line-height: 1.3;
            white-space: nowrap;

            &.is-reported {
              background: rgba(46, 204, 113, 0.95);
            }

            &:disabled {
              opacity: 0.45;
              cursor: not-allowed;
              filter: grayscale(0.5);
              background: rgba(120, 120, 120, 0.7);
            }

            &:hover:not(:disabled) {
              filter: brightness(1.1);
            }
          }

          &.is-disabled {
            background: rgba(255, 255, 255, 0.08);
            border-color: rgba(255, 255, 255, 0.12);

            .smart-report-name {
              color: rgba(255, 255, 255, 0.35);
            }
          }

          &.suggestion-active {
            background: linear-gradient(180deg, #fee5a3, #dd984b);
            border-color: transparent;
            box-shadow: 0 0 0 2px #e7c4ab, inset 0 0 1px 1px #5d3b11;

            .smart-report-name {
              color: rgba(108, 41, 14, 1);
            }
          }

          &.suggestion-disabled {
            opacity: 0.55;
            pointer-events: none;
          }
        }
      }

      .smart-confirm-btn {
        width: 140px;
        height: 40px;
        border: none;
        border-radius: 20px;
        background: linear-gradient(180deg, #fee5a3, #dd984b);
        color: rgba(108, 41, 14, 1);
        font-size: 16px;
        font-weight: bold;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        transition: all 0.2s ease;

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          filter: grayscale(0.4);
        }

        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
        }
      }
    }
  }
}

.smart-arrange-panel.is-layout-locked {
  .smart-row-cards {
    pointer-events: none;
  }
}

// 智能摆牌面板内的牌尺寸
.smart-arrange-panel ::v-deep .playing-card.small {
  width: 36px;
  height: 50px;
  border-radius: 4px;

  .card-corner {
    font-size: 8px;

    .rank {
      font-size: 10px;
    }

    .suit {
      font-size: 5px;
    }

    &.top-left {
      top: 2px;
      left: 2px;
    }

    &.bottom-right {
      bottom: 2px;
      right: 2px;
    }
  }

  .card-center .suit-large {
    font-size: 16px;
  }
}

// 智能摆牌选中态
.smart-arrange-panel ::v-deep .playing-card.small.smart-card-selected {
  box-shadow: 0 0 0 3px #ffd700, 0 0 8px 2px rgba(255, 215, 0, 0.6);
  transform: translateY(-4px);
  cursor: pointer;
}

.smart-arrange-panel ::v-deep .playing-card.small {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.smart-arrange-panel ::v-deep .playing-card.small:hover {
  transform: translateY(-2px);
}

// 手动摆牌选中态
.arrange-panel ::v-deep .playing-card.small.manual-slot-selected {
  box-shadow: 0 0 0 3px #ffd700, 0 0 8px 2px rgba(255, 215, 0, 0.6);
  transform: translateY(-4px);
  cursor: pointer;
}

.arrange-panel ::v-deep .playing-card.small {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.arrange-panel ::v-deep .playing-card.small:hover {
  transform: translateY(-2px);
}

// 移动端 + 竖屏：摆牌按三道、推荐、出牌自上而下排列
.game-page.is-mobile.is-portrait {
  .top-bar {
    padding: 8px 10px;
  }

  .player-position {
    &.portrait-left-player {
      margin-left: -28px;

      ::v-deep .player-info {
        align-items: flex-start;

        .player-details,
        .player-name,
        .player-title-line,
        .player-round-line,
        .row-score-line,
        .result-tag-line {
          text-align: left;
          align-items: flex-start;
          justify-content: flex-start;
        }
      }
    }

    &.portrait-right-player {
      margin-left: 28px;

      ::v-deep .player-info {
        align-items: flex-end;

        .player-details,
        .player-name,
        .player-title-line,
        .player-round-line,
        .row-score-line,
        .result-tag-line {
          text-align: right;
          align-items: flex-end;
          justify-content: flex-end;
        }
      }
    }

    &.portrait-left-player,
    &.portrait-right-player {
      ::v-deep .player-info {
        width: 68px;
        flex-direction: column;
        gap: 2px;

        .player-avatar {
          flex-shrink: 0;
        }

        .player-details {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 1px;
        }

        .player-name {
          width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 1.15;
        }

        .player-title-line,
        .player-round-line {
          width: 100%;
          flex-direction: column;
          gap: 1px;
          line-height: 1.1;
        }

        .row-score-line,
        .result-tag-line {
          width: 100%;
        }

        .confirmed-badge,
        .folded-badge,
        .special-hand {
          max-width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }

    &.portrait-center-player {
      ::v-deep .player-info {
        width: 168px;

        .player-details {
          width: 100%;
        }

        .player-title-line,
        .player-round-line,
        .row-score-line,
        .result-tag-line {
          justify-content: flex-start;
          text-align: left;
        }

        .player-title-line {
          flex-wrap: nowrap;
        }

        .player-round-line {
          flex-wrap: nowrap;
        }
      }
    }
  }

  .cards-position {
    &.portrait-left-player {
      margin-left: -40px;

      .ai-cards-fan,
      .ai-cards-back,
      .dealing-hands {
        transform-origin: left center;
      }
    }

    &.portrait-right-player {
      margin-left: 40px;

      .ai-cards-fan,
      .ai-cards-back,
      .dealing-hands {
        transform-origin: right center;
      }
    }

    &.portrait-left-player,
    &.portrait-right-player {
      .ai-cards-fan {
        width: 240px;
        height: 190px;

        .fan-row {
          height: 106px;

          &.middle-row {
            top: 42px;
          }

          &.back-row {
            top: 85px;
          }
        }
      }

      .ai-cards-back {
        .three-stacks-fan {
          width: 240px;
          height: 190px;

          .stack-row-fan {
            height: 106px;

            &.middle-stack {
              top: 42px;
            }

            &.back-stack {
              top: 85px;
            }
          }
        }
      }

      .dealing-hands {
        min-width: 120px;
        transform: scale(0.82);
      }
    }
  }

  .sweep-overlay {
    &.portrait-left-player {
      margin-left: -64px;
      align-items: flex-start;
    }

    &.portrait-right-player {
      margin-left: 64px;
      align-items: flex-end;
    }

    &.portrait-left-player,
    &.portrait-right-player {
      .sweep-badge {
        transform: scale(0.72);
        transform-origin: center center;
      }
    }
  }

  .arrange-overlay {
    background: rgba(0, 0, 0, 0.72);
  }

  .smart-arrange-panel {
    width: 96vw;
    max-height: 96dvh;
    border-radius: 18px;
    border: 0;

    .smart-arrange-inner {
      padding: 10px;
      background-size: 135%;
      background-position: center bottom;
      box-sizing: border-box;
      background-image: none;
    }

    .smart-arrange-top {
      min-height: 0;
      padding: 0;
      position: static;

      .smart-countdown {
        position: absolute;
        top: 38px;
        right: 10px;
        width: 64px;
        padding: 0;
        gap: 0;
        font-size: 15px;

        .clock-icon {
          width: 30px;
          height: 30px;
        }
      }
    }

    .smart-arrange-body {
      position: relative;
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
      gap: 10px;
      padding: 2px 0 0;
      min-height: 0;

      .smart-layout-rows {
        order: 1;
        width: 100%;
        position: relative;
        gap: 8px;

        .smart-row {
          width: 100%;
          height: 82px;
          box-sizing: border-box;
          border: none;
          border-radius: 10px;
          background: url('@/assets/images/baipei_row_portrait_back.png') no-repeat center / 100% 100%;
          padding: 6px 8px;

          .smart-row-info {
            display: none;
          }

          .smart-row-stat {
            left: 38px;

            .smart-row-check {
              width: 14px;
              height: 14px;
              font-size: 11px;
            }

            .smart-row-type {
              max-width: 46px;
              font-size: 10px;
              white-space: normal;
              line-height: 1.1;
              text-align: center;
            }
          }

          .smart-row-cards {
            left: 54px;
            right: 8px;
            gap: 2px;
            justify-content: center;
          }
        }

        .row-front {
          width: calc(100% - 78px);
        }
      }

      .smart-switch-area {
        display: none;
      }

      .smart-right-side {
        order: 2;
        width: 100%;
        min-height: 0;
        gap: 10px;

        .smart-suggestions {
          width: 100%;
          height: auto;
          max-height: 30dvh;
          box-sizing: border-box;
          display: flex;
          grid-template-columns: none;
          overflow-x: auto;
          overflow-y: hidden;
          gap: 8px;
          padding: 8px;
          scroll-snap-type: x proximity;

          .smart-suggestion-item {
            min-width: 64px;
            min-height: 64px;
            padding: 6px;
            scroll-snap-align: start;
            .suggestion-row {
              gap: 3px;

              .suggestion-label {
                width: 16px;
                height: 16px;
              }

              .suggestion-type {
                min-width: 0;
                font-size: 11px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
            }
          }

          .smart-report-row {
            min-width: 64px;
            width: auto;
            grid-column: auto;
            flex-wrap: nowrap;
            scroll-snap-align: start;
          }
        }

        .smart-confirm-btn {
          order: 3;
          width: 100%;
          height: 48px;
          border-radius: 24px;
          font-size: 18px;
          flex-shrink: 0;
        }
      }
    }
  }

  .smart-arrange-panel ::v-deep .playing-card.small,
  .arrange-panel ::v-deep .playing-card.small {
    width: 40px;
    height: 56px;

    .card-center .suit-large {
      font-size: 18px;
    }
  }

  .arrange-panel {
    width: 96vw;
    height: 96dvh;
    border-radius: 18px;

    .arrange-panel-inner {
      flex-direction: column;
      padding: 10px;
      box-sizing: border-box;
      background-size: 135%;
    }

    .arrange-main {
      order: 1;
      width: 100%;
      justify-content: flex-start;
      min-height: 0;
    }

    .layout-rows {
      width: 100%;
      gap: 8px;

      .row-front,
      .row-middle,
      .row-back {
        width: 100%;
      }

      .layout-row {
        height: 82px;
        box-sizing: border-box;
        border-width: 5px 14px 5px 62px;

        .row-label-text {
          left: -60px;
          width: 50px;
          height: 26px;
          font-size: 12px;
        }

        .row-slot {
          left: 4px;
          right: 18px;
          height: 68px;
        }

        .row-clear-btn,
        .row-clear-placeholder {
          width: 20px;
          height: 20px;
        }
      }
    }

    .hand-type-hints {
      width: 100%;
      gap: 6px;
      padding: 8px 0;

      .hint-item {
        min-width: 54px;
        padding: 5px 8px;
        font-size: 12px;
        text-align: center;
      }
    }

    .arrange-bottom {
      width: 100%;

      .hands-area-new {
        min-height: 74px;
        width: 100%;
        box-sizing: border-box;

        .hands-cards-new {
          width: 100%;
          height: 60px;
          overflow-x: auto;
          justify-content: flex-start;
        }
      }
    }

    .arrange-side {
      order: 2;
      width: 100%;
      flex: 1;
      min-height: 0;
      flex-direction: column;

      .arrange-top-bar {
        position: absolute;
        top: 8px;
        right: 8px;

        .arrange-countdown {
          width: 64px;
          padding: 0;
          font-size: 15px;

          .clock-icon {
            width: 30px;
            height: 30px;
          }
        }
      }

      .arrange-smart-btn {
        position: absolute;
        top: 12px;
        left: 12px;
        flex: 0;
        width: auto;
      }

      .arrange-actions {
        width: 100%;
        flex: 1;
        justify-content: flex-end;
        align-items: stretch;
        gap: 8px;

        .action-btn-onekey,
        .action-btn-reset {
          width: 100%;
          height: 36px;
        }

        .action-btn-confirm {
          width: 100%;
          height: 48px;
          font-size: 18px;
          border-radius: 24px;
        }
      }
    }
  }

  .settle-modal {
    // align-items: stretch;
    padding: 10px;
  }

  .settle-content {
    width: 100%;
    max-width: none;
    // max-height: calc(100dvh - 20px);
    // padding: 18px 12px 36px;
    padding: 10px;
    border-radius: 18px;

    .result-section {
      height: auto;
      max-height: calc(100dvh - 118px);
      margin-top: 24px;
      padding-right: 2px;
    }

    .settle-players {
      grid-template-columns: 1fr;
      gap: 8px;
    }

    .settle-player-card {
      padding: 3px 5px;
      border-radius: 12px;


      .player-header {
        display: grid;
        grid-template-columns: 30px minmax(0, 1fr) auto;
        grid-template-rows: auto auto;
        align-items: center;
        gap: 2px 6px;

        .player-avatar {
          grid-column: 1;
          grid-row: 1 / span 2;
          width: 30px;
          height: 30px;
          font-size: 15px;
        }

        .player-meta {
          grid-column: 2 / span 2;
          grid-row: 1;
          width: auto;
          min-width: 0;
          flex-direction: row;
          align-items: center;
          gap: 4px;

          .player-name {
            max-width: 48px;
            font-size: 12px;
          }

          .player-total {
            font-size: 13px;
            flex-shrink: 0;
          }

          .player-tags {
            flex-wrap: nowrap;
            min-width: 0;

            .tag {
              max-width: 40px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }

          .player-row-scores {
            width: auto;
            flex-shrink: 0;
            flex-direction: row;
            justify-content: flex-end;
            gap: 6px;
            font-size: 10px;
          }
        }

        .player-cards {
          grid-column: 2 / span 2;
          grid-row: 2;
          height: 42px;
          justify-content: flex-start;
          gap: 0;
          overflow: visible;

          .card-divider {
            width: 10px;
          }

          .card-group {
            flex-shrink: 0;
            overflow: visible;

            &.front {
              width: 62px;
            }

            &.middle,
            &.back {
              width: 94px;
            }

            .playing-card {
              margin-left: -15px;

              &:first-child {
                margin-left: 0;
              }
            }
          }
        }
      }
    }
  }

  .settle-content ::v-deep .playing-card.tiny {
    width: 31px;
    height: 43px;
  }
}
</style>
